const React = require('react');
const { createElement: h, useCallback, useEffect, useRef, useState } = React;
const { signup, signin, signinWithGoogle, signout, loadCurrentUser, resetUsage, requestPasswordReset, resetPassword } = require('./lib/authStorage');
const LoginPage = require('./components/LoginPage');
const ExtensionAuthPage = require('./components/ExtensionAuthPage');
const ExtensionAuthNotice = require('./components/ExtensionAuthNotice');
const ResetPasswordPage = require('./components/ResetPasswordPage');
const WaitlistPage = require('./components/WaitlistPage');
const betaConfig = require('./lib/betaConfig');
const WorkflowListPage = require('./components/WorkflowListPage');
const WorkbookExplorerPanel = require('./components/WorkbookExplorerPanel');
const WorkbookMoveToDialog = require('./components/WorkbookMoveToDialog');
const Header = require('./components/Header');
const Sidebar = require('./components/Sidebar');
const WorkflowCanvas = require('./components/WorkflowCanvas');
const CustomObjectEditor = require('./components/CustomObjectEditor');
const AiInstructionModal = require('./components/AiInstructionModal');
const ConfirmDialog = require('./components/ConfirmDialog');
const RunInputModal = require('./components/RunInputModal');
const GoogleScopePickerHost = require('./components/GoogleScopePickerHost');
const PublishLiveDialog = require('./components/PublishLiveDialog');
const LiveModeBanner = require('./components/LiveModeBanner');
const DraftConflictBanner = require('./components/DraftConflictBanner');
const LogoPickerPopup = require('./components/LogoPickerPopup');
const ErrorPage = require('./components/ErrorPage');
const MarketingPage = require('./components/MarketingPage');
const TemplateStorePage = require('./components/TemplateStorePage');
const AdminDashboardPage = require('./components/AdminDashboardPage');
const AdminEvalPage = require('./components/AdminEvalPage');
const PublicBenchmarkPage = require('./components/PublicBenchmarkPage');
const PublishTemplateModal = require('./components/PublishTemplateModal');
const ApiDocsPage = require('./components/ApiDocsPage');
const OAuthConsentPage = require('./components/OAuthConsentPage');
const UserGuidePage = require('./components/UserGuidePage');
const LegalPage = require('./components/LegalPage');
const UIPageEditor = require('./components/UIPageEditor');
const UiPagePublicView = require('./components/UiPagePublicView');
const UIPlannerModal = require('./components/UIPlannerModal');
const uiPageStorage = require('./lib/uiPageStorage');
const { termsOfService, privacyPolicy } = require('./data/legalContent');
const { request, getApiBasePath, getAccessToken, setAccessToken, setEmbedWorkflowId } = require('./lib/apiClient');
const { stripStaleNodeSize, deriveParamKey, uniqueParamKey } = require('./lib/nodeTypes');
const { buildEditorContextNodes, computeUpstreamIdsNearestFirst, compactConditionTitle, syncHyperparamDefault, computeSetVariableAutofill, clearFedTemporaryVariableValues } = require('./lib/canvasUtils');
const {
  listWorkflows,
  createWorkflow,
  loadWorkflowStateById,
  loadWorkflowWithMetaById,
  saveWorkflowStateById,
  loadCustomObjects,
  saveCustomObject,
  deleteCustomObject,
  deleteWorkflow,
  startWorkflowExecution,
  stopWorkflowExecution,
  pauseWorkflowExecution,
  resumeWorkflowExecution,
  subscribeWorkflowExecution,
  subscribePublicWorkflowExecution,
  acquireWorkflowPublicListener,
  heartbeatWorkflowPublicListener,
  releaseWorkflowPublicListener,
  putCodefix,
  startManualNodeAutoFix,
  saveUserAiInstruction,
  listWorkflowVersions,
  restoreWorkflowVersion,
  getLiveVersion,
  publishLiveVersion,
  loadLiveVersionState,
  saveLiveVersionState,
  setLiveOffline,
  listLiveHistory
} = require('./lib/workflowStorage');
const workbookStorage = require('./lib/workbookStorage');
const localFlowCache = require('./lib/localFlowCache');
const workflowProgress = require('./lib/workflowProgressStorage');
const storageQuota = require('./lib/storageQuota');
const { objectTemplates, systemTemplates } = require('./data/workflowData');
const { getLandingNavItems, getLandingPageContentByRoute } = require('./data/marketingPages');
const { getLandingStrings } = require('./data/landingStrings');
const { detectLanguage, setActiveLanguage, setStoredLanguage } = require('./lib/language');
const { compressWorkflow, decompressWorkflow, isCompressedWorkflow } = require('./lib/workflowCodec');
const { workflowToCode, parseVariableValue } = require('./lib/workflowToCode');
const { runLogLineFromEvent, shortDuration: formatRunLogDuration } = require('./lib/runLogLines');
const { shrinkShapeForPersistence } = require('./lib/shapeCache');
const { detectMissingUserInput, hasEmptyRequiredValue } = require('./lib/missingUserInput');
const { createCollabClient } = require('./lib/collabClient');
const { diffWorkflowStates, applyOps } = require('./lib/workflowStateDiff');
const { createRemotePointer } = require('./lib/remoteControl');
const rrweb = require('rrweb');
const RemoteSupportPopup = require('./components/RemoteSupportPopup');
const RemoteSessionViewer = require('./components/RemoteSessionViewer');
require('./App.css');

function createDefaultStartNode() {
  var startTemplate = (objectTemplates || []).find(function(template) {
    return template && template.icon === 'start';
  });

  if (!startTemplate) {
    return {
      id: 'sys-start-default',
      kind: 'workflow-node',
      nodeType: 'card',
      category: 'system',
      title: 'Start',
      subtitle: 'Entry point of the workflow',
      label: 'Start',
      icon: 'start',
      borderColor: '',
      conditionRule: null,
      options: null,
      stats: null,
      hyperparameters: [],
      paramValues: {},
      position: { left: 80, top: 290 }
    };
  }

  return {
    id: 'sys-start-' + Date.now().toString(36),
    kind: startTemplate.kind,
    nodeType: startTemplate.nodeType,
    category: startTemplate.category || null,
    title: startTemplate.title,
    subtitle: startTemplate.subtitle,
    label: startTemplate.label,
    icon: startTemplate.icon,
    logoUrl: startTemplate.logoUrl || null,
    templateId: startTemplate.id || null,
    code: startTemplate.code || '',
    generationPrompt: startTemplate.generationPrompt ? String(startTemplate.generationPrompt) : '',
    color: startTemplate.color,
    borderColor: startTemplate.borderColor,
    options: Array.isArray(startTemplate.options) ? startTemplate.options.slice() : null,
    stats: Array.isArray(startTemplate.stats)
      ? startTemplate.stats.map(function(stat) { return Object.assign({}, stat); })
      : null,
    hyperparameters: Array.isArray(startTemplate.hyperparameters)
      ? startTemplate.hyperparameters.map(function(param) { return Object.assign({}, param); })
      : [],
    conditionRule: startTemplate.conditionRule || null,
    paramValues: Array.isArray(startTemplate.hyperparameters)
      ? startTemplate.hyperparameters.reduce(function(acc, param) {
          if (!param || !param.key) return acc;
          acc[param.key] = param.default !== undefined ? param.default : '';
          return acc;
        }, {})
      : {},
    position: { left: 80, top: 290 }
  };
}

function ensureDefaultStartNode(nodes) {
  var safeNodes = Array.isArray(nodes) ? nodes : [];
  return safeNodes;
}

// Backfill the "Additional Instructions" field onto Tool Calling nodes that were
// saved before it existed. New nodes get it from the template (workflowData.js);
// already-placed nodes carry their own stored hyperparameters and would otherwise
// never show the field. Pure: returns the same node ref when nothing changes.
// NOTE: keep this param definition in sync with the template in workflowData.js.
function migrateToolAgentNodes(nodes) {
  var safeNodes = Array.isArray(nodes) ? nodes : [];
  return safeNodes.map(function(node) {
    if (!node || node.icon !== 'tool-agent') return node;
    var hp = Array.isArray(node.hyperparameters) ? node.hyperparameters : [];
    var hasField = hp.some(function(p) { return p && p.key === 'additionalInstructions'; });
    if (hasField) return node;
    var instructionsParam = {
      id: 'tool-agent-instructions',
      key: 'additionalInstructions',
      label: 'Additional Instructions',
      type: 'multiline',
      rows: 6,
      default: '',
      hint: 'Optional standing guidance for how the agent should behave across the whole run (e.g. tone, constraints, "always cite sources"). Added to the agent\'s system rules — separate from the task above.'
    };
    // Insert right after `prompt` so it renders directly under the Prompt field,
    // matching the template order; fall back to appending if prompt isn't present.
    var promptIdx = hp.findIndex(function(p) { return p && p.key === 'prompt'; });
    var nextHp = hp.slice();
    if (promptIdx >= 0) nextHp.splice(promptIdx + 1, 0, instructionsParam);
    else nextHp.push(instructionsParam);
    var nextParamValues = Object.assign({}, node.paramValues);
    if (nextParamValues.additionalInstructions === undefined) nextParamValues.additionalInstructions = '';
    return Object.assign({}, node, { hyperparameters: nextHp, paramValues: nextParamValues });
  });
}

function getStartNodeParams(nodes) {
  var startNode = (nodes || []).find(function(n) { return n && n.icon === 'start'; });
  if (!startNode || !Array.isArray(startNode.hyperparameters) || !startNode.hyperparameters.length) return null;
  var params = {};
  startNode.hyperparameters.forEach(function(p) {
    if (!p || !p.key) return;
    params[p.key] = (startNode.paramValues && startNode.paramValues[p.key] !== undefined)
      ? startNode.paramValues[p.key]
      : (p.default !== undefined ? p.default : '');
  });
  return Object.keys(params).length > 0 ? params : null;
}

function buildLegacyMarketingPathMap(navItems) {
  return (navItems || []).reduce(function(map, item) {
    map[item.path] = item.route;
    return map;
  }, {});
}

function isStaticVariableNode(node) {
  return !!(
    node &&
    node.icon === 'variables' &&
    node.paramValues &&
    String(node.paramValues.retain || 'false') === 'true'
  );
}

function formatVariableValueForStorage(dataType, value) {
  var type = String(dataType || '').toLowerCase();
  if (type === 'bool' || type === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (type === 'int' || type === 'integer' || type === 'number') {
    var numeric = Number(value);
    return Number.isFinite(numeric) ? String(numeric) : '0';
  }
  if (type === 'json' || type === 'object' || type === 'array') {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    try { return JSON.stringify(value, null, 2); } catch(e) { return String(value); }
  }
  return value !== undefined && value !== null ? String(value) : '';
}

// Retained values above this size are not written into the workflow state.
// The state is saved to the server, snapshotted per save into version history,
// and cached in localStorage — a single multi-MB retained value used to blow
// all three up (413 "entity too large" on save). Truncating instead would
// corrupt the variable's default, so we keep the previous value.
var MAX_RETAINED_VARIABLE_CHARS = 200 * 1024;

function applyStaticVariableValue(state, variableName, value) {
  if (!state || !Array.isArray(state.nodes) || !variableName) return state;
  var didChange = false;
  var nextNodes = state.nodes.map(function(node) {
    if (!isStaticVariableNode(node)) return node;
    var title = String(node.title || '').trim();
    if (title !== variableName) return node;

    var nextParamValues = Object.assign({}, node.paramValues);
    var nextStoredValue = formatVariableValueForStorage(nextParamValues.dataType, value);
    if (nextParamValues.value === nextStoredValue) return node;
    if (typeof nextStoredValue === 'string' && nextStoredValue.length > MAX_RETAINED_VARIABLE_CHARS) {
      console.warn('[variables] "' + variableName + '" run value not retained (' +
        Math.round(nextStoredValue.length / 1024) + 'KB > ' +
        Math.round(MAX_RETAINED_VARIABLE_CHARS / 1024) + 'KB) — previous default kept.');
      return node;
    }

    nextParamValues.value = nextStoredValue;
    didChange = true;
    return Object.assign({}, node, { paramValues: nextParamValues });
  });

  if (!didChange) return state;
  return Object.assign({}, state, { nodes: nextNodes });
}

// Attach (or replace) a node's persisted lastOutput entry so the variable
// picker can describe upstream output structure on next workflow load. Pure
// — returns the same state reference when no node matches.
function applyNodeLastOutput(state, nodeId, lastOutput) {
  if (!state || !Array.isArray(state.nodes) || !nodeId) return state;
  var targetId = String(nodeId);
  var didChange = false;
  var nextNodes = state.nodes.map(function(node) {
    if (!node || String(node.id) !== targetId) return node;
    didChange = true;
    return Object.assign({}, node, { lastOutput: lastOutput });
  });
  if (!didChange) return state;
  return Object.assign({}, state, { nodes: nextNodes });
}

function isPayloadTooLargeError(error) {
  var status = error && (error.statusCode || error.status);
  return Number(status) === 413;
}

// Worth an automatic retry? Network blips (ERR_FAILED / disconnect / CORS
// preflight during a brief API outage or restart) reject with NO status; server
// errors / timeouts / rate-limits are also transient. 4xx client errors (auth,
// payload-too-large, etc.) are NOT — retrying just fails the same way.
function isTransientStartError(error) {
  var status = Number(error && (error.statusCode || error.status)) || 0;
  if (status === 0) return true;                 // network / CORS / disconnect
  if (status >= 500) return true;                // server error
  if (status === 408 || status === 429) return true; // timeout / rate limited
  return false;
}

function isLockedDuplicateNode(node) {
  if (!node || node.duplicateLocked !== true) return false;
  var sourceId = node.duplicateSourceNodeId !== undefined ? String(node.duplicateSourceNodeId).trim() : '';
  return sourceId.length > 0;
}

function HeroIcon(props) {
  var paths = [];
  if (props.name === 'table-cells') {
    paths = [
      'M3.75 6.75h16.5m-16.5 5.25h16.5m-16.5 5.25h16.5M8.25 3.75v16.5m7.5-16.5v16.5',
      'M3 3h18v18H3z'
    ];
  } else if (props.name === 'document') {
    paths = [
      'M19.5 14.25v-8.25a2.25 2.25 0 00-2.25-2.25h-8.379a1.5 1.5 0 00-1.06.44L4.19 7.81a1.5 1.5 0 00-.44 1.06v9.38a2.25 2.25 0 002.25 2.25h11.25a2.25 2.25 0 002.25-2.25z',
      'M8.25 12h7.5M8.25 15.75h4.5'
    ];
  } else if (props.name === 'circle-stack') {
    paths = [
      'M3.75 7.5C3.75 5.843 7.444 4.5 12 4.5s8.25 1.343 8.25 3-3.694 3-8.25 3-8.25-1.343-8.25-3z',
      'M3.75 12c0 1.657 3.694 3 8.25 3s8.25-1.343 8.25-3',
      'M3.75 16.5c0 1.657 3.694 3 8.25 3s8.25-1.343 8.25-3'
    ];
  } else if (props.name === 'sparkles') {
    paths = [
      'M9.813 15.904L9 18l-.813-2.096a4.5 4.5 0 00-2.29-2.29L3.75 12l2.147-.813a4.5 4.5 0 002.29-2.29L9 6.75l.813 2.147a4.5 4.5 0 002.29 2.29L14.25 12l-2.147.813a4.5 4.5 0 00-2.29 2.29z',
      'M18.259 8.715L18 9.75l-.259-1.035a2.25 2.25 0 00-1.456-1.456L15.25 7l1.035-.259A2.25 2.25 0 0017.741 5.285L18 4.25l.259 1.035a2.25 2.25 0 001.456 1.456L20.75 7l-1.035.259a2.25 2.25 0 00-1.456 1.456z'
    ];
  } else if (props.name === 'chart-bar') {
    paths = [
      'M3.75 3v18h16.5',
      'M7.5 14.25v3.75m4.5-7.5v7.5m4.5-10.5v10.5'
    ];
  } else if (props.name === 'cog') {
    paths = [
      'M10.5 6h3m-9 6h3m9 0h3m-9 6h3M7.59 7.59l2.12 2.12m4.58 4.58l2.12 2.12m0-8.82l-2.12 2.12m-4.58 4.58l-2.12 2.12',
      'M12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z'
    ];
  } else if (props.name === 'chip') {
    paths = [
      'M9 3v2.25M15 3v2.25M9 18.75V21M15 18.75V21M3 9h2.25M3 15h2.25M18.75 9H21M18.75 15H21',
      'M7.5 6.75h9a.75.75 0 01.75.75v9a.75.75 0 01-.75.75h-9a.75.75 0 01-.75-.75v-9a.75.75 0 01.75-.75z',
      'M10.5 10.5h3v3h-3z'
    ];
  } else if (props.name === 'eye') {
    paths = [
      'M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z',
      'M15 12a3 3 0 11-6 0 3 3 0 016 0z'
    ];
  } else if (props.name === 'check-circle') {
    paths = [
      'M9 12.75L11.25 15 15 9.75',
      'M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
    ];
  } else if (props.name === 'bot') {
    paths = [
      'M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V8h2a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H9a3 3 0 0 1-3-3v-6a3 3 0 0 1 3-3h2V5.73A2 2 0 0 1 12 2z',
      'M9.5 13.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm5 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
      'M9.5 16h5'
    ];
  } else {
    paths = [
      'M15.75 6.75a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z',
      'M4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75a17.933 17.933 0 01-7.499-1.632z'
    ];
  }

  return h('svg', {
    className: props.className || 'hero-icon',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '1.8',
    'aria-hidden': 'true'
  }, paths.map(function(path, index) {
    return h('path', { key: props.name + '-' + index, d: path, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' });
  }));
}

function BrandLogo() {
  return h('svg', {
    width: 128, height: 30, viewBox: '0 0 128 30',
    fill: 'none', xmlns: 'http://www.w3.org/2000/svg',
    'aria-label': 'AlleFlow'
  },
    h('rect', { x: 0, y: 3, width: 24, height: 24, rx: 6, fill: '#1a1f2e' }),
    h('circle', { cx: 8, cy: 15, r: 2.5, fill: 'white', fillOpacity: 0.9 }),
    h('line', { x1: 10.5, y1: 15, x2: 13.5, y2: 15, stroke: 'white', strokeWidth: 1.5, strokeOpacity: 0.65 }),
    h('path', { d: 'M13 12 L17 15 L13 18', stroke: 'white', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' }),
    h('text', {
      x: 31, y: 21,
      fontFamily: 'Manrope, Segoe UI, sans-serif',
      fontSize: 17, fontWeight: 800,
      letterSpacing: -0.4, fill: '#1a1f2e'
    }, 'AlleFlow')
  );
}

function LanguageToggle(props) {
  var language = props.language === 'da' ? 'da' : 'en';
  var onChange = props.onChange || function() {};
  function buttonFor(code, label) {
    var active = language === code;
    return h('button', {
      key: code,
      type: 'button',
      className: 'lang-toggle__btn' + (active ? ' lang-toggle__btn--active' : ''),
      'aria-pressed': active,
      onClick: function() {
        if (!active) {
          var result = onChange(code);
          if (result && typeof result.catch === 'function') result.catch(function() {});
        }
      }
    }, label);
  }
  return h('div', { className: 'lang-toggle', role: 'group', 'aria-label': 'Language' },
    buttonFor('en', 'EN'),
    buttonFor('da', 'DA')
  );
}

function TopNav(props) {
  var currentUser = props.currentUser;
  var activeSection = props.activeSection || '';
  var menuOpen = props.mobileMenuOpen;
  var toggleMenu = props.onToggleMobileMenu;
  var navItems = Array.isArray(props.navItems) ? props.navItems : [];
  var navStrings = (props.strings && props.strings.nav) || {};
  return h('header', { className: 'top-nav' },
    h('div', { className: 'top-nav__inner' },
      h('div', { className: 'top-nav__row' },
        h('button', { className: 'brand brand--button', type: 'button', onClick: function() { props.onNavigateSection(''); } }, h(BrandLogo)),
        h('button', {
          className: 'top-nav__hamburger' + (menuOpen ? ' top-nav__hamburger--open' : ''),
          type: 'button',
          'aria-label': menuOpen ? (navStrings.menuClose || 'Close menu') : (navStrings.menuOpen || 'Open menu'),
          'aria-expanded': menuOpen,
          onClick: toggleMenu
        },
          h('span', { className: 'top-nav__hamburger-line' }),
          h('span', { className: 'top-nav__hamburger-line' }),
          h('span', { className: 'top-nav__hamburger-line' })
        )
      ),
      h('div', { className: 'top-nav__collapsible' + (menuOpen ? ' top-nav__collapsible--open' : '') },
        !props.hideNavLinks && navItems.length
          ? h('nav', { className: 'top-nav__links', 'aria-label': 'Primary' },
              navItems.map(function(item) {
                var isActive = activeSection === item.sectionId;
                return h('a', {
                  key: item.sectionId,
                  href: '#' + item.sectionId,
                  className: 'nav-link' + (isActive ? ' nav-link--active' : ''),
                  'aria-current': isActive ? 'page' : null,
                  onClick: function(event) {
                    event.preventDefault();
                    props.onNavigateSection(item.sectionId);
                    if (menuOpen) toggleMenu();
                  }
                }, item.label);
              }),
              h('a', {
                key: 'templates',
                href: '/store',
                className: 'nav-link',
                onClick: function(event) {
                  event.preventDefault();
                  props.onNavigateTo('/store');
                  if (menuOpen) toggleMenu();
                }
              }, navStrings.templates || 'Templates'),
              // User guide link hidden for now — the /user-guide route still works.
              null,
              h('a', {
                key: 'api-docs',
                href: '/apidocs',
                className: 'nav-link',
                onClick: function(event) {
                  event.preventDefault();
                  props.onNavigateTo('/apidocs');
                  if (menuOpen) toggleMenu();
                }
              }, navStrings.apiDocs || 'API docs')
            )
          : h('div', { className: 'top-nav__spacer', 'aria-hidden': 'true' }),
        currentUser
          ? h('div', { className: 'top-nav__actions' },
              h(LanguageToggle, { language: props.language, onChange: props.onChangeLanguage }),
              h('span', { className: 'user-pill' }, String(currentUser.username || navStrings.signedIn || 'Signed in')),
              h('button', { className: 'btn btn--ghost', type: 'button', onClick: props.onSignout }, navStrings.signOut || 'Sign out')
            )
          : h('div', { className: 'top-nav__actions' },
              h(LanguageToggle, { language: props.language, onChange: props.onChangeLanguage }),
              h('button', { className: 'btn btn--light', type: 'button', onClick: props.onOpenLogin }, navStrings.signIn || 'Sign in')
            )
      )
    )
  );
}

function ServiceIcon(props) {
  var s = props.service;
  var size = props.size || 26;
  if (s === 'google') {
    return h('svg', { width: size, height: size, viewBox: '0 0 24 24' },
      h('path', { d: 'M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z', fill: '#4285F4' }),
      h('path', { d: 'M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z', fill: '#34A853' }),
      h('path', { d: 'M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z', fill: '#FBBC05' }),
      h('path', { d: 'M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z', fill: '#EA4335' })
    );
  }
  if (s === 'sheets') {
    return h('svg', { width: size, height: size, viewBox: '0 0 24 24' },
      h('rect', { x: 5, y: 2, width: 14, height: 18, rx: 1.5, fill: '#0F9D58' }),
      h('rect', { x: 8, y: 7, width: 8, height: 1.4, rx: 0.5, fill: 'white', fillOpacity: 0.92 }),
      h('rect', { x: 8, y: 10.3, width: 8, height: 1.4, rx: 0.5, fill: 'white', fillOpacity: 0.92 }),
      h('rect', { x: 8, y: 13.6, width: 5, height: 1.4, rx: 0.5, fill: 'white', fillOpacity: 0.92 }),
      h('line', { x1: 12, y1: 7, x2: 12, y2: 15, stroke: 'white', strokeOpacity: 0.35, strokeWidth: 0.8 })
    );
  }
  if (s === 'claude') {
    return h('svg', { width: size, height: size, viewBox: '0 0 24 24', fill: 'none' },
      h('path', { d: 'M12 4.5L7.5 19h2.6l1.1-3.1h3.6l1.1 3.1H18.5L14 8 12 4.5zm0 6.2l1.1 3.2h-2.2L12 10.7z', fill: '#CF6B4C' })
    );
  }
  if (s === 'slack') {
    return h('svg', { width: size, height: size, viewBox: '0 0 24 24' },
      h('rect', { x: 2.5, y: 2.5, width: 8, height: 8, rx: 2.5, fill: '#E01E5A' }),
      h('rect', { x: 13.5, y: 2.5, width: 8, height: 8, rx: 2.5, fill: '#36C5F0' }),
      h('rect', { x: 2.5, y: 13.5, width: 8, height: 8, rx: 2.5, fill: '#ECB22E' }),
      h('rect', { x: 13.5, y: 13.5, width: 8, height: 8, rx: 2.5, fill: '#2EB67D' })
    );
  }
  if (s === 'github') {
    return h('svg', { width: size, height: size, viewBox: '0 0 24 24' },
      h('path', { d: 'M12 2C6.48 2 2 6.48 2 12c0 4.42 2.87 8.17 6.84 9.49.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.11-1.46-1.11-1.46-.91-.62.07-.61.07-.61 1 .07 1.53 1.03 1.53 1.03.89 1.52 2.34 1.08 2.91.83.09-.65.35-1.08.63-1.33-2.22-.25-4.55-1.11-4.55-4.95 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.02A9.56 9.56 0 0 1 12 6.8c.85 0 1.71.11 2.52.34 1.91-1.29 2.75-1.02 2.75-1.02.55 1.37.2 2.39.1 2.64.64.7 1.03 1.59 1.03 2.68 0 3.85-2.34 4.7-4.57 4.95.36.31.68.92.68 1.85v2.74c0 .27.18.58.69.48A10.01 10.01 0 0 0 22 12C22 6.48 17.52 2 12 2z', fill: '#24292E' })
    );
  }
  return null;
}

// Hero right-column visual: a dotted "canvas" background (matching the workflow
// editor) with the main product video blended on top like live nodes. Auto-
// plays and loops muted.
function HeroVideo() {
  return h('div', { className: 'hero-canvas', 'aria-hidden': 'true' },
    h('video', {
      className: 'hero-video',
      autoPlay: true,
      muted: true,
      loop: true,
      playsInline: true,
      src: '/main.mp4'
    })
  );
}

function HeroSection(props) {
  var hero = (props.strings && props.strings.hero) || {};
  var trustBadges = Array.isArray(hero.trustBadges) ? hero.trustBadges : [];
  return h('main', { className: 'hero' },
    h('section', { className: 'hero-content' },
      h('span', { className: 'hero-badge' }, hero.badge || ''),
      h('h1', { className: 'hero-title' },
        hero.titleLine1 || '',
        h('br'),
        h('span', { className: 'hero-title__accent' }, hero.titleAccent || ''),
        h('br'),
        hero.titleLine3 || ''
      ),
      h('p', { className: 'hero-copy' }, hero.copy || ''),
      h('div', { className: 'hero-cta' },
        h('button', { className: 'btn btn--primary', type: 'button', onClick: props.onGetStarted },
          (betaConfig.WAITLIST_MODE ? hero.ctaPrimaryWaitlist : hero.ctaPrimary) || '',
          h('span', { className: 'hero-cta__arrow' }, '→')
        ),
        h('button', { className: 'btn btn--outline', type: 'button', onClick: props.onExplore }, hero.ctaSecondary || '')
      ),
      trustBadges.length ? h('div', { className: 'hero-trust' },
        trustBadges.map(function(label) {
          return h('span', { key: label, className: 'trust-badge' }, label);
        })
      ) : null
    ),
    h(HeroVideo)
  );
}


function ComparisonTable(props) {
  var comparison = (props && props.strings && props.strings.comparison) || {};
  var rows = Array.isArray(comparison.rows) ? comparison.rows : [];
  var headers = comparison.headers || {};
  return h('section', { className: 'comparison-section' },
    h('div', { className: 'comparison-section__header' },
      h('p', { className: 'landing-highlights__eyebrow' }, comparison.eyebrow || ''),
      h('h2', { className: 'landing-highlights__title' }, comparison.title || '')
    ),
    h('div', { className: 'comparison-table-wrap' },
      h('table', { className: 'comparison-table' },
        h('thead', null,
          h('tr', null,
            h('th', { className: 'comparison-table__th comparison-table__th--feature' }, headers.feature || ''),
            h('th', { className: 'comparison-table__th comparison-table__th--traditional' }, headers.traditional || ''),
            h('th', { className: 'comparison-table__th comparison-table__th--alleflow' }, headers.alleflow || '')
          )
        ),
        h('tbody', null,
          rows.map(function(row) {
            return h('tr', { key: row.feature, className: 'comparison-table__row' },
              h('td', { className: 'comparison-table__td comparison-table__td--feature' }, row.feature),
              h('td', { className: 'comparison-table__td comparison-table__td--traditional' },
                h('span', { className: 'comparison-table__icon comparison-table__icon--traditional' }, '\u2715'),
                row.traditional
              ),
              h('td', { className: 'comparison-table__td comparison-table__td--alleflow' },
                h('span', { className: 'comparison-table__icon comparison-table__icon--alleflow' }, '\u2713'),
                row.alleflow
              )
            );
          })
        )
      )
    )
  );
}

function LandingFinalCta(props) {
  var cta = (props.strings && props.strings.finalCta) || {};
  return h('section', { className: 'marketing-cta' },
    h('div', { className: 'marketing-cta__content' },
      h('p', { className: 'marketing-cta__eyebrow' }, cta.eyebrow || ''),
      h('h2', { className: 'marketing-cta__title' }, cta.title || ''),
      h('p', { className: 'marketing-cta__copy' }, cta.copy || '')
    ),
    h('div', { className: 'marketing-cta__actions' },
      h('button', { className: 'marketing-cta__btn marketing-cta__btn--primary', type: 'button', onClick: props.onGetStarted }, cta.ctaPrimary || ''),
      h('button', { className: 'marketing-cta__btn marketing-cta__btn--secondary', type: 'button', onClick: props.onExplorePricing }, cta.ctaSecondary || '')
    )
  );
}

function VideoSection(props) {
  var video = props.copy || {};
  return h('section', { className: 'landing-video-section' },
    h('video', {
      className: 'landing-video-section__bg',
      autoPlay: true,
      muted: true,
      loop: true,
      playsInline: true,
      src: props.src
    }),
    h('div', { className: 'landing-video-section__overlay' }),
    h('div', { className: 'landing-video-section__content' },
      h('p', { className: 'landing-video-section__eyebrow' }, video.eyebrow || ''),
      h('h2', { className: 'landing-video-section__title' }, video.title || ''),
      h('p', { className: 'landing-video-section__copy' }, video.copy || '')
    )
  );
}

function LandingVideoSection(props) {
  return h(VideoSection, { src: '/landing_video.mp4', copy: props.strings && props.strings.video });
}

function LandingAutopilotSection(props) {
  return h(VideoSection, { src: '/landing_video2.mp4', copy: props.strings && props.strings.autopilot });
}

function SaasLogo(props) {
  var s = props.name;
  var sz = 26;
  if (s === 'uniconta') return h('img', { src: 'https://cdn.prod.website-files.com/633477637a686d796f22d5a0/651aa31200023b9282d4267f_Uniconta-logo_Periti_partner.svg', alt: 'Uniconta', width: sz, height: sz, style: { objectFit: 'contain' } });
  if (s === 'gmail') return h('img', { src: 'https://static.vecteezy.com/system/resources/previews/013/948/544/non_2x/gmail-logo-on-transparent-white-background-free-vector.jpg', alt: 'Gmail', width: sz, height: sz, style: { objectFit: 'contain' } });
  if (s === 'google-search') return h('svg', { width: sz, height: sz, viewBox: '0 0 24 24' },
    h('path', { d: 'M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z', fill: '#4285F4' }),
    h('path', { d: 'M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z', fill: '#34A853' }),
    h('path', { d: 'M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z', fill: '#FBBC05' }),
    h('path', { d: 'M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z', fill: '#EA4335' })
  );
  if (s === 'google-sheets') return h('svg', { width: sz, height: sz, viewBox: '0 0 24 24' },
    h('path', { d: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z', fill: '#0F9D58' }),
    h('path', { d: 'M14 2v6h6', fill: '#87CEAC' }),
    h('rect', { x: 7, y: 12, width: 10, height: 7, rx: 0.5, fill: '#fff', fillOpacity: 0.9 }),
    h('line', { x1: 7, y1: 15, x2: 17, y2: 15, stroke: '#0F9D58', strokeWidth: 0.5 }),
    h('line', { x1: 12, y1: 12, x2: 12, y2: 19, stroke: '#0F9D58', strokeWidth: 0.5 })
  );
  if (s === 'outlook') return h('img', { src: 'https://mailmeteor.com/logos/assets/PNG/Microsoft_Office_Outlook_Logo_512px.png', alt: 'Outlook', width: sz, height: sz, style: { objectFit: 'contain' } });
  if (s === 'teams') return h('img', { src: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/07/Microsoft_Office_Teams_%282025%E2%80%93present%29.svg/960px-Microsoft_Office_Teams_%282025%E2%80%93present%29.svg.png', alt: 'Teams', width: sz, height: sz, style: { objectFit: 'contain' } });
  if (s === 'e-conomic') return h('img', { src: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTjqJ70oLX1StlSR697nOOOojwrE8gmDqMncg&s', alt: 'e-conomic', width: sz, height: sz, style: { objectFit: 'contain', borderRadius: '4px' } });
  if (s === 'ucpim') return h('img', { src: 'https://ucpim.dk/assets/android-chrome-512x512.png', alt: 'UCPIM', width: sz, height: sz, style: { objectFit: 'contain', borderRadius: '4px' } });
  if (s === 'sharepoint') return h('img', { src: 'https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/webp/microsoft-sharepoint.webp', alt: 'SharePoint', width: sz, height: sz, style: { objectFit: 'contain' } });
  return h('img', { src: 'https://pbs.twimg.com/profile_images/1026981625291190272/35O2KIRX_400x400.jpg', alt: s, width: sz, height: sz, style: { objectFit: 'contain', borderRadius: '4px' } });
}

var INTEGRATION_ROW_1 = ['uniconta', 'gmail', 'google-search', 'google-sheets', 'outlook', 'teams', 'e-conomic', 'ucpim', 'sharepoint', 'uniconta', 'gmail', 'google-search', 'google-sheets', 'outlook', 'teams', 'e-conomic', 'ucpim', 'sharepoint'];
var INTEGRATION_ROW_2 = ['sharepoint', 'ucpim', 'e-conomic', 'teams', 'outlook', 'google-sheets', 'google-search', 'gmail', 'uniconta', 'sharepoint', 'ucpim', 'e-conomic', 'teams', 'outlook', 'google-sheets', 'google-search', 'gmail', 'uniconta'];

function IntegrationsRibbon(props) {
  var names = props.names;
  var tripled = names.concat(names).concat(names);
  return h('div', { className: 'integrations-ribbon-wrap' },
    h('div', { className: 'integrations-ribbon' + (props.reverse ? ' integrations-ribbon--reverse' : '') },
      tripled.map(function(name, i) {
        return h('div', { key: name + '-' + i, className: 'integrations-tile' },
          h(SaasLogo, { name: name })
        );
      })
    )
  );
}

function AnimatedCounter(props) {
  var target = typeof props.value === 'number' ? props.value : 0;
  var [display, setDisplay] = useState(0);
  var displayRef = useRef(0);

  useEffect(function() {
    var from = displayRef.current;
    if (from === target) return undefined;
    var start = null;
    var frame = null;
    function tick(now) {
      if (start === null) start = now;
      var t = Math.min((now - start) / 1200, 1);
      var eased = 1 - Math.pow(1 - t, 3);
      var next = Math.round(from + (target - from) * eased);
      displayRef.current = next;
      setDisplay(next);
      if (t < 1) frame = requestAnimationFrame(tick);
    }
    frame = requestAnimationFrame(tick);
    return function() { cancelAnimationFrame(frame); };
  }, [target]);

  return h('span', { className: 'landing-stats__number' }, display.toLocaleString());
}

function LandingStatsSection(props) {
  var stats = (props.strings && props.strings.stats) || {};
  var [data, setData] = useState(null);
  var [shown, setShown] = useState(null);
  var [visible, setVisible] = useState(false);
  var sectionRef = useRef(null);

  useEffect(function() {
    var cancelled = false;
    function load() {
      request('/public/stats').then(function(response) {
        if (!cancelled && response && response.data) setData(response.data);
      }).catch(function() {
        // Keep last data; the section stays hidden if nothing loaded yet.
      });
    }
    load();
    var interval = setInterval(load, 20000);
    return function() { cancelled = true; clearInterval(interval); };
  }, []);

  // The section only mounts once data exists, so re-run when that flips.
  useEffect(function() {
    var node = sectionRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    var observer = new IntersectionObserver(function(entries) {
      setVisible(!!(entries[0] && entries[0].isIntersecting));
    });
    observer.observe(node);
    return function() { observer.disconnect(); };
  }, [!!data]);

  // Counters only take new values while on screen: first reveal counts up
  // from 0, and anything accumulated while scrolled away counts up from the
  // last shown number on re-entry (never restarts from 0).
  useEffect(function() {
    if (visible && data) setShown(data);
  }, [visible, data]);

  if (!data) return null;
  return h('section', { className: 'landing-stats', ref: sectionRef },
    h('div', { className: 'landing-stats__content' },
      h('h2', { className: 'landing-stats__title' },
        stats.titlePrefix || '',
        h('span', { className: 'landing-stats__highlight' }, stats.titleHighlight || '')
      ),
      h('div', { className: 'landing-stats__row' },
        h('div', { className: 'landing-stats__item' },
          h(AnimatedCounter, { value: shown ? shown.totalExecutions : 0 }),
          h('p', { className: 'landing-stats__label' }, stats.executionsLabel || '')
        ),
        h('div', { className: 'landing-stats__item' },
          h(AnimatedCounter, { value: shown ? shown.totalWorkflows : 0 }),
          h('p', { className: 'landing-stats__label' }, stats.workflowsLabel || '')
        )
      )
    )
  );
}

function LandingIntegrationsSection(props) {
  var integrations = (props.strings && props.strings.integrations) || {};
  return h('section', { className: 'landing-integrations' },
    h('div', { className: 'landing-integrations__content' },
      h('h2', { className: 'landing-integrations__title' },
        integrations.titlePrefix || '',
        h('span', { className: 'landing-integrations__highlight' }, integrations.titleHighlight || '')
      ),
      h('p', { className: 'landing-integrations__copy' }, integrations.copy || '')
    ),
    h('div', { className: 'landing-integrations__rows' },
      h(IntegrationsRibbon, { names: INTEGRATION_ROW_1 }),
      h(IntegrationsRibbon, { names: INTEGRATION_ROW_2, reverse: true })
    )
  );
}

var TRUSTED_LOGOS = [
  { name: 'Stripe', icon: 'S' },
  { name: 'Vercel', icon: 'V' },
  { name: 'Linear', icon: 'L' },
  { name: 'Notion', icon: 'N' },
  { name: 'Figma', icon: 'F' },
  { name: 'Slack', icon: 'S' },
  { name: 'GitHub', icon: 'G' },
  { name: 'Shopify', icon: 'S' }
];

function TrustedBySection() {
  return h('section', { className: 'trusted-section' },
    h('p', { className: 'trusted-section__eyebrow' }, 'Trusted & used in projects'),
    h('h2', { className: 'trusted-section__title' }, 'Brugt af teams der bygger med moderne værktøjer'),
    h('div', { className: 'trusted-section__logos' },
      TRUSTED_LOGOS.map(function(logo) {
        return h('div', { key: logo.name, className: 'trusted-logo' },
          h('span', { className: 'trusted-logo__icon' }, logo.icon),
          h('span', { className: 'trusted-logo__name' }, logo.name)
        );
      })
    )
  );
}

function LandingHome(props) {
  var navItems = Array.isArray(props.navItems) ? props.navItems : [];
  var pageContent = props.landingPageContent || {};
  var strings = props.landingStrings || {};
  var footer = strings.footer || {};
  // Paid plans are hidden for now — the Pricing section surfaces only the free tier.
  var pricingPage = pageContent.pricing || {};
  var freeTiers = Array.isArray(pricingPage.tiers)
    ? pricingPage.tiers.filter(function(tier) { return tier && /free/i.test(tier.name || ''); })
    : [];
  // Render the Pricing route with the free tier only (paid plans stay hidden during beta).
  var freeOnlyPricingPage = Object.assign({}, pricingPage, { tiers: freeTiers });
  return h('main', { className: 'landing-story' },
    h(HeroSection, { strings: strings, onGetStarted: props.onGetStarted, onExplore: props.onExplore }),
    navItems[0] ? h(MarketingPage, {
      key: navItems[0].sectionId,
      page: pageContent[navItems[0].route],
      sectionId: navItems[0].sectionId,
      labels: strings.marketingLabels,
      onGetStarted: props.onGetStarted,
      onSelectPlan: props.onSelectPlan
    }) : null,
    h(LandingVideoSection, { strings: strings }),
    h(LandingStatsSection, { strings: strings }),
    h(LandingIntegrationsSection, { strings: strings }),
    h(ComparisonTable, { strings: strings }),
    h(LandingAutopilotSection, { strings: strings }),
    // h(TrustedBySection),
    navItems.slice(1).map(function(item) {
      return h(MarketingPage, {
        key: item.sectionId,
        page: item.route === 'pricing' ? freeOnlyPricingPage : pageContent[item.route],
        sectionId: item.sectionId,
        labels: strings.marketingLabels,
        onGetStarted: props.onGetStarted,
        onSelectPlan: props.onSelectPlan
      });
    }),
    h(LandingFinalCta, { strings: strings, onGetStarted: props.onGetStarted, onExplorePricing: props.onExplorePricing }),
    h('footer', { className: 'landing-footer' },
      h('div', { className: 'landing-footer__inner' },
        h('span', { className: 'landing-footer__copy' }, '\u00a9 ' + new Date().getFullYear() + ' AlleFlow'),
        h('div', { className: 'landing-footer__links' },
          h('a', { href: '/apidocs', className: 'landing-footer__link', onClick: function(e) { e.preventDefault(); props.onNavigateTo('/apidocs'); } }, footer.apiDocs || 'API docs'),
          h('a', { href: '/privacy.html', className: 'landing-footer__link' }, footer.privacy || 'Privacy policy'),
          h('a', { href: '/terms.html', className: 'landing-footer__link' }, footer.terms || 'Terms of service')
        )
      )
    )
  );
}

function createEmptyWorkflowState() {
  return {
    workflowName: 'Untitled Workflow',
    aiInstruction: '',
    nodes: ensureDefaultStartNode([]),
    connections: [],
    groups: [],
    checkSilentError: false
  };
}

function normalizeWorkflowState(savedState) {
  if (!savedState || typeof savedState !== 'object') return createEmptyWorkflowState();
  return {
    workflowName: savedState.workflowName !== undefined && savedState.workflowName !== null
      ? String(savedState.workflowName)
      : 'Untitled Workflow',
    aiInstruction: savedState.aiInstruction !== undefined && savedState.aiInstruction !== null
      ? String(savedState.aiInstruction)
      : '',
    nodes: clearFedTemporaryVariableValues(
      migrateToolAgentNodes(stripStaleNodeSize(ensureDefaultStartNode(savedState.nodes))),
      savedState.connections
    ),
    connections: Array.isArray(savedState.connections) ? savedState.connections : [],
    groups: Array.isArray(savedState.groups) ? savedState.groups : [],
    checkSilentError: !!savedState.checkSilentError
  };
}

// A workflow is "blank" when it carries no nodes. Saving such a state would
// overwrite a real server-side workflow with an empty canvas — which is exactly
// what happens if a save fires mid-run before the live state has fully rehydrated.
// We refuse to persist it so a blank payload can never clobber saved work.
function isBlankWorkflowState(state) {
  return !state || !Array.isArray(state.nodes) || state.nodes.length === 0;
}

// Fed to the canvas in Live mode when nothing is published yet, so it shows its
// own empty placeholder instead of erroring on a missing state.
var EMPTY_LIVE_STATE = { workflowName: '', nodes: [], connections: [] };

function createExecutionState(overrides) {
  return Object.assign({
    status: 'idle',
    executionId: null,
    activeNodeId: null,
    error: '',
    nodeStatusById: {},
    nodeFixById: {},
    nodeTimingById: {},
    nodeTokensById: {},
    traversedConnectionIds: {},
    nodeInputById: {},
    nodeOutputById: {},
    nodeCallCountById: {},
    nodeVariableSnapshotById: {},
    variableValuesByName: {},
    accessedVariableNames: {},
    accessedVariableNodeId: {},
    silentErrorByNodeId: {},
    silentCheckingByNodeId: {},
    silentCheckSkippedByNodeId: {}
  }, overrides || {});
}

// Find the node id that will fire first on this run, so we can light it up
// optimistically before SSE connects. Honours fromNodeId (re-run from a
// specific node); otherwise picks the workflow's Start node.
function findStartNodeForOptimisticGlow(workflowState, fromNodeId) {
  if (!workflowState || !Array.isArray(workflowState.nodes)) return null;
  if (fromNodeId) return String(fromNodeId);
  for (var i = 0; i < workflowState.nodes.length; i++) {
    var n = workflowState.nodes[i];
    if (n && (n.icon === 'start' || n.type === 'start')) return n.id;
  }
  return null;
}

function createPublicStreamCode() {
  return 'evt_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

function createPublicListenerSessionId() {
  return 'lst_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

function createPublicListenState(overrides) {
  return Object.assign({
    enabled: false,
    code: '',
    error: '',
    connectionStatus: 'idle',
    lastEventAt: ''
  }, overrides || {});
}

function logPublicListenDebug(action, details) {
  if (typeof console === 'undefined' || !console.log) return;
  console.log('[public-listen][' + new Date().toISOString() + '] ' + action, details || {});
}

var PUBLIC_LISTENER_HEARTBEAT_MS = 1000 * 60;

function getPersistedPublicListenerSessionId() {
  try {
    var key = 'alleflow-public-listener-session';
    var existing = sessionStorage.getItem(key);
    if (existing) return existing;
    var created = createPublicListenerSessionId();
    sessionStorage.setItem(key, created);
    return created;
  } catch (error) {
    return createPublicListenerSessionId();
  }
}

function getPublicListenStorageKey(workflowId) {
  return 'alleflow-public-listen:' + String(workflowId || '').trim();
}

function readPersistedPublicListenConfig(workflowId) {
  try {
    var raw = sessionStorage.getItem(getPublicListenStorageKey(workflowId));
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (!parsed || parsed.enabled !== true || !parsed.code) return null;
    return {
      enabled: true,
      code: String(parsed.code)
    };
  } catch (error) {
    return null;
  }
}

function writePersistedPublicListenConfig(workflowId, config) {
  try {
    sessionStorage.setItem(getPublicListenStorageKey(workflowId), JSON.stringify(config || {}));
  } catch (error) {}
}

function clearPersistedPublicListenConfig(workflowId) {
  try {
    sessionStorage.removeItem(getPublicListenStorageKey(workflowId));
  } catch (error) {}
}

function buildWorkflowExportName(name) {
  var normalized = String(name || '').trim() || 'untitled-workflow';
  return normalized.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') + '.json';
}

function downloadWorkflowJson(state) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  var normalized = normalizeWorkflowState(state);
  var compressed = compressWorkflow(normalized);
  var payload = JSON.stringify(compressed, null, 2);
  var blob = new Blob([payload], { type: 'application/json' });
  var href = window.URL.createObjectURL(blob);
  var link = document.createElement('a');
  link.href = href;
  link.download = buildWorkflowExportName(state && state.workflowName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(href);
}

function buildWorkflowCodeFileName(name) {
  var normalized = String(name || '').trim() || 'untitled-workflow';
  return normalized.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') + '.js';
}

function downloadWorkflowCode(state) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  var normalized = normalizeWorkflowState(state);
  var source = workflowToCode(normalized);
  var blob = new Blob([source], { type: 'text/javascript' });
  var href = window.URL.createObjectURL(blob);
  var link = document.createElement('a');
  link.href = href;
  link.download = buildWorkflowCodeFileName(state && state.workflowName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(href);
}

function readTextFile(file) {
  return new Promise(function(resolve, reject) {
    if (!file) {
      reject(new Error('No file selected.'));
      return;
    }
    var reader = new FileReader();
    reader.onload = function(event) {
      resolve(event && event.target ? String(event.target.result || '') : '');
    };
    reader.onerror = function() {
      reject(new Error('Could not read the selected file.'));
    };
    reader.readAsText(file);
  });
}

function describeChange(prev, next) {
  if (!prev) return 'Initial state';
  var pn = (prev.nodes || []).length, nn = (next.nodes || []).length;
  if (nn > pn) return 'Added node';
  if (nn < pn) return 'Removed node';
  var pc = (prev.connections || []).length, nc = (next.connections || []).length;
  if (nc > pc) return 'Connected nodes';
  if (nc < pc) return 'Removed connection';
  if (prev.workflowName !== next.workflowName) return 'Renamed workflow';
  var prevMap = {};
  (prev.nodes || []).forEach(function(n) { prevMap[n.id] = n; });
  var nextNodes = next.nodes || [];
  for (var i = 0; i < nextNodes.length; i++) {
    var n = nextNodes[i], p = prevMap[n.id];
    if (!p) continue;
    if (p.title !== n.title) return 'Renamed "' + (n.title || 'node') + '"';
    if (JSON.stringify(p.paramValues) !== JSON.stringify(n.paramValues)) return 'Edited "' + (n.title || 'node') + '"';
    if (JSON.stringify(p.size) !== JSON.stringify(n.size)) return 'Resized "' + (n.title || 'node') + '"';
    if (p.position && n.position && (p.position.left !== n.position.left || p.position.top !== n.position.top)) return 'Moved "' + (n.title || 'node') + '"';
  }
  return 'Changed workflow';
}

function regenerateNodeIds(state) {
  if (!state || !Array.isArray(state.nodes)) return state;
  var suffix = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  var idMap = {};
  var nodes = state.nodes.map(function(node) {
    if (!node || !node.id) return node;
    var oldId = String(node.id);
    var newId = oldId + '-' + suffix;
    idMap[oldId] = newId;
    return Object.assign({}, node, { id: newId });
  });
  var connections = Array.isArray(state.connections)
    ? state.connections.map(function(conn) {
        if (!conn) return conn;
        return Object.assign({}, conn, {
          id: conn.id ? String(conn.id) + '-' + suffix : conn.id,
          from: conn.from && idMap[String(conn.from)] ? idMap[String(conn.from)] : conn.from,
          to: conn.to && idMap[String(conn.to)] ? idMap[String(conn.to)] : conn.to
        });
      })
    : [];
  var groups = Array.isArray(state.groups)
    ? state.groups.map(function(g) {
        if (!g) return g;
        var newNodeIds = Array.isArray(g.nodeIds)
          ? g.nodeIds.map(function(nid) { return idMap[String(nid)] || nid; })
          : g.nodeIds;
        return Object.assign({}, g, {
          id: g.id ? String(g.id) + '-' + suffix : g.id,
          nodeIds: newNodeIds
        });
      })
    : [];
  return Object.assign({}, state, { nodes: nodes, connections: connections, groups: groups });
}

function parseImportedWorkflowState(fileText) {
  var parsed;
  try {
    parsed = JSON.parse(String(fileText || ''));
  } catch (error) {
    throw new Error('Invalid JSON file.');
  }
  if (isCompressedWorkflow(parsed)) {
    parsed = decompressWorkflow(parsed);
  }
  return normalizeWorkflowState(regenerateNodeIds(parsed));
}

// Walk a workflow state's nodes, find every flow-ref node, look up its
// referenced flow, and refresh the snapshot fields (title + hyperparameters)
// from the source. The user's edits to paramValues are preserved for any
// hyperparameter key that still exists in the source flow's start node — keys
// that were renamed or removed in the source are dropped.
//
// This makes flow-ref nodes act like a live link to the .flw file: if the
// source flow's title or start params change, the parent flow picks up the
// new values the next time it's opened.
function refreshFlowRefNodesFromSource(state) {
  if (!state || !Array.isArray(state.nodes)) return Promise.resolve(state);
  var refNodes = state.nodes.filter(function(n) { return n && n.icon === 'flow-ref' && n.flowRefId; });
  if (refNodes.length === 0) return Promise.resolve(state);

  // Dedupe by flowRefId so we only fetch each referenced flow once even if
  // it's used by several nodes in the same parent.
  var idToNodes = {};
  refNodes.forEach(function(n) {
    var id = String(n.flowRefId);
    if (!idToNodes[id]) idToNodes[id] = [];
    idToNodes[id].push(n);
  });

  var ids = Object.keys(idToNodes);
  return Promise.all(ids.map(function(id) {
    return loadWorkflowStateById(id).then(function(result) {
      return { id: id, state: result };
    }).catch(function() {
      return { id: id, state: null };
    });
  })).then(function(results) {
    var refStateById = {};
    results.forEach(function(r) { refStateById[r.id] = r.state; });

    var nextNodes = state.nodes.map(function(node) {
      if (!node || node.icon !== 'flow-ref' || !node.flowRefId) return node;
      var refState = refStateById[String(node.flowRefId)];
      if (!refState) return node; // referenced flow couldn't be loaded — keep snapshot
      var startNode = (Array.isArray(refState.nodes) ? refState.nodes : []).find(function(n) {
        return n && n.icon === 'start';
      });
      var freshParams = (startNode && Array.isArray(startNode.hyperparameters))
        ? startNode.hyperparameters.map(function(p) { return Object.assign({}, p); })
        : [];
      var prevValues = (node.paramValues && typeof node.paramValues === 'object') ? node.paramValues : {};
      var nextValues = freshParams.reduce(function(acc, p) {
        if (!p || !p.key) return acc;
        if (Object.prototype.hasOwnProperty.call(prevValues, p.key)) {
          // Preserve the parent's edited value if the key still exists.
          acc[p.key] = prevValues[p.key];
        } else {
          var startSaved = startNode && startNode.paramValues && startNode.paramValues[p.key];
          acc[p.key] = startSaved !== undefined
            ? startSaved
            : (p.default !== undefined ? p.default : '');
        }
        return acc;
      }, {});
      var freshTitle = String(refState.name || node.flowRefName || node.title || 'Flow');
      return Object.assign({}, node, {
        title: freshTitle,
        flowRefName: freshTitle,
        hyperparameters: freshParams,
        paramValues: nextValues
      });
    });

    return Object.assign({}, state, { nodes: nextNodes });
  });
}

// ---------------------------------------------------------------------------
// Friendly "a run hit a problem" notice — shared look + a tiny builder so every
// run-failure popup reads and looks the same (no duplicated markup or styles).
//   - lead:       text/elements for the opening sentence (string or array)
//   - issueText:  optional reason; when empty the "What went wrong" box is hidden
//   - stepsLabel: heading above the numbered list
//   - steps:      array of steps; each step is a string or an array of children
// Highlight helpers for callers: STYLE.mark (action), STYLE.strong, STYLE.chip.
// ---------------------------------------------------------------------------
var RUN_NOTICE_STYLE = {
  lead: { margin: '0 0 14px', color: '#555', fontSize: '13.5px', lineHeight: 1.55 },
  label: { fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6b8e5a', margin: '0 0 6px' },
  issue: { background: '#fff7ed', borderLeft: '3px solid #f59e0b', borderRadius: '8px', padding: '9px 11px', color: '#7c2d12', fontSize: '13px', lineHeight: 1.45, margin: '0 0 16px' },
  steps: { margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: '10px', color: '#555', fontSize: '13.5px', lineHeight: 1.5 },
  strong: { color: '#1a1a1a', fontWeight: 600 },
  // "Marker pen" highlight for the key action in each important step.
  mark: { background: '#eaf5e1', color: '#2f5121', fontWeight: 700, padding: '1px 6px', borderRadius: '5px', WebkitBoxDecorationBreak: 'clone', boxDecorationBreak: 'clone' },
  chip: { background: '#eef2ff', color: '#4338ca', fontWeight: 600, fontSize: '12px', padding: '1px 6px', borderRadius: '5px', whiteSpace: 'nowrap' }
};

function buildRunFailureNotice(opts) {
  var S = RUN_NOTICE_STYLE;
  var leadChildren = Array.isArray(opts.lead) ? opts.lead : [opts.lead];
  var stepItems = (opts.steps || []).map(function(step, i) {
    var children = Array.isArray(step) ? step : [step];
    return h.apply(null, ['li', { key: i }].concat(children));
  });
  return h('div', { style: { width: '100%' } },
    h.apply(null, ['p', { style: S.lead }].concat(leadChildren)),
    opts.issueText ? h('div', { style: S.label }, opts.issueLabel || 'What went wrong') : null,
    opts.issueText ? h('div', { style: S.issue }, opts.issueText) : null,
    h('div', { style: S.label }, opts.stepsLabel || 'What to do next'),
    h.apply(null, ['ol', { style: S.steps }].concat(stepItems))
  );
}

// True when the beta gate should block this user (waitlist mode on + not approved).
function isUserGated(user) {
  return !!(betaConfig.WAITLIST_MODE && user && user.waitlistStatus !== 'approved');
}

function App() {
  var isEmbed = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embed') === 'true';
  var [currentUser, setCurrentUser] = useState(null);
  var [isAuthLoading, setIsAuthLoading] = useState(true);
  // Beta waitlist gate — when on, an unapproved user sees the "join the waitlist"
  // popup instead of the app (see betaConfig + handleAuthSuccess + boot effect).
  var [showBetaGate, setShowBetaGate] = useState(false);
  var [route, setRoute] = useState(getRouteFromLocation());
  var [storeTemplateId, setStoreTemplateId] = useState(getStoreIdFromLocation());
  var [editorPublishWorkflow, setEditorPublishWorkflow] = useState(null);
  // Live (production) version: the current published version for the open
  // workflow (null = none published yet), plus the publish dialog state.
  var [liveVersionInfo, setLiveVersionInfo] = useState(null);
  var [publishLiveOpen, setPublishLiveOpen] = useState(false);
  var [publishLiveBusy, setPublishLiveBusy] = useState(false);
  var [publishLiveError, setPublishLiveError] = useState('');
  // Staging / Live view toggle. 'staging' = editable canvas (default); 'live' =
  // read-only render of the published version. liveSnapshot holds the fetched
  // live `value` while in Live mode (null until loaded / when none published).
  var [workflowMode, setWorkflowMode] = useState(isEmbed ? 'live' : 'staging');
  var [liveSnapshot, setLiveSnapshot] = useState(null);
  var [liveSnapshotLoading, setLiveSnapshotLoading] = useState(false);
  // Live-mode config edits (variable values / hyperparameters) that haven't been
  // saved back to the live snapshot yet, plus an in-flight save flag.
  var [liveDirty, setLiveDirty] = useState(false);
  var [liveSaving, setLiveSaving] = useState(false);
  var [liveOfflineBusy, setLiveOfflineBusy] = useState(false);
  var liveSnapshotRef = useRef(null);
  liveSnapshotRef.current = liveSnapshot;
  var [language, setLanguage] = useState(detectLanguage());
  var [logoPickerNodeId, setLogoPickerNodeId] = useState('');
  var navItems = getLandingNavItems(language);
  var landingPageContent = getLandingPageContentByRoute(language);
  var landingStrings = getLandingStrings(language);
  var legacyMarketingPathMap = buildLegacyMarketingPathMap(navItems);
  function applyLanguage(nextLang) {
    var normalized = nextLang === 'da' ? 'da' : 'en';
    setStoredLanguage(normalized);
    setActiveLanguage(normalized);
    setLanguage(normalized);
  }
  function handleChangeLanguage(nextLang) {
    var normalized = nextLang === 'da' ? 'da' : 'en';
    var previous = language;
    applyLanguage(normalized);
    if (!currentUser) return Promise.resolve({ language: normalized });

    return request('/user/language', {
      method: 'PUT',
      body: { language: normalized }
    }).then(function(result) {
      var saved = result && result.language === 'da' ? 'da' : 'en';
      applyLanguage(saved);
      setCurrentUser(function(user) {
        return user ? Object.assign({}, user, { language: saved }) : user;
      });
      return { language: saved };
    }).catch(function(error) {
      applyLanguage(previous);
      throw error;
    });
  }
  useEffect(function() {
    if (!currentUser || !currentUser.language) return;
    var accountLanguage = currentUser.language === 'da' ? 'da' : 'en';
    if (accountLanguage !== language) applyLanguage(accountLanguage);
  }, [currentUser && currentUser.language]);
  var [dashboardTab, setDashboardTab] = useState(typeof window !== 'undefined' ? String(window.location.hash || '').replace(/^#/, '') : '');
  var [workflows, setWorkflows] = useState([]);
  var [isWorkflowListHydrated, setIsWorkflowListHydrated] = useState(false);
  var [isCreatingWorkflow, setIsCreatingWorkflow] = useState(false);
  var [isDuplicatingWorkflow, setIsDuplicatingWorkflow] = useState(false);
  var [workbooks, setWorkbooks] = useState([]);
  var [isCreatingWorkbook, setIsCreatingWorkbook] = useState(false);
  var [currentWorkbook, setCurrentWorkbook] = useState(null);
  var [selectedWorkbookId, setSelectedWorkbookId] = useState(getWorkbookIdFromLocation());
  var [isWorkbookPanelOpen, setIsWorkbookPanelOpen] = useState(false);
  var [workbookMoveItem, setWorkbookMoveItem] = useState(null);
  // Asks the explorer panel to put an item into inline-rename mode (used right
  // after creating a folder so the user can name it without a native prompt).
  var [workbookRenameRequest, setWorkbookRenameRequest] = useState(null);
  var creatingWorkbookFlowRef = useRef(false);
  var [selectedWorkflowId, setSelectedWorkflowId] = useState(getWorkflowIdFromLocation());
  if (isEmbed && selectedWorkflowId) {
    setEmbedWorkflowId(selectedWorkflowId);
  }
  var [workflowState, setWorkflowState] = useState(createEmptyWorkflowState());
  var [isWorkflowHydrated, setIsWorkflowHydrated] = useState(false);
  // True for the brief paint frame after hydration while the canvas commits the
  // node DOM (reported by WorkflowCanvas via onRenderingChange). The toolbar
  // treats the flow as "ready" only once both hydrated AND not rendering.
  var [isCanvasRendering, setIsCanvasRendering] = useState(false);
  var handleCanvasRenderingChange = useCallback(function(rendering) {
    setIsCanvasRendering(!!rendering);
  }, []);

  // Tracks the single node currently selected on the canvas. When set, the
  // right sidebar swaps from System Objects → that node's parameter editor.
  var [selectedNodeId, setSelectedNodeId] = useState(null);

  // Append a new free-form hyperparameter to a node. The popover collects
  // {name, type, initialValue}; we derive a unique camelCase key from name
  // and seed paramValues with the initial value so the row works immediately.
  function handleSidebarParamAdd(nodeId, draft) {
    if (!nodeId || !draft) return;
    var cleanLabel = String(draft.name || '').trim();
    if (!cleanLabel) return;
    var type = String(draft.type || 'text').trim() || 'text';
    var initialValue = draft.initialValue;
    setWorkflowState(function(prev) {
      if (!prev || !Array.isArray(prev.nodes)) return prev;
      var nextNodes = prev.nodes.map(function(node) {
        if (node.id !== nodeId) return node;
        var hp = Array.isArray(node.hyperparameters) ? node.hyperparameters.slice() : [];
        var key = uniqueParamKey(deriveParamKey(cleanLabel), hp);
        hp.push({ id: 'p-' + Date.now().toString(36), key: key, label: cleanLabel, type: type, default: initialValue });
        var nextValues = Object.assign({}, node.paramValues || {});
        if (initialValue !== undefined && initialValue !== '' && initialValue !== null) {
          nextValues[key] = initialValue;
        }
        return Object.assign({}, node, { hyperparameters: hp, paramValues: nextValues });
      });
      return Object.assign({}, prev, { nodes: nextNodes });
    });
  }

  // Remove a hyperparameter from a node entirely (drops the row + its stored
  // value). Symmetric to handleSidebarParamAdd. Used by the sidebar form's
  // trash button on custom / start / AI-custom nodes.
  function handleSidebarParamRemove(nodeId, key) {
    if (!nodeId || !key) return;
    setWorkflowState(function(prev) {
      if (!prev || !Array.isArray(prev.nodes)) return prev;
      var nextNodes = prev.nodes.map(function(node) {
        if (node.id !== nodeId) return node;
        var hp = Array.isArray(node.hyperparameters) ? node.hyperparameters : [];
        var nextHp = hp.filter(function(p) { return !p || p.key !== key; });
        var nextParams = Object.assign({}, node.paramValues || {});
        delete nextParams[key];
        return Object.assign({}, node, { hyperparameters: nextHp, paramValues: nextParams });
      });
      return Object.assign({}, prev, { nodes: nextNodes });
    });
  }

  // Rename a hyperparameter's label/key in-place. Migrates any existing
  // paramValues entry so the user-entered value travels with the rename.
  // Renaming a param renames its key too, so the node's output field follows the
  // label the user sees (rename "sku" -> "id" and Start now emits { id: ... }).
  // The key is what downstream @refs and inputs[0].<key> reads use, so those
  // still have to be updated by hand after a rename.
  function handleSidebarParamRename(nodeId, oldKey, newLabel) {
    if (!nodeId || !oldKey) return;
    var cleanLabel = String(newLabel || '').trim();
    if (!cleanLabel) return;
    setWorkflowState(function(prev) {
      if (!prev || !Array.isArray(prev.nodes)) return prev;
      var nextNodes = prev.nodes.map(function(node) {
        if (node.id !== nodeId) return node;
        var hp = Array.isArray(node.hyperparameters) ? node.hyperparameters : [];
        // Stay unique against the *other* params — comparing against itself
        // would bump an unchanged key to "id_2" on every no-op rename.
        var others = hp.filter(function(p) { return p && p.key !== oldKey; });
        var cleanKey = uniqueParamKey(deriveParamKey(cleanLabel), others);
        var keyChanged = cleanKey !== oldKey;
        var nextHp = hp.map(function(p) {
          if (!p || p.key !== oldKey) return p;
          var patched = Object.assign({}, p);
          patched.label = cleanLabel;
          if (keyChanged) patched.key = cleanKey;
          return patched;
        });
        var nextParams = Object.assign({}, node.paramValues || {});
        if (keyChanged && Object.prototype.hasOwnProperty.call(nextParams, oldKey)) {
          nextParams[cleanKey] = nextParams[oldKey];
          delete nextParams[oldKey];
        }
        return Object.assign({}, node, { hyperparameters: nextHp, paramValues: nextParams });
      });
      return Object.assign({}, prev, { nodes: nextNodes });
    });
  }

  // Apply a param change to a node by id. Used by the sidebar param editor
  // so canvas-side hyperparams can now be edited from the side panel.
  function handleSidebarParamChange(nodeId, key, value) {
    if (!nodeId) return;
    var execOutputs = executionState && executionState.nodeOutputById ? executionState.nodeOutputById : {};
    var pendingTempPreview = null;
    setWorkflowState(function(prev) {
      if (!prev || !Array.isArray(prev.nodes)) return prev;
      var changed = false;
      var nextNodes = prev.nodes.map(function(node) {
        if (node.id !== nodeId) return node;
        var nextParams = Object.assign({}, node.paramValues || {});
        nextParams[key] = value;
        changed = true;
        var patch = { paramValues: nextParams };
        // Lockstep: mirror the value into the matching hyperparameter's default
        // so the "Edit this step" modal stays in agreement with the sidebar.
        var syncedHp = syncHyperparamDefault(node.hyperparameters, key, value);
        if (syncedHp !== node.hyperparameters) patch.hyperparameters = syncedHp;
        // Keep set-variable node titles in sync with the picked variable so the
        // canvas card label updates immediately ("Set Google" instead of the
        // static "Set Variable" type label). Without this, the card relies
        // solely on the displayTitle prop which can lag behind in some render
        // paths and ship stale text to the user.
        if (node.icon === 'set-variable' && key === 'variable') {
          var picked = String(value || '').trim();
          patch.title = picked ? 'Set ' + picked : 'Set Variable';
        }
        // Same idea for condition nodes: their tile title should reflect the
        // current rule (e.g. "@Google = ?") instead of the static "Condition"
        // label. The variable lives in paramValues.variable; operator + value
        // live in conditionRule, so we recompute the compact title from a
        // pretend-merged node that includes the new param.
        if (node.icon === 'condition' && key === 'variable') {
          patch.title = compactConditionTitle(Object.assign({}, node, { paramValues: nextParams }));
        }
        return Object.assign({}, node, patch);
      });
      if (!changed) return prev;
      // Set Variable: picking the target variable from the dropdown pulls the
      // connected source's current output into that variable (event-driven).
      var targetNode = prev.nodes.find(function(n) { return n.id === nodeId; });
      var isSetVar = targetNode && (targetNode.templateId === 'sys-set-variable' || targetNode.icon === 'set-variable');
      if (isSetVar && key === 'variable') {
        var auto = computeSetVariableAutofill(nextNodes, prev.connections, nodeId, execOutputs);
        nextNodes = auto.nextNodes;
        pendingTempPreview = auto.tempPreview;
      }
      return Object.assign({}, prev, { nodes: nextNodes });
    });
    // Temporary target: push the value into the ephemeral live map (shown in the
    // panel, never saved). Done outside the state updater to keep it pure.
    if (pendingTempPreview) setVariableLivePreviewValue(pendingTempPreview.varName, pendingTempPreview.value);
  }

  // Write a value into the ephemeral live-variable map (executionState) so a
  // Temporary variable shows its Set-Variable result without persisting it.
  function setVariableLivePreviewValue(varName, value) {
    if (!varName) return;
    setExecutionState(function(prev) {
      var nextVars = Object.assign({}, prev.variableValuesByName || {});
      nextVars[varName] = value;
      return Object.assign({}, prev, { variableValuesByName: nextVars });
    });
  }

  // Pure updater that sets `node.credentialIds[provider] = id` on the matching
  // node (empty string clears the pin, restoring the workflow-owner default).
  // Shared by the staging and Live writers so the merge logic lives in one place.
  function applyCredentialChange(nodeId, provider, credentialId) {
    return function(prev) {
      if (!prev || !Array.isArray(prev.nodes)) return prev;
      var changed = false;
      var nextNodes = prev.nodes.map(function(node) {
        if (node.id !== nodeId) return node;
        var nextIds = Object.assign({}, node.credentialIds || {});
        var trimmed = String(credentialId || '').trim();
        if (trimmed) nextIds[provider] = trimmed;
        else delete nextIds[provider];
        changed = true;
        return Object.assign({}, node, { credentialIds: nextIds });
      });
      if (!changed) return prev;
      return Object.assign({}, prev, { nodes: nextNodes });
    };
  }

  // Per-node OAuth account pin (Staging). Sandbox `<provider>.login()` reads this
  // at run time via runtimeContext.
  function handleSidebarCredentialChange(nodeId, provider, credentialId) {
    if (!nodeId || !provider) return;
    setWorkflowState(applyCredentialChange(nodeId, provider, credentialId));
  }

  // Per-node OAuth account pin in LIVE mode. Routes through handleSetLiveSnapshot
  // so the change lands on the live snapshot and marks it dirty — saved by the
  // same Live "Save changes" button as variable values (see saveLiveWorkflowConfig
  // on the server, which whitelists credentialIds alongside variable values).
  function handleLiveCredentialChange(nodeId, provider, credentialId) {
    if (!nodeId || !provider) return;
    handleSetLiveSnapshot(applyCredentialChange(nodeId, provider, credentialId));
  }

  // Replace a Condition node's rule. Used by the sidebar's ConditionSidebarEditor.
  function handleSidebarConditionRuleChange(nodeId, nextRule) {
    if (!nodeId) return;
    setWorkflowState(function(prev) {
      if (!prev || !Array.isArray(prev.nodes)) return prev;
      var changed = false;
      var nextNodes = prev.nodes.map(function(node) {
        if (node.id !== nodeId) return node;
        changed = true;
        // Recompute the title from the new rule so the canvas tile updates
        // alongside the operator/value change.
        var nextTitle = compactConditionTitle(Object.assign({}, node, { conditionRule: nextRule }));
        return Object.assign({}, node, { conditionRule: nextRule, title: nextTitle });
      });
      if (!changed) return prev;
      return Object.assign({}, prev, { nodes: nextNodes });
    });
  }
  var [customObjectTemplates, setCustomObjectTemplates] = useState([]);
  var [editorOpen, setEditorOpen] = useState(false);
  var [editingObject, setEditingObject] = useState(null);
  var [autoFixPrompt, setAutoFixPrompt] = useState(null);
  // When autopilot opens the editor to fix a flagged node, we also surface the
  // specific reason DeepSeek gave for flagging it, so the Ask panel can show
  // the user what the checker thought was wrong instead of a blank prompt.
  var [autoFixReason, setAutoFixReason] = useState('');
  // 'doctor' | 'autopilot' | null — disables the Q&A input when set so the
  // user can't interrupt an automated fix mid-flight.
  var [autoFixSource, setAutoFixSource] = useState(null);
  var [aiInstructionModalOpen, setAiInstructionModalOpen] = useState(false);
  // Doctor chat sidebar — toggled from the Header toolbar (top-right).
  // Lifted out of WorkflowCanvas so the header button can drive it.
  var [doctorChatOpen, setDoctorChatOpen] = useState(false);
  // ---- Remote Support (live collab) ----
  // Sidebar toggled from the Header toolbar: manage support agents + presence.
  var [remoteSupportOpen, setRemoteSupportOpen] = useState(false);
  // Set while THIS client is a support agent inside another user's live
  // session: {ownerId, ownerUsername, workflowId}. Null in normal editing.
  var [remoteSession, setRemoteSession] = useState(null);
  var remoteSessionRef = useRef(null);
  useEffect(function() { remoteSessionRef.current = remoteSession; }, [remoteSession]);
  // Collab plumbing lives in refs so socket traffic (ops, cursors) never
  // re-renders the app; only roster changes touch React state.
  var collabClientRef = useRef(null);
  var collabActiveRef = useRef(false);
  // Last canvas state the session has seen — the broadcast effect diffs
  // against this after every commit and sends only the delta.
  var collabSyncedStateRef = useRef(null);
  // True for the commit that applies ops received FROM the session, so the
  // broadcast effect skips it (no echo loops).
  var applyingRemoteRef = useRef(false);
  // sessionId -> {x, y, username, color} in world coords, updated on every
  // cursor message; RemoteCursorsLayer reads it imperatively via rAF.
  var collabCursorsRef = useRef({});
  var [collabRoster, setCollabRoster] = useState([]);
  var [collabSelfSessionId, setCollabSelfSessionId] = useState('');
  // Notice shown on the dashboard after an agent session ends (owner left,
  // access revoked, session gone).
  var [collabNotice, setCollabNotice] = useState('');
  // Peers' selected nodes: sessionId -> {nodeId, color, username} (colored
  // ring on the canvas node).
  var [collabSelections, setCollabSelections] = useState({});
  // Peers' open popups: "sessionId:popup" -> {popup, nodeTitle, username,
  // color} (floating activity chips: "X is editing step Y").
  var [collabPeerUi, setCollabPeerUi] = useState({});
  // True while applying an exec event that arrived FROM the session, so
  // handleExecutionEvent doesn't forward it back (echo guard).
  var applyingRemoteExecRef = useRef(false);
  // TeamViewer mode — owner side: rrweb stop-fn while the screen is being
  // mirrored to agents, and one virtual-pointer controller per agent.
  var mirrorRecorderRef = useRef(null);
  var mirrorPingTimerRef = useRef(null);
  var remotePointersRef = useRef({});
  // TeamViewer mode — agent side: buffers/forwards the owner's rrweb events
  // into the RemoteSessionViewer.
  var mirrorBusRef = useRef(null);
  var [editorMode, setEditorMode] = useState('template');
  // UI Planner state
  var [uiPlannerOpen, setUiPlannerOpen] = useState(false);
  var [uiPageRoute, setUiPageRoute] = useState({
    pageId: getUiPageIdFromLocation(),
    slug: getUiPageSlugFromLocation()
  });
  var [workbookUiPages, setWorkbookUiPages] = useState([]);
  // All UI pages owned by the current user, regardless of which workbook they
  // belong to (or if they're standalone). Used to detect "does this workflow
  // already have a UI page?" so the canvas-footer button can flip from
  // "+ UI Page" → "View UI". Loaded once on auth and refreshed after any
  // planner save / page delete.
  var [allUserUiPages, setAllUserUiPages] = useState([]);
  var [fatalError, setFatalError] = useState('');
  // Stale-draft conflict: the open workflow's local draft is based on an older
  // server version than the latest save (someone else saved in the meantime).
  // { workflowId, serverUpdatedAt } | null — drives the DraftConflictBanner.
  var [draftConflict, setDraftConflict] = useState(null);
  var [draftConflictBusy, setDraftConflictBusy] = useState(false);
  var [runtimePopup, setRuntimePopup] = useState(null);
  // When the Start node declares inputs, pressing Play opens this popup to
  // collect them from the user before the run. null = closed.
  var [runInputModal, setRunInputModal] = useState(null);
  var [executionState, setExecutionState] = useState(createExecutionState());
  var [executionLogs, setExecutionLogs] = useState([]);
  // Live run-log lines for the bottom Run Log panel: [{ id, tone, text }],
  // newest-last. Cleared at the start of each run, appended on every SSE event.
  var [runLogLines, setRunLogLines] = useState([]);
  var [runLogOpen, setRunLogOpen] = useState(false);
  var runLogSeqRef = useRef(0);
  // Stash each node's input event so it can be paired with its later output.
  var runLogInputByNodeRef = useRef({});
  var [publicListenState, setPublicListenState] = useState(createPublicListenState());
  var [activeLandingSection, setActiveLandingSection] = useState(getLandingSectionFromLocation());
  var [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  var executionStreamCloseRef = useRef(null);
  // Generation token for run starts. Each startExecutionWithState claims the
  // next value; Stop (or a newer start) bumps it. The async start's .then opens
  // the SSE only if its token is still current — otherwise the run was
  // superseded (e.g. Stop pressed before the start POST resolved) and we tear
  // the server execution down instead of leaking an orphan stream.
  var executionStartTokenRef = useRef(0);
  var publicExecutionStreamCloseRef = useRef(null);
  var publicSeenEventKeysRef = useRef({});
  var publicListenerSessionIdRef = useRef(getPersistedPublicListenerSessionId());
  var publicListenerHeartbeatRef = useRef(null);
  var publicListenerAutoResumeRef = useRef('');
  var publicListenEnabledRef = useRef(false);
  var uiViewAutoListenRef = useRef(false);
  var selectedWorkflowIdRef = useRef(selectedWorkflowId);
  var workflowStateRef = useRef(workflowState);
  // Mirrors executionState so promise-based callers (the Doctor's runWorkflow
  // tool) can poll the live status without subscribing to React renders.
  var executionStateRef = useRef(null);
  var nodeRunStartClientTimeRef = useRef({});
  var executionTraceRef = useRef([]);
  var executionFromNodeIdRef = useRef(null);
  var priorRerunVariablesRef = useRef(null);
  var preservePriorExecStateRef = useRef(false);
  // Mirrors workflowState.checkSilentError so synchronous code paths (e.g.
  // startWorkflowExecution) can read the current flag without re-deriving from
  // React state. Kept in sync via a useEffect below.
  var checkSilentErrorRef = useRef(false);
  // Mirrors workflowMode ('staging' | 'live') so synchronous run paths can tell
  // whether a Live run is starting without a stale closure read. Kept in sync
  // via the useEffect below.
  var workflowModeRef = useRef(isEmbed ? 'live' : 'staging');
  var autoFixActiveRef = useRef(false);
  var autoFixNodeIdRef = useRef(null);
  var autoFixAttemptsRef = useRef({});
  var autoFixSnapshotRef = useRef(null);
  // When the server-side silent codefix gives up WITHOUT changing code, it
  // sends a node.fix.skipped event carrying the reason + a short root cause.
  // We stash that per node here and surface it as a single explanatory notice
  // when the run fails (see showCodefixSkippedNotice), instead of leaving the
  // user with no idea why the auto-fix did nothing.
  var codefixSkipInfoRef = useRef({});
  var codefixSkipNotifiedRef = useRef({});
  // Doctor-chat "Approve" queue. The user clicks Approve once and we open
  // AskCode for each step in turn — advancing only after the previous
  // step's editor closes. Holding the queue in a ref (not state) keeps the
  // advance logic synchronous inside handleCloseEditor.
  var doctorFixQueueRef = useRef([]);
  // True while ANY doctor-queue step is in flight (queue has items OR a
  // step's editor is currently open). Used by onAutoFixComplete to detect
  // "doctor flow finished — auto-close the editor" instead of running the
  // autopilot rerun-node sequence.
  var doctorFixActiveRef = useRef(false);
  // Per-queue result tally — captured as each step's onAutoFixComplete fires
  // so the doctor sidebar can post a final summary message when the last
  // step closes. Cleared when a new Approve cycle starts.
  // Shape: { applied: [{nodeId, nodeTitle}], skipped: [{nodeId, nodeTitle, reason}], totalSteps }
  var doctorFixResultsRef = useRef(null);
  // Push-driven notice the doctor sidebar consumes: { kind: 'queue_complete', summary, applied, skipped }.
  // Set by App.js when the Approve queue drains; cleared by the popup once it
  // converts the notice into an in-chat message.
  var [doctorPendingNotice, setDoctorPendingNotice] = useState(null);
  // Live per-step status fed into the doctor sidebar so each card can show
  // pending / active / done / skipped without waiting for the full queue to drain.
  // Shape: { activeNodeId: string|null, completed: {[nodeId]: 'done'|'skipped'} }
  var [doctorFixStatus, setDoctorFixStatus] = useState(null);
  // Tracks the most recent node.failed event so execution.failed can route to
  // AskCode for that node instead of showing a generic failure popup.
  var lastFailedNodeRef = useRef(null);
  // Autopilot driver runs as an explicit step-by-step script (Selenium-style):
  // wait-for-condition → act → wait-for-condition → act. The current step is
  // surfaced on the canvas via autopilotStatus so the user can watch it work.
  var [autopilotStatus, setAutopilotStatus] = useState(null);
  var editorOpenRef = useRef(false);
  // Which node the AskCode editor is currently open on — read by the copilot's
  // editNodeCode tool (getAskCodeEditorState) to detect a dead/closed edit.
  var editingNodeIdRef = useRef(null);
  // Copilot-opened AskCode auto-closes 3s after its turn completes.
  var manualFixAutoCloseRef = useRef(false);
  var AUTOFIX_MAX_ATTEMPTS = 3;
  var AUTOFIX_STEP_TIMEOUT_MS = 3000;
  var [isSaved, setIsSaved] = useState(true);
  var [isSaving, setIsSaving] = useState(false);
  var isSavedRef = useRef(true);
  var isWorkflowHydratedRef = useRef(false);
  // Local-first save model: edits go straight to localStorage and mark the
  // flow dirty. The Save button flushes all dirty flows + queued workbook ops
  // to the backend in one batch. lastHydratedStateRef holds the exact object
  // reference we just hydrated from cache/server so the dirty effect can
  // distinguish "user typed something" from "we just loaded the file".
  var dirtyFlowIdsRef = useRef({});
  var pendingWorkbookOpsRef = useRef([]);
  var lastHydratedStateRef = useRef(null);

  // --- Change history ---
  var [historyEntries, setHistoryEntries] = useState([]); // [{id,label,state,time}]
  var [historyPos, setHistoryPos] = useState(0);          // 0 = head (latest)
  var historyRef = useRef([]);
  var historyPosRef = useRef(0);
  var isHistoryNavRef = useRef(false);
  var historyDebounceRef = useRef(null);
  var prevStateForHistoryRef = useRef(null);
  // Undo/redo is scoped per workflow so undoing in one flow can never apply
  // edits from a different flow's history.
  var historyScopeIdRef = useRef('');

  var [isDark, setIsDark] = useState(function() {
    return localStorage.getItem('alleflow-theme') === 'dark';
  });

  // Re-hydrate any deferred workbook ops + dirty flow set from a previous
  // session so a refresh doesn't silently drop unsaved work. We rebuild the
  // dirty Set from the localStorage index; the actual cached state lives in
  // localFlowCache and is loaded lazily when each flow opens.
  useEffect(function() {
    pendingWorkbookOpsRef.current = localFlowCache.readPendingWorkbookOps() || [];
    var dirtyIds = localFlowCache.listDirtyFlowIds();
    var nextDirty = {};
    dirtyIds.forEach(function(id) { nextDirty[id] = true; });
    dirtyFlowIdsRef.current = nextDirty;
    if (dirtyIds.length || pendingWorkbookOpsRef.current.length) {
      setIsSaved(false);
    }
    // Drop run-progress records from workflows that haven't run in days —
    // they only matter for resume-from-node shortly after a run, and they
    // were a major localStorage quota hog (up to 2MB each, never cleaned).
    storageQuota.sweepStaleProgress();
  }, []);

  useEffect(function() {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    localStorage.setItem('alleflow-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  function handleThemeToggle() { setIsDark(function(v) { return !v; }); }

  function closeExecutionStream() {
    if (typeof executionStreamCloseRef.current === 'function') {
      executionStreamCloseRef.current();
      executionStreamCloseRef.current = null;
    }
  }

  function closePublicExecutionStream() {
    if (typeof publicExecutionStreamCloseRef.current === 'function') {
      publicExecutionStreamCloseRef.current();
      publicExecutionStreamCloseRef.current = null;
    }
    publicSeenEventKeysRef.current = {};
  }

  function stopPublicListenerHeartbeat() {
    if (publicListenerHeartbeatRef.current) {
      clearInterval(publicListenerHeartbeatRef.current);
      publicListenerHeartbeatRef.current = null;
    }
  }

  function releasePublicListenerLease(workflowIdOverride) {
    var workflowId = String(workflowIdOverride || selectedWorkflowId || '').trim();
    var sessionId = String(publicListenerSessionIdRef.current || '').trim();
    if (!workflowId || !sessionId) return;
    releaseWorkflowPublicListener(workflowId, sessionId).catch(function() {});
  }

  function releasePublicListenerLeaseImmediately(workflowIdOverride) {
    if (typeof window === 'undefined') return;
    var workflowId = String(workflowIdOverride || selectedWorkflowIdRef.current || selectedWorkflowId || '').trim();
    var sessionId = String(publicListenerSessionIdRef.current || '').trim();
    var accessToken = String(getAccessToken() || '').trim();
    if (!workflowId || !sessionId || !accessToken) return;

    var url = getApiBasePath()
      + '/workflow/' + encodeURIComponent(workflowId)
      + '/public-listener/release?access_token=' + encodeURIComponent(accessToken);
    var body = JSON.stringify({ sessionId: sessionId });

    try {
      if (navigator.sendBeacon) {
        var sent = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        logPublicListenDebug('listener.release.beacon', {
          workflowId: workflowId,
          sent: sent
        });
        if (sent) return;
      }
    } catch (error) {
      logPublicListenDebug('listener.release.beacon_error', {
        workflowId: workflowId,
        message: error && error.message ? error.message : 'Beacon release failed.'
      });
    }

    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true
      }).catch(function() {});
      logPublicListenDebug('listener.release.keepalive', {
        workflowId: workflowId
      });
    } catch (error) {
      logPublicListenDebug('listener.release.keepalive_error', {
        workflowId: workflowId,
        message: error && error.message ? error.message : 'Keepalive release failed.'
      });
    }
  }

  useEffect(function() {
    var mounted = true;
    loadCurrentUser().then(function(user) {
      if (!mounted) return;
      setCurrentUser(user || null);
      setIsAuthLoading(false);
      // Beta gate on refresh: a gated user landing on an app route (e.g.
      // /dashboard) gets the popup. Public/login/waitlist routes are exempt so
      // they can still reach the waitlist form.
      var publicRoutes = { home: true, login: true, waitlist: true, privacy: true, terms: true };
      if (isUserGated(user) && !publicRoutes[getRouteFromLocation()]) {
        setShowBetaGate(true);
      }
    });
    return function() {
      mounted = false;
    };
  }, []);

  useEffect(function() {
    if (isAuthLoading) return;
    if (isEmbed && route === 'workflow') return;
    // The consent screen needs a signed-in user to approve on behalf of. Keep
    // the whole OAuth query string so the request survives the login detour.
    if (route === 'oauth-consent' && !currentUser) {
      try {
        sessionStorage.setItem('alleflow_auth_return_to', window.location.pathname + window.location.search);
      } catch (_) {}
      navigateTo('/login');
      return;
    }
    if ((route === 'dashboard' || route === 'workflow') && !currentUser) {
      if (route === 'dashboard' && getExtensionAuthCodeFromLocation()) {
        try {
          sessionStorage.setItem('alleflow_auth_return_to', window.location.pathname + window.location.search);
        } catch (_) {}
      }
      navigateTo('/login');
    }
  }, [route, currentUser, isAuthLoading]);

  useEffect(function() {
    if (!currentUser || route !== 'dashboard') {
      // Keep `workflows` populated across the workflow editor route so the
      // workbook-detection effect can find the open flow without a race.
      // Only clear the dashboard-specific creation flags here.
      setIsCreatingWorkflow(false);
      setIsDuplicatingWorkflow(false);
      return;
    }

    var mounted = true;
    setIsWorkflowListHydrated(false);
    listWorkflows()
      .then(function(items) {
        if (!mounted) return;
        setWorkflows(Array.isArray(items) ? items : []);
      })
      .catch(function(err) {
        if (!mounted) return;
        // 401 here typically means the cached token went stale while we were
        // away (e.g. came back via browser-back from the public UI page hard
        // nav). Clear the token + user; the auth-gate effect above will then
        // bounce us to /login. Without this catch the rejection bubbles up as
        // an "Uncaught Unauthorized" red overlay on the dashboard.
        if (err && (err.status === 401 || err.statusCode === 401)) {
          setAccessToken('');
          setCurrentUser(null);
        }
      })
      .finally(function() {
        if (mounted) setIsWorkflowListHydrated(true);
      });

    return function() {
      mounted = false;
    };
  }, [currentUser, route]);

  function handleRefreshWorkflows() {
    if (!currentUser || route !== 'dashboard') return Promise.resolve([]);
    setIsWorkflowListHydrated(false);
    return listWorkflows()
      .then(function(items) {
        setWorkflows(Array.isArray(items) ? items : []);
        return items;
      })
      .catch(function(err) {
        if (err && (err.status === 401 || err.statusCode === 401)) {
          setAccessToken('');
          setCurrentUser(null);
        }
        return [];
      })
      .finally(function() {
        setIsWorkflowListHydrated(true);
      });
  }

  // Load workbooks list when on dashboard
  useEffect(function() {
    if (!currentUser || route !== 'dashboard') {
      setWorkbooks([]);
      return;
    }
    var mounted = true;
    workbookStorage.listWorkbooks()
      .then(function(items) {
        if (!mounted) return;
        setWorkbooks(Array.isArray(items) ? items : []);
      })
      .catch(function() {});
    return function() { mounted = false; };
  }, [currentUser, route]);

  function loadCurrentWorkbookFromId(workbookId) {
    if (!workbookId) {
      setCurrentWorkbook(null);
      return Promise.resolve(null);
    }
    return workbookStorage.loadWorkbook(workbookId)
      .then(function(wb) {
        setCurrentWorkbook(wb || null);
        return wb;
      })
      .catch(function() { setCurrentWorkbook(null); return null; });
  }

  // Auto-redirect /workbook/:id → first flow inside the workbook
  useEffect(function() {
    if (route !== 'workbook-redirect') return;
    if (isAuthLoading) return;
    if (!currentUser) { navigateTo('/login'); return; }
    var wbId = getWorkbookIdFromLocation();
    if (!wbId) { navigateTo('/dashboard'); return; }
    handleOpenWorkbook(wbId);
  }, [route, currentUser, isAuthLoading]);

  // Load the workbook whenever the URL contains a workbook id
  // (covers fresh page loads on /workbook/<wbId>/<flowId> and explorer clicks).
  useEffect(function() {
    if (!currentUser) return;
    if (!selectedWorkbookId) {
      if (route === 'workflow') {
        // Standalone workflow route — clear any prior workbook context.
        setCurrentWorkbook(null);
        setIsWorkbookPanelOpen(false);
      }
      return;
    }
    setIsWorkbookPanelOpen(true);
    if (!currentWorkbook || currentWorkbook.id !== selectedWorkbookId) {
      loadCurrentWorkbookFromId(selectedWorkbookId);
    }
  }, [currentUser, selectedWorkbookId, route]);

  // Fallback: if the user lands on /workflow/<id> for a flow that actually
  // belongs to a workbook, redirect them to the nested URL so the workbook
  // context (and the file explorer panel) is preserved.
  useEffect(function() {
    if (!currentUser || route !== 'workflow' || !selectedWorkflowId) return;
    if (selectedWorkbookId) return; // already nested — nothing to do
    if (!Array.isArray(workflows) || !workflows.length) return;
    var summary = workflows.find(function(w) { return w && String(w.id) === String(selectedWorkflowId); });
    if (!summary) return;
    var wbId = summary.workbookId ? String(summary.workbookId) : '';
    if (!wbId) return; // truly standalone — leave the URL alone
    navigateTo(buildWorkbookFlowPath(wbId, selectedWorkflowId));
  }, [currentUser, route, selectedWorkflowId, selectedWorkbookId, workflows]);

  // Ensure we have the workflow list for the editor (so we can know its workbookId)
  useEffect(function() {
    if (!currentUser || route !== 'workflow') return;
    if (workflows && workflows.length) return;
    listWorkflows().then(function(items) {
      setWorkflows(Array.isArray(items) ? items : []);
    }).catch(function() {});
  }, [currentUser, route, selectedWorkflowId]);

  useEffect(function() {
    function onPopState() {
      setRoute(getRouteFromLocation());
      setSelectedWorkflowId(getWorkflowIdFromLocation());
      setSelectedWorkbookId(getWorkbookIdFromLocation());
      setStoreTemplateId(getStoreIdFromLocation());
      setActiveLandingSection(getLandingSectionFromLocation());
      setUiPageRoute({
        pageId: getUiPageIdFromLocation(),
        slug: getUiPageSlugFromLocation()
      });
    }
    window.addEventListener('popstate', onPopState);
    return function() {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  useEffect(function() {
    if (typeof window === 'undefined') return;
    if (route !== 'home') return;

    var legacySection = legacyMarketingPathMap[window.location.pathname || ''];
    if (legacySection) {
      var targetHash = '#' + legacySection;
      window.history.replaceState({}, '', '/' + targetHash);
      setActiveLandingSection(legacySection);
      window.requestAnimationFrame(function() {
        scrollToLandingSection(legacySection);
      });
      return;
    }

    var initialSection = getLandingSectionFromLocation();
    if (initialSection) {
      window.requestAnimationFrame(function() {
        scrollToLandingSection(initialSection);
      });
    }
  }, [route]);

  useEffect(function() {
    if (typeof window === 'undefined') return;
    if (route !== 'home') return;

    var observer = new IntersectionObserver(function(entries) {
      var visibleEntry = null;
      for (var i = 0; i < entries.length; i += 1) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;
        if (!visibleEntry || entry.intersectionRatio > visibleEntry.intersectionRatio) {
          visibleEntry = entry;
        }
      }
      if (!visibleEntry || !visibleEntry.target || !visibleEntry.target.id) return;
      setActiveLandingSection(String(visibleEntry.target.id));
    }, {
      root: null,
      rootMargin: '-20% 0px -55% 0px',
      threshold: [0.2, 0.35, 0.5]
    });

    navItems.forEach(function(item) {
      var node = document.getElementById(item.sectionId);
      if (node) observer.observe(node);
    });

    return function() {
      observer.disconnect();
    };
  }, [route]);

  useEffect(function() {
    if (!(isEmbed && route === 'workflow' && selectedWorkflowId) && (!currentUser || route !== 'workflow' || !selectedWorkflowId)) {
      if (publicListenState.enabled) {
        stopPublicListenerHeartbeat();
        releasePublicListenerLease(selectedWorkflowId);
      }
      setWorkflowState(createEmptyWorkflowState());
      setCustomObjectTemplates([]);
      setIsWorkflowHydrated(false);
      setExecutionState(createExecutionState());
      setPublicListenState(createPublicListenState());
      publicListenerAutoResumeRef.current = '';
      closeExecutionStream();
      closePublicExecutionStream();
      return;
    }

    // Remote Support agent session: the canvas state arrives from the owner's
    // client over the collab socket — never from the draft cache or the
    // server (the server would 404 this foreign workflowId anyway). The
    // agent-session effect owns hydration.
    if (remoteSession) {
      setWorkflowState(createEmptyWorkflowState());
      setIsWorkflowHydrated(false);
      setExecutionState(createExecutionState());
      return;
    }

    var mounted = true;
    setDraftConflict(null);
    setDraftConflictBusy(false);

    // True when the server has a save newer than the version this draft was
    // forked from. No baseline (legacy draft) → assume no conflict.
    function isServerNewer(baseUpdatedAt, serverUpdatedAt) {
      if (!baseUpdatedAt || !serverUpdatedAt) return false;
      var base = new Date(baseUpdatedAt).getTime();
      var server = new Date(serverUpdatedAt).getTime();
      return isFinite(base) && isFinite(server) && server > base;
    }

    // Flow drafts live in IndexedDB with a synchronous in-memory mirror; wait
    // for the mirror to finish loading before deciding cache-vs-server, so a
    // fast reload can't miss a dirty draft and clobber it with the server
    // copy. Resolves instantly on every open after the first.
    localFlowCache.whenReady().then(function() {
      if (mounted) hydrateFlow();
    });

    function hydrateFlow() {
      var cached = localFlowCache.readFlowCache(selectedWorkflowId);

      if (cached && cached.state) {
        // Instant open: hydrate from the local draft cache with no spinner. We
        // mark lastHydratedStateRef before calling setWorkflowState so the
        // dirty effect can tell this is a hydration (not a user edit).
        var normalizedCached = normalizeWorkflowState(cached.state);
        lastHydratedStateRef.current = normalizedCached;
        setWorkflowState(normalizedCached);
        seedExecutionStateFromNodeLastOutputs(normalizedCached);
        setFatalError('');
        setIsWorkflowHydrated(true);
        // Custom objects + flow-ref refresh happen in the background — they
        // don't block the canvas from rendering.
        loadCustomObjects()
          .then(function(objs) {
            if (!mounted) return;
            setCustomObjectTemplates(Array.isArray(objs) ? objs : []);
          })
          .catch(function() {});
        // If the cache is clean (last save matches server), opportunistically
        // refresh from the server in the background. If the user has already
        // started editing by the time the fetch returns, we leave their draft
        // alone.
        if (!cached.dirty) {
          loadWorkflowWithMetaById(selectedWorkflowId)
            .then(function(meta) {
              if (!mounted || !meta || !meta.state) return;
              var normalizedServer = normalizeWorkflowState(meta.state);
              var latest = localFlowCache.readFlowCache(selectedWorkflowId);
              if (latest && latest.dirty) return; // user is editing, don't clobber
              localFlowCache.writeFlowCache(selectedWorkflowId, normalizedServer, { dirty: false, baseUpdatedAt: meta.updatedAt });
              lastHydratedStateRef.current = normalizedServer;
              setWorkflowState(normalizedServer);
              seedExecutionStateFromNodeLastOutputs(normalizedServer);
            })
            .catch(function() {});
        } else {
          // Dirty draft: the canvas keeps showing the user's edits, but check in
          // the background whether someone else saved a newer version since this
          // draft's baseline — if so, surface the conflict banner instead of
          // silently letting the next save overwrite their work.
          loadWorkflowWithMetaById(selectedWorkflowId)
            .then(function(meta) {
              if (!mounted || !meta) return;
              if (isServerNewer(cached.baseUpdatedAt, meta.updatedAt)) {
                setDraftConflict({ workflowId: selectedWorkflowId, serverUpdatedAt: meta.updatedAt });
              }
            })
            .catch(function() {});
        }
        return;
      }

      // No cache → fall back to the original load path with the spinner.
      setIsWorkflowHydrated(false);
      var serverUpdatedAt = null;
      Promise.all([
        loadWorkflowWithMetaById(selectedWorkflowId),
        loadCustomObjects()
      ])
        .then(function(results) {
          if (!mounted) return null;
          serverUpdatedAt = results[0] ? results[0].updatedAt : null;
          var normalized = normalizeWorkflowState(results[0] ? results[0].state : null);
          setCustomObjectTemplates(Array.isArray(results[1]) ? results[1] : []);
          setFatalError('');
          // Refresh any flow-ref nodes from their referenced .flw files so the
          // node's title + start hyperparameters reflect the latest source.
          // This runs before we expose the workflow on the canvas so the user
          // never sees a stale snapshot flash.
          return refreshFlowRefNodesFromSource(normalized);
        })
        .then(function(refreshed) {
          if (!mounted || !refreshed) return;
          // Cache as clean so the next open is instant.
          localFlowCache.writeFlowCache(selectedWorkflowId, refreshed, { dirty: false, baseUpdatedAt: serverUpdatedAt });
          lastHydratedStateRef.current = refreshed;
          setWorkflowState(refreshed);
          seedExecutionStateFromNodeLastOutputs(refreshed);
        })
        .catch(function(error) {
          if (!mounted) return;
          setFatalError(error && error.message ? error.message : 'Could not load workflow.');
        })
        .finally(function() {
          if (mounted) setIsWorkflowHydrated(true);
        });
    }

    return function() {
      mounted = false;
    };
  }, [currentUser, route, selectedWorkflowId, remoteSession]);

  // Fetch the current live (production) version for editor metadata and for the
  // read-only embed canvas.
  function refreshLiveVersionInfo() {
    if (!selectedWorkflowId) {
      setLiveVersionInfo(null);
      return Promise.resolve(null);
    }
    return getLiveVersion(selectedWorkflowId)
      .then(function(info) {
        setLiveVersionInfo(info || null);
        return info || null;
      })
      .catch(function() {
        // Best-effort: a failed lookup just hides the badge.
        setLiveVersionInfo(null);
        return null;
      });
  }

  useEffect(function() {
    // Editor workflow changes return to Staging; embeds always load Live.
    setWorkflowMode(isEmbed ? 'live' : 'staging');
    setLiveSnapshot(null);
    setLiveDirty(false);
    setLiveSaving(false);
    if ((!isEmbed && !currentUser) || route !== 'workflow' || !selectedWorkflowId) {
      setLiveVersionInfo(null);
      return;
    }
    refreshLiveVersionInfo();
    if (isEmbed) loadLiveSnapshot();
  }, [currentUser, route, selectedWorkflowId, isEmbed]);

  useEffect(function() {
    var workflowIdAtEffectStart = selectedWorkflowId;
    return function() {
      closeExecutionStream();
      stopPublicListenerHeartbeat();
      if (publicListenEnabledRef.current) {
        logPublicListenDebug('listener.cleanup', {
          workflowId: workflowIdAtEffectStart || selectedWorkflowIdRef.current || '',
          enabled: publicListenEnabledRef.current
        });
        releasePublicListenerLease(workflowIdAtEffectStart || selectedWorkflowIdRef.current);
      }
      closePublicExecutionStream();
    };
  }, [selectedWorkflowId]);

  useEffect(function() {
    if (!selectedWorkflowId) return;
    if (publicListenState.enabled && publicListenState.code) {
      writePersistedPublicListenConfig(selectedWorkflowId, {
        enabled: true,
        code: publicListenState.code
      });
      return;
    }
    clearPersistedPublicListenConfig(selectedWorkflowId);
  }, [selectedWorkflowId, publicListenState.enabled, publicListenState.code]);

  useEffect(function() {
    if (!currentUser || route !== 'workflow' || !selectedWorkflowId) return;
    if (publicListenState.enabled) return;
    var persisted = readPersistedPublicListenConfig(selectedWorkflowId);
    if (!persisted || !persisted.code) return;
    var resumeKey = selectedWorkflowId + ':' + persisted.code;
    if (publicListenerAutoResumeRef.current === resumeKey) return;
    publicListenerAutoResumeRef.current = resumeKey;
    startPublicEventStreamListening(persisted.code).catch(function() {});
  }, [currentUser, route, selectedWorkflowId, publicListenState.enabled]);

  useEffect(function() {
    workflowStateRef.current = workflowState;
    checkSilentErrorRef.current = !!(workflowState && workflowState.checkSilentError);
  }, [workflowState]);

  useEffect(function() {
    workflowModeRef.current = workflowMode;
  }, [workflowMode]);

  // Keep executionStateRef in sync so runWorkflowAndAwait (Doctor tool) can
  // poll the live status from outside React.
  useEffect(function() {
    executionStateRef.current = executionState;
  }, [executionState]);

  // ── Autopilot driver (Selenium-style) ──────────────────────────────────────
  // Each action is the same code path the corresponding UI button calls. The
  // driver runs as an explicit script with wait-for-condition gates between
  // steps so the user can watch each phase via the canvas status pill.
  function waitForCondition(check, timeoutMs) {
    return new Promise(function(resolve) {
      var start = Date.now();
      function tick() {
        if (check()) return resolve(true);
        if (Date.now() - start >= timeoutMs) return resolve(false);
        setTimeout(tick, 50);
      }
      tick();
    });
  }

  function getNodeCodeFromState(nodeId) {
    var state = workflowStateRef.current;
    if (!state || !Array.isArray(state.nodes)) return '';
    var node = state.nodes.find(function(n) { return n && n.id === nodeId; });
    return node ? String(node.code || '') : '';
  }

  async function runAutoFixSequence(nodeId, expectedCode, snapshot) {
    setAutopilotStatus('Applying fix…');

    // Edge-only fix: there's no new code to wait for — propPath updates land
    // on workflowState.connections via onSilentSave the same render tick. Skip
    // straight to close-editor + rerun.
    if (expectedCode) {
      var applied = await waitForCondition(function() {
        return getNodeCodeFromState(nodeId) === String(expectedCode);
      }, AUTOFIX_STEP_TIMEOUT_MS);

      if (!applied) {
        setAutopilotStatus('Fix did not apply — closing');
        handleCloseEditor();
        setTimeout(function() { setAutopilotStatus(null); }, 1500);
        return;
      }
    }

    setAutopilotStatus('Closing editor…');
    handleCloseEditor();
    await waitForCondition(function() { return !editorOpenRef.current; }, 1000);

    setAutopilotStatus('Re-running node…');
    handleRerunFromNode(nodeId, snapshot);
    setTimeout(function() { setAutopilotStatus(null); }, 1200);
  }

  // Keep isSavedRef and isWorkflowHydratedRef in sync for use inside intervals/closures
  useEffect(function() { isSavedRef.current = isSaved; }, [isSaved]);
  useEffect(function() { isWorkflowHydratedRef.current = isWorkflowHydrated; }, [isWorkflowHydrated]);
  useEffect(function() { editorOpenRef.current = editorOpen; }, [editorOpen]);
  useEffect(function() { publicListenEnabledRef.current = publicListenState.enabled; }, [publicListenState.enabled]);
  useEffect(function() { selectedWorkflowIdRef.current = selectedWorkflowId; }, [selectedWorkflowId]);

  // Temporary variables are runtime-only: a Temporary variable fed by a Set
  // Variable must never persist its value. Scrub it from the in-memory state so
  // EVERY save / cache write stays clean (the live runtime value still shows in
  // the panel via the preview). Idempotent — once values are empty it's a no-op,
  // so it never loops or marks a clean flow dirty.
  useEffect(function() {
    if (!isWorkflowHydrated) return;
    setWorkflowState(function(prev) {
      if (!prev || !Array.isArray(prev.nodes)) return prev;
      var nextNodes = clearFedTemporaryVariableValues(prev.nodes, prev.connections);
      return nextNodes === prev.nodes ? prev : Object.assign({}, prev, { nodes: nextNodes });
    });
  }, [workflowState.nodes, workflowState.connections, isWorkflowHydrated]);

  // Mark unsaved + write to localStorage whenever workflowState changes
  // (after hydration). We compare against lastHydratedStateRef so that
  // the initial hydration from cache/server doesn't itself mark the flow
  // dirty — only subsequent user edits do.
  useEffect(function() {
    if (!isWorkflowHydrated) return;
    if (workflowState === lastHydratedStateRef.current) return;
    // Support agents in a remote session never persist drafts — their canvas
    // is a live mirror of the owner's, discarded when the session ends.
    if (remoteSession) return;
    setIsSaved(false);
    if (selectedWorkflowId) {
      localFlowCache.writeFlowCache(selectedWorkflowId, workflowState, { dirty: true });
      dirtyFlowIdsRef.current[selectedWorkflowId] = true;
    }
  }, [workflowState]);

  // Reset undo/redo history whenever the active workflow changes so the stack
  // never mixes states across different flows.
  useEffect(function() {
    if (historyScopeIdRef.current === selectedWorkflowId) return;
    historyScopeIdRef.current = selectedWorkflowId || '';
    if (historyDebounceRef.current) {
      clearTimeout(historyDebounceRef.current);
      historyDebounceRef.current = null;
    }
    historyRef.current = [];
    historyPosRef.current = 0;
    setHistoryEntries([]);
    setHistoryPos(0);
    prevStateForHistoryRef.current = null;
  }, [selectedWorkflowId]);

  // Debounced history push on workflowState change
  useEffect(function() {
    if (!isWorkflowHydrated) return;
    if (isHistoryNavRef.current) return;
    if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
    var snapshot = workflowState;
    var prev = prevStateForHistoryRef.current;
    var scopeId = selectedWorkflowId || '';
    historyDebounceRef.current = setTimeout(function() {
      // Drop the push if the active workflow changed during the debounce.
      if (historyScopeIdRef.current !== scopeId) return;
      var label = describeChange(prev, snapshot);
      var entry = { id: Date.now() + '-' + Math.random(), label: label, state: snapshot, time: Date.now() };
      var pos = historyPosRef.current;
      var stack = pos > 0 ? historyRef.current.slice(pos) : historyRef.current;
      var newStack = [entry].concat(stack).slice(0, 50);
      historyRef.current = newStack;
      historyPosRef.current = 0;
      setHistoryEntries(newStack);
      setHistoryPos(0);
      prevStateForHistoryRef.current = snapshot;
    }, 600);
  }, [workflowState]);

  // ---- Remote Support: live session sync ----

  // Apply ops that arrived from the collab socket. applyingRemoteRef makes
  // the broadcast effect below skip that commit so ops never echo back.
  function applyRemoteCollabOps(ops) {
    if (!Array.isArray(ops) || !ops.length) return;
    applyingRemoteRef.current = true;
    setWorkflowState(function(prev) { return applyOps(prev, ops); });
  }

  function collabHandleRoster(msg) {
    var members = Array.isArray(msg.members) ? msg.members : [];
    setCollabRoster(members);
    // Drop cursors/selections/activity of members who left.
    var keep = {};
    members.forEach(function(m) { keep[m.sessionId] = true; });
    var cursors = collabCursorsRef.current;
    Object.keys(cursors).forEach(function(sid) { if (!keep[sid]) delete cursors[sid]; });
    var pointers = remotePointersRef.current;
    Object.keys(pointers).forEach(function(sid) {
      if (!keep[sid]) {
        try { pointers[sid].destroy(); } catch (_) {}
        delete pointers[sid];
      }
    });
    setCollabSelections(function(prev) {
      var next = {};
      Object.keys(prev).forEach(function(sid) { if (keep[sid]) next[sid] = prev[sid]; });
      return next;
    });
    setCollabPeerUi(function(prev) {
      var next = {};
      Object.keys(prev).forEach(function(key) {
        if (keep[key.split(':')[0]]) next[key] = prev[key];
      });
      return next;
    });
  }

  function collabHandleCursor(msg) {
    collabCursorsRef.current[msg.sessionId] = {
      x: msg.x, y: msg.y, username: msg.username, color: msg.color
    };
  }

  // Events from other session members: mirrored execution, selection
  // presence, open-popup presence, and remote run/stop triggers.
  function collabHandlePeerEvent(msg) {
    if (!msg || !msg.type) return;
    var payload = msg.payload || {};

    // A run (started by ANY member) mirrors to everyone: same statuses,
    // badges, outputs and run log, driven by the runner's event stream.
    if (msg.type === 'exec_event') {
      applyingRemoteExecRef.current = true;
      try {
        handleExecutionEvent(payload.event, payload.executionId, { closeOnTerminal: false });
      } finally {
        applyingRemoteExecRef.current = false;
      }
      return;
    }

    if (msg.type === 'selection') {
      setCollabSelections(function(prev) {
        var next = Object.assign({}, prev);
        if (payload.nodeId) {
          next[msg.sessionId] = { nodeId: payload.nodeId, color: msg.color, username: msg.username };
        } else {
          delete next[msg.sessionId];
        }
        return next;
      });
      return;
    }

    if (msg.type === 'ui_state') {
      // "Agent pressed Play/Stop" — the action runs on THIS (owner) client:
      // owner's context, owner's connections. Execution then mirrors back to
      // the whole room via exec_event.
      if (payload.popup === 'run_request') {
        if (!remoteSessionRef.current) {
          if (payload.action === 'stop') handleStopWorkflow();
          else handleRunWorkflow();
        }
        return;
      }
      setCollabPeerUi(function(prev) {
        var next = Object.assign({}, prev);
        var key = msg.sessionId + ':' + (payload.popup || '');
        if (payload.open) {
          next[key] = {
            popup: payload.popup,
            nodeTitle: payload.nodeTitle || '',
            username: msg.username,
            color: msg.color
          };
        } else {
          delete next[key];
        }
        return next;
      });
    }
  }

  // Share my selected node with the session (colored ring on their canvas).
  // Re-broadcast when the roster changes so late joiners see it too.
  useEffect(function() {
    if (!collabActiveRef.current || !collabClientRef.current) return;
    collabClientRef.current.sendPeerEvent('selection', { nodeId: selectedNodeId || null });
  }, [selectedNodeId, collabRoster.length]);

  // Share "step editor open on node X" (AskCode) and Doctor state so the
  // other side sees what's being worked on.
  useEffect(function() {
    if (!collabActiveRef.current || !collabClientRef.current) return;
    var nodeTitle = '';
    var nodeId = editingNodeIdRef.current;
    var nodes = workflowStateRef.current && workflowStateRef.current.nodes;
    if (nodeId && Array.isArray(nodes)) {
      var editingNode = nodes.find(function(n) { return n && String(n.id) === String(nodeId); });
      if (editingNode) nodeTitle = editingNode.title || '';
    }
    collabClientRef.current.sendPeerEvent('ui_state', { popup: 'stepEditor', open: !!editorOpen, nodeTitle: nodeTitle });
  }, [editorOpen, collabRoster.length]);

  useEffect(function() {
    if (!collabActiveRef.current || !collabClientRef.current) return;
    collabClientRef.current.sendPeerEvent('ui_state', { popup: 'doctor', open: !!doctorChatOpen });
  }, [doctorChatOpen, collabRoster.length]);

  // ---- TeamViewer mode: screen mirror + virtual pointer ----

  // OWNER: input from an agent's virtual pointer — replay it on this
  // document (clicks, drags, scrolling, typing act as the owner's own).
  function collabHandlePointer(msg) {
    if (!msg || !msg.sessionId) return;
    var controllers = remotePointersRef.current;
    if (!controllers[msg.sessionId]) {
      controllers[msg.sessionId] = createRemotePointer({ username: msg.username, color: msg.color });
    }
    controllers[msg.sessionId].handle(msg.payload || {});
  }

  function destroyRemotePointers() {
    var controllers = remotePointersRef.current;
    Object.keys(controllers).forEach(function(sid) {
      try { controllers[sid].destroy(); } catch (_) {}
    });
    remotePointersRef.current = {};
  }

  function stopMirrorRecorder() {
    if (mirrorRecorderRef.current) {
      try { mirrorRecorderRef.current(); } catch (_) {}
      mirrorRecorderRef.current = null;
    }
    if (mirrorPingTimerRef.current) {
      clearInterval(mirrorPingTimerRef.current);
      mirrorPingTimerRef.current = null;
    }
  }

  // OWNER: mirror this screen (rrweb) while at least one agent is in the
  // session. A newly joined agent triggers a fresh full snapshot so their
  // viewer bootstraps instantly.
  useEffect(function() {
    if (remoteSession) return; // agents never record
    var agentCount = collabRoster.filter(function(m) { return m && m.role === 'agent'; }).length;
    if (agentCount > 0 && !mirrorRecorderRef.current && collabClientRef.current) {
      mirrorRecorderRef.current = rrweb.record({
        emit: function(event) {
          if (collabClientRef.current) collabClientRef.current.sendMirrorEvent(event);
        },
        // Tuned for responsiveness over bandwidth: 'all' input echoes each
        // keystroke instead of only the settled value, and the movement
        // samples stay near frame rate.
        sampling: { mousemove: 20, scroll: 30, input: 'all' }
      }) || null;
      // Keep-alive through the recorder: agents detect lost frames by
      // silence (watchdog → mirror_resync), so an idle-but-healthy screen
      // must still tick. Custom events are tiny and change no DOM.
      if (mirrorRecorderRef.current && !mirrorPingTimerRef.current) {
        mirrorPingTimerRef.current = setInterval(function() {
          try { rrweb.record.addCustomEvent('mirror_ping', {}); } catch (_) {}
        }, 3000);
      }
    } else if (agentCount > 0 && mirrorRecorderRef.current) {
      try { rrweb.record.takeFullSnapshot(true); } catch (_) {}
    } else if (agentCount === 0) {
      stopMirrorRecorder();
      destroyRemotePointers();
    }
  }, [collabRoster, remoteSession]);

  // AGENT: forward my viewer interactions to the owner's client.
  var handleAgentPointer = useCallback(function(payload) {
    if (collabClientRef.current) collabClientRef.current.sendPointer(payload);
  }, []);

  function createMirrorBus() {
    var buffer = [];
    var listener = null;
    return {
      push: function(event) {
        if (listener) listener(event);
        else buffer.push(event);
      },
      subscribe: function(fn) {
        listener = fn;
        var pending = buffer;
        buffer = [];
        pending.forEach(fn);
        return function() { if (listener === fn) listener = null; };
      }
    };
  }

  // Broadcast: after every committed canvas change, diff against the last
  // state the session saw and send only the delta. One seam catches every
  // edit path (drag, params, hooks, copilot) with no per-call-site changes.
  useEffect(function() {
    var prev = collabSyncedStateRef.current;
    collabSyncedStateRef.current = workflowState;
    if (!collabActiveRef.current || !collabClientRef.current) return;
    if (applyingRemoteRef.current) { applyingRemoteRef.current = false; return; }
    if (!prev || prev === workflowState) return;
    var ops = diffWorkflowStates(prev, workflowState);
    if (ops.length) collabClientRef.current.sendOps(ops);
  }, [workflowState]);

  // OWNER side: having the canvas open IS the live session. Support agents
  // can only join while this socket is up; closing the workflow (or tab)
  // destroys the room server-side and disconnects them.
  useEffect(function() {
    if (route !== 'workflow' || isEmbed || !currentUser || !selectedWorkflowId) return;
    if (remoteSession) return; // agent mode has its own effect below
    if (!isWorkflowHydrated) return;
    var client = createCollabClient({
      onJoined: function(msg) {
        collabActiveRef.current = true;
        collabSyncedStateRef.current = workflowStateRef.current;
        setCollabSelfSessionId(msg.sessionId || '');
      },
      onStatus: function(status) {
        if (status !== 'connected') collabActiveRef.current = false;
      },
      // An agent just joined — hand them the live draft (incl. unsaved edits).
      onStateRequest: function(msg) {
        var state = workflowStateRef.current;
        client.sendState(msg.forSession, state, state && state.workflowName);
      },
      onOps: function(msg) { applyRemoteCollabOps(msg.ops); },
      onRoster: collabHandleRoster,
      onCursor: collabHandleCursor,
      onPeerEvent: collabHandlePeerEvent,
      onPointer: collabHandlePointer,
      // An agent's mirror went stale (lost frames) — a fresh full snapshot
      // rebases their replayer on the current DOM.
      onMirrorResync: function() {
        if (mirrorRecorderRef.current) {
          try { rrweb.record.takeFullSnapshot(true); } catch (_) {}
        }
      }
    });
    collabClientRef.current = client;
    client.connect({
      role: 'owner',
      workflowId: selectedWorkflowId,
      workflowName: (workflowStateRef.current && workflowStateRef.current.workflowName) || 'Untitled'
    });
    return function() {
      collabActiveRef.current = false;
      if (collabClientRef.current === client) collabClientRef.current = null;
      client.disconnect();
      setCollabRoster([]);
      setCollabSelfSessionId('');
      collabCursorsRef.current = {};
      setCollabSelections({});
      setCollabPeerUi({});
      stopMirrorRecorder();
      destroyRemotePointers();
      mirrorBusRef.current = null;
    };
  }, [route, isEmbed, currentUser && currentUser.id, selectedWorkflowId, isWorkflowHydrated, remoteSession]);

  // AGENT side: joined from the dashboard's live-sessions list. The canvas
  // state bootstraps from the owner's client over the socket; nothing is
  // read from (or written to) the server or the local draft cache.
  useEffect(function() {
    if (!remoteSession || !currentUser) return;
    var client = createCollabClient({
      onJoined: function(msg) {
        collabActiveRef.current = true;
        setCollabSelfSessionId(msg.sessionId || '');
      },
      onStatus: function(status) {
        if (status !== 'connected') collabActiveRef.current = false;
      },
      onState: function(msg) {
        var normalized = normalizeWorkflowState(msg.state);
        lastHydratedStateRef.current = normalized;
        collabSyncedStateRef.current = normalized;
        setWorkflowState(normalized);
        // Same as normal hydration: show the owner's last-run badges/outputs
        // so the agent's canvas isn't bare.
        seedExecutionStateFromNodeLastOutputs(normalized);
        setIsWorkflowHydrated(true);
      },
      onOps: function(msg) { applyRemoteCollabOps(msg.ops); },
      onRoster: collabHandleRoster,
      onCursor: collabHandleCursor,
      onPeerEvent: collabHandlePeerEvent,
      onMirrorEvent: function(msg) {
        if (mirrorBusRef.current) mirrorBusRef.current.push(msg.payload);
      },
      onSessionEnded: function() { endRemoteSupportSession('The owner closed the workflow — support session ended.'); },
      onKicked: function() { endRemoteSupportSession('Your support access to this session was removed.'); },
      onJoinDenied: function() { endRemoteSupportSession('This support session is no longer available.'); }
    });
    collabClientRef.current = client;
    client.connect({
      role: 'agent',
      workflowId: remoteSession.workflowId,
      ownerId: remoteSession.ownerId
    });
    return function() {
      collabActiveRef.current = false;
      if (collabClientRef.current === client) collabClientRef.current = null;
      client.disconnect();
      setCollabRoster([]);
      setCollabSelfSessionId('');
      collabCursorsRef.current = {};
      setCollabSelections({});
      setCollabPeerUi({});
      stopMirrorRecorder();
      destroyRemotePointers();
      mirrorBusRef.current = null;
    };
  }, [remoteSession, currentUser && currentUser.id]);

  // Dashboard "Join" on a live session card.
  function handleJoinSupportSession(session) {
    if (!session || !session.workflowId) return;
    setCollabNotice('');
    // Created BEFORE the viewer renders so its first render already has the
    // bus (a ref change alone would never re-render).
    mirrorBusRef.current = createMirrorBus();
    setRemoteSession({
      ownerId: session.ownerId,
      ownerUsername: session.ownerUsername || '',
      workflowId: session.workflowId
    });
    navigateTo('/workflow/' + encodeURIComponent(String(session.workflowId)));
  }

  function endRemoteSupportSession(notice) {
    setRemoteSession(null);
    if (notice) setCollabNotice(notice);
    navigateTo('/dashboard');
  }

  // Leaving the canvas any other way (header back button, browser history,
  // opening another workflow) also ends an agent's remote session — the
  // effect cleanup above disconnects the socket.
  useEffect(function() {
    if (!remoteSession) return;
    if (route !== 'workflow' || String(selectedWorkflowId) !== String(remoteSession.workflowId)) {
      setRemoteSession(null);
    }
  }, [route, selectedWorkflowId, remoteSession]);

  // Cursor position (world coords) from the canvas — throttled in the client.
  var handleCollabCursorMove = useCallback(function(x, y) {
    if (!collabActiveRef.current || !collabClientRef.current) return;
    collabClientRef.current.sendCursor(x, y);
  }, []);

  // Ctrl+S / Cmd+S to save
  useEffect(function() {
    function onKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSaveWorkflow();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return function() { window.removeEventListener('keydown', onKeyDown); };
  }, [selectedWorkflowId]);

  // Warn before closing/refreshing tab when there are unsaved changes.
  // Only while on the canvas (workflow route) — other pages like the
  // dashboard should never trigger the browser reload warning.
  useEffect(function() {
    if (isSaved || route !== 'workflow') return;
    function onBeforeUnload(e) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return function() { window.removeEventListener('beforeunload', onBeforeUnload); };
  }, [isSaved, route]);

  useEffect(function() {
    function onPageHide() {
      if (!publicListenEnabledRef.current) return;
      releasePublicListenerLeaseImmediately(selectedWorkflowIdRef.current);
    }
    window.addEventListener('pagehide', onPageHide);
    return function() {
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);

  // Auto-save to backend has been removed in favor of the local-first model:
  // edits are persisted to localStorage on every change, and the user flushes
  // everything to the backend by clicking Save (or pressing Ctrl+S).

  function navigateTo(path) {
    // Strip hash AND query so route matching works for paths like /login?plan=free.
    var cleanPath = (path.split('#')[0] || path).split('?')[0];
    var hash = path.indexOf('#') !== -1 ? path.slice(path.indexOf('#') + 1) : '';
    if (window.location.pathname + window.location.hash !== path) {
      window.history.pushState({}, '', path);
    }
    if (cleanPath === '/login') setRoute('login');
    else if (cleanPath === '/extension-auth') setRoute('extension-auth');
    else if (cleanPath === '/reset-password') setRoute('reset-password');
    else if (cleanPath === '/waitlist') { setShowBetaGate(false); setRoute('waitlist'); }
    else if (cleanPath === '/dashboard') {
      setRoute('dashboard');
      setDashboardTab(hash || '');
    }
    else if (cleanPath === '/admin') setRoute('admin');
    else if (cleanPath === '/admin/benchmark') setRoute('admin-benchmark');
    else if (cleanPath === '/benchmark') setRoute('benchmark');
    else if (cleanPath === '/store' || cleanPath.indexOf('/store/') === 0) {
      var storeMatch = cleanPath.match(/^\/store\/([^/]+)/);
      setRoute('store');
      setStoreTemplateId(storeMatch ? decodeURIComponent(storeMatch[1]) : '');
    }
    else if (cleanPath === '/oauth/authorize') setRoute('oauth-consent');
    else if (cleanPath === '/apidocs') setRoute('apidocs');
    else if (cleanPath === '/user-guide') setRoute('user-guide');
    else if (cleanPath === '/privacy') setRoute('privacy');
    else if (cleanPath === '/terms') setRoute('terms');
    else if (cleanPath.indexOf('/workflow/') === 0) {
      setRoute('workflow');
      setSelectedWorkflowId(decodeURIComponent(cleanPath.slice('/workflow/'.length)));
      setSelectedWorkbookId('');
    }
    else if (cleanPath.indexOf('/workbook/') === 0) {
      // /workbook/<wbId>/<flowId>  → nested workflow editor with workbook context
      // /workbook/<wbId>           → redirect to first flow inside the workbook
      var nestedMatch = cleanPath.match(/^\/workbook\/([^/]+)\/([^/]+)/);
      if (nestedMatch) {
        setRoute('workflow');
        setSelectedWorkbookId(decodeURIComponent(nestedMatch[1]));
        setSelectedWorkflowId(decodeURIComponent(nestedMatch[2]));
      } else {
        var simpleMatch = cleanPath.match(/^\/workbook\/([^/]+)/);
        setRoute('workbook-redirect');
        if (simpleMatch) setSelectedWorkbookId(decodeURIComponent(simpleMatch[1]));
      }
    }
    else if (cleanPath.indexOf('/ui-page/') === 0) {
      var pageMatch = cleanPath.match(/^\/ui-page\/([^/]+)/);
      setRoute('ui-page-editor');
      setUiPageRoute({ pageId: pageMatch ? decodeURIComponent(pageMatch[1]) : '', slug: '' });
    }
    else if (cleanPath.indexOf('/p/') === 0) {
      var slugMatch = cleanPath.match(/^\/p\/([^/]+)/);
      setRoute('ui-page-public');
      setUiPageRoute({ pageId: '', slug: slugMatch ? decodeURIComponent(slugMatch[1]) : '' });
    }
    else {
      setRoute('home');
      setActiveLandingSection(getLandingSectionFromLocation());
    }
  }

  // ── UI Page handlers ──────────────────────────────────────────────────────
  // Currently active page in the editor (used by the planner to update an
  // existing page rather than create a new one).
  var [activeUiPageId, setActiveUiPageId] = useState('');
  // The full record of the page the planner is currently editing — set when
  // the editor's "Open Planner" button is used. Lets the modal start in
  // refine-existing mode (skip workflow picker, prime preview, auto-probe).
  var [activeUiPageRecord, setActiveUiPageRecord] = useState(null);
  // Bumped after the planner saves an update so UIPageEditor remounts and
  // re-fetches the page (its `key` depends on this).
  var [uiPageEditorReloadKey, setUiPageEditorReloadKey] = useState(0);
  var [canvasViewMode, setCanvasViewMode] = useState('workflow');

  // Load UI pages whenever the current workbook changes
  useEffect(function() {
    if (!currentWorkbook || !currentWorkbook.id) {
      setWorkbookUiPages([]);
      return;
    }
    uiPageStorage.listUiPages(currentWorkbook.id).then(function(pages) {
      setWorkbookUiPages(Array.isArray(pages) ? pages : []);
    }).catch(function() { setWorkbookUiPages([]); });
  }, [currentWorkbook && currentWorkbook.id]);

  function refreshWorkbookUiPages() {
    if (!currentWorkbook || !currentWorkbook.id) return;
    uiPageStorage.listUiPages(currentWorkbook.id).then(function(pages) {
      setWorkbookUiPages(Array.isArray(pages) ? pages : []);
    }).catch(function() {});
  }

  // Load every UI page the user owns (across workbooks AND standalone) so we
  // can detect "this workflow already has a UI page" regardless of where the
  // workflow is being viewed from. Refreshed by refreshAllUserUiPages() after
  // any planner save / page delete.
  useEffect(function() {
    if (!currentUser) {
      setAllUserUiPages([]);
      return;
    }
    uiPageStorage.listUiPages('').then(function(pages) {
      setAllUserUiPages(Array.isArray(pages) ? pages : []);
    }).catch(function() { setAllUserUiPages([]); });
  }, [currentUser && currentUser.id]);

  function refreshAllUserUiPages() {
    if (!currentUser) return;
    uiPageStorage.listUiPages('').then(function(pages) {
      setAllUserUiPages(Array.isArray(pages) ? pages : []);
    }).catch(function() {});
  }

  function handleSaveUiPageFromPlanner(uiPage) {
    // uiPage = { tree, bindings, workflowRefs }
    var name = (uiPage && uiPage.tree && uiPage.tree.name) || 'New UI Page';
    var workbookId = (currentWorkbook && currentWorkbook.id) || '';
    var payload = {
      name: name,
      workbookId: workbookId,
      folderId: '',
      value: uiPage
    };

    // If the planner was opened on top of an existing page editor, update it.
    if (activeUiPageId) {
      return uiPageStorage.updateUiPage(activeUiPageId, { value: uiPage }).then(function(updated) {
        setUiPlannerOpen(false);
        // Keep the live record in sync so a re-open shows the new HTML, and
        // bump the editor key so UIPageEditor reloads the page from the API.
        if (updated) setActiveUiPageRecord(updated);
        setUiPageEditorReloadKey(function(k) { return k + 1; });
        refreshWorkbookUiPages();
        refreshAllUserUiPages();
      });
    }

    return uiPageStorage.createUiPage(payload).then(function(created) {
      setUiPlannerOpen(false);
      refreshWorkbookUiPages();
      refreshAllUserUiPages();
      if (created && created.id) {
        navigateTo('/ui-page/' + encodeURIComponent(created.id));
      }
    });
  }

  function handleOpenUiPlanner() {
    var hasWorkbookFlows = !!(currentWorkbook && Array.isArray(currentWorkbook.workflows) && currentWorkbook.workflows.length);
    var hasStandaloneFlow = !currentWorkbook && !!selectedWorkflowId;
    if (!hasWorkbookFlows && !hasStandaloneFlow) {
      setRuntimePopup({
        title: 'No workflows yet',
        message: 'Add at least one workflow to this playbook before designing a UI page.'
      });
      return;
    }
    setActiveUiPageId('');
    setActiveUiPageRecord(null);
    setUiPlannerOpen(true);
  }

  function handleOpenUiPlannerForExisting(page) {
    setActiveUiPageId((page && page.id) || '');
    setActiveUiPageRecord(page || null);
    setUiPlannerOpen(true);
  }

  function handleUiPageDeleted() {
    refreshWorkbookUiPages();
    refreshAllUserUiPages();
    if (currentWorkbook && currentWorkbook.id) {
      navigateTo('/workbook/' + encodeURIComponent(currentWorkbook.id));
    } else {
      navigateTo('/dashboard');
    }
  }

  function scrollToLandingSection(sectionId) {
    if (typeof document === 'undefined') return;
    if (!sectionId) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    var element = document.getElementById(sectionId);
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function handleNavigateLandingSection(sectionId) {
    if (route !== 'home') {
      var nextPath = sectionId ? ('/#' + sectionId) : '/';
      navigateTo(nextPath);
      if (typeof window !== 'undefined') {
        window.requestAnimationFrame(function() {
          scrollToLandingSection(sectionId);
        });
      }
      return;
    }

    var nextUrl = sectionId ? ('/#' + sectionId) : '/';
    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', nextUrl);
    }
    setActiveLandingSection(sectionId || '');
    scrollToLandingSection(sectionId);
  }

  function handleOpenLogin() {
    navigateTo('/login');
  }

  function handleSelectPlan(tierName) {
    if (currentUser) { navigateTo('/dashboard'); return; }
    var planMap = { 'Free Trial': 'free', 'Pro': 'pro', 'Max': 'max', 'Enterprise': 'enterprise' };
    var plan = planMap[tierName] || 'free';
    navigateTo('/login?plan=' + plan);
  }

  function handleSignin(payload) {
    return signin(payload);
  }

  function handleSignup(payload) {
    return signup(Object.assign({}, payload, { language: language }));
  }

  function handleAuthSuccess(user) {
    setCurrentUser(user || null);
    // Beta gate — unapproved users get the "join the waitlist" popup instead of
    // the dashboard. Approved users (and gate-off) proceed as normal.
    if (isUserGated(user)) {
      setShowBetaGate(true);
      return;
    }
    try {
      var authReturnTo = sessionStorage.getItem('alleflow_auth_return_to') || '';
      sessionStorage.removeItem('alleflow_auth_return_to');
      if (authReturnTo.indexOf('/extension-auth') === 0 || authReturnTo.indexOf('/dashboard?extension-auth-code=') === 0 || authReturnTo.indexOf('/oauth/authorize') === 0) {
        navigateTo(authReturnTo);
        return;
      }
    } catch (_) {}
    navigateTo('/dashboard');
  }

  function handleSignout() {
    signout();
    setCurrentUser(null);
    navigateTo('/');
  }

  function handleResetUsage() {
    return resetUsage().then(function() {
      return loadCurrentUser();
    }).then(function(user) {
      setCurrentUser(user || null);
    });
  }

  function handleRefreshCurrentUser() {
    return loadCurrentUser().then(function(user) {
      setCurrentUser(user || null);
      return user || null;
    });
  }

  function handleCreateWorkflow(name) {
    setIsCreatingWorkflow(true);
    return createWorkflow({ name: String(name || '').trim() || 'Untitled Workflow' })
      .then(function(created) {
        if (!created) return null;
        setWorkflows(function(prev) {
          return [created].concat(prev || []);
        });
        return created;
      })
      .finally(function() {
        setIsCreatingWorkflow(false);
      });
  }

  function handleDuplicateWorkflow(workflowId) {
    setIsDuplicatingWorkflow(true);
    return loadWorkflowStateById(workflowId)
      .then(function(state) {
        var workflowName = state && state.workflowName ? String(state.workflowName) : 'Untitled Workflow';
        // Same server contract as import: workflowName + nodes/connections go
        // at the top level of the POST body, not under a nested `state` key.
        return createWorkflow(Object.assign({}, state || {}, {
          workflowName: workflowName + ' Copy'
        }));
      })
      .then(function(created) {
        if (!created) return null;
        setWorkflows(function(prev) {
          return [created].concat(prev || []);
        });
        return created;
      })
      .finally(function() {
        setIsDuplicatingWorkflow(false);
      });
  }

  // Import one or more workflow JSON files from the dashboard: each file
  // becomes its own new workflow. Reuses the same parser as the canvas Import
  // menu item (accepts compressed or full state JSON). A single successful
  // import opens the new workflow; multiple imports stay on the list so the
  // user sees all the new cards. Rejects with a per-file summary if any fail.
  function handleImportWorkflowFiles(files) {
    var list = Array.prototype.slice.call(files || []);
    var imported = [];
    var failures = [];
    return list.reduce(function(chain, file) {
      return chain.then(function() {
        return readTextFile(file)
          .then(parseImportedWorkflowState)
          .then(function(state) {
            // The server reads workflowName + nodes/connections from the TOP
            // level of the POST body (not a nested `state` key), so spread the
            // imported state directly into the payload.
            var name = state && state.workflowName ? String(state.workflowName) : 'Imported Workflow';
            return createWorkflow(Object.assign({}, state || {}, { workflowName: name }));
          })
          .then(function(created) {
            if (created) imported.push(created);
          })
          .catch(function(error) {
            failures.push((file && file.name ? file.name : 'file') + ': ' + (error && error.message ? error.message : 'Could not import.'));
          });
      });
    }, Promise.resolve()).then(function() {
      if (imported.length) {
        setWorkflows(function(prev) {
          return imported.slice().reverse().concat(prev || []);
        });
      }
      if (imported.length === 1 && !failures.length && imported[0].id) {
        handleOpenWorkflow(imported[0].id);
      }
      if (failures.length) {
        var prefix = imported.length ? ('Imported ' + imported.length + ' — failed: ') : '';
        throw new Error(prefix + failures.join(' · '));
      }
      return imported;
    });
  }

  function handleDeleteWorkflow(workflowId) {
    if (!workflowId) return;
    deleteWorkflow(workflowId).then(function() {
      localFlowCache.clearFlowCache(workflowId);
      delete dirtyFlowIdsRef.current[workflowId];
      setWorkflows(function(prev) {
        return (prev || []).filter(function(w) { return w && String(w.id) !== String(workflowId); });
      });
      if (String(selectedWorkflowId || '') === String(workflowId)) {
        setSelectedWorkflowId('');
        navigateTo('/dashboard');
      }
    }).catch(function() {});
  }

  function handleOpenWorkflow(workflowId) {
    if (!workflowId) return;
    navigateTo('/workflow/' + encodeURIComponent(String(workflowId)));
  }

  function handleOpenWorkflowInsideWorkbook(workflowId) {
    if (!workflowId) return;
    if (currentWorkbook && currentWorkbook.id) {
      navigateTo('/workbook/' + encodeURIComponent(String(currentWorkbook.id)) + '/' + encodeURIComponent(String(workflowId)));
      return;
    }
    handleOpenWorkflow(workflowId);
  }

  function handleOpenWorkflowList() {
    navigateTo('/dashboard');
  }

  function handleCreateWorkbook() {
    setIsCreatingWorkbook(true);
    return workbookStorage.createWorkbook({ name: 'Untitled playbook' })
      .then(function(created) {
        if (!created) return null;
        setWorkbooks(function(prev) { return [created].concat(prev || []); });
        navigateTo('/workbook/' + encodeURIComponent(created.id));
        return created;
      })
      .finally(function() { setIsCreatingWorkbook(false); });
  }

  function buildWorkbookFlowPath(wbId, flowId) {
    return '/workbook/' + encodeURIComponent(String(wbId)) + '/' + encodeURIComponent(String(flowId));
  }

  function handleOpenWorkbook(workbookId) {
    if (!workbookId) return;
    setIsWorkbookPanelOpen(true);
    workbookStorage.loadWorkbook(workbookId)
      .then(function(wb) {
        if (!wb) {
          console.warn('[workbook] Workbook not found:', workbookId);
          navigateTo('/dashboard');
          return;
        }
        setCurrentWorkbook(wb);
        var firstFlow = Array.isArray(wb.workflows) && wb.workflows.length ? wb.workflows[0] : null;
        if (firstFlow && firstFlow.id) {
          navigateTo(buildWorkbookFlowPath(wb.id, firstFlow.id));
          return;
        }
        // Empty workbook: try to create a starter flow inside it. If we can't
        // (plan limit, network error, etc.), fall back to the dashboard so the
        // user is never stuck on the loading screen.
        return createWorkflow({ name: 'Untitled Workflow' })
          .then(function(created) {
            if (!created) { navigateTo('/dashboard'); return; }
            return workbookStorage.moveWorkflow(created.id, { workbookId: wb.id, folderId: '' })
              .then(function() {
                setWorkflows(function(prev) {
                  var next = Object.assign({}, created, { workbookId: wb.id, folderId: '' });
                  return [next].concat(prev || []);
                });
                return loadCurrentWorkbookFromId(wb.id);
              })
              .then(function() {
                navigateTo(buildWorkbookFlowPath(wb.id, created.id));
              });
          })
          .catch(function(err) {
            console.error('[workbook] Could not create starter flow:', err && err.message ? err.message : err);
            navigateTo('/dashboard');
          });
      })
      .catch(function(err) {
        console.error('[workbook] Could not load workbook:', err && err.message ? err.message : err);
        navigateTo('/dashboard');
      });
  }

  function reloadCurrentWorkbook() {
    if (!currentWorkbook) return Promise.resolve(null);
    return loadCurrentWorkbookFromId(currentWorkbook.id);
  }

  function handleAddFolder(parentId) {
    if (!currentWorkbook) return;
    // Create with a default name, show it immediately, and put it straight
    // into inline-rename mode so the user can name it in place.
    workbookStorage.addFolder(currentWorkbook.id, { name: 'New folder', parentId: parentId || null })
      .then(function(folder) {
        if (!folder) return;
        setCurrentWorkbook(function(prev) {
          if (!prev) return prev;
          return Object.assign({}, prev, { folders: (prev.folders || []).concat([folder]) });
        });
        setWorkbookRenameRequest({ kind: 'folder', id: folder.id, token: Date.now() });
      })
      .catch(function() {});
  }

  function handleRenameFolder(folderId, name) {
    if (!currentWorkbook) return;
    setWorkbookRenameRequest(null);
    var clean = String(name || '').trim();
    if (!clean) return;
    // Optimistic local update so the rename shows immediately.
    setCurrentWorkbook(function(prev) {
      if (!prev || !Array.isArray(prev.folders)) return prev;
      var nextFolders = prev.folders.map(function(f) {
        if (f && String(f.id) === String(folderId)) {
          return Object.assign({}, f, { name: clean });
        }
        return f;
      });
      return Object.assign({}, prev, { folders: nextFolders });
    });
    queueWorkbookOp({
      type: 'renameFolder',
      workbookId: currentWorkbook.id,
      folderId: folderId,
      name: clean
    });
  }

  function handleRenameCurrentWorkbook(name) {
    if (!currentWorkbook) return;
    var clean = String(name || '').trim();
    if (!clean) return;
    // Optimistic local update so the title changes immediately.
    setCurrentWorkbook(function(prev) {
      return prev ? Object.assign({}, prev, { name: clean }) : prev;
    });
    setWorkbooks(function(prev) {
      return (prev || []).map(function(wb) {
        if (wb && String(wb.id) === String(currentWorkbook.id)) {
          return Object.assign({}, wb, { name: clean });
        }
        return wb;
      });
    });
    queueWorkbookOp({
      type: 'renameWorkbook',
      workbookId: currentWorkbook.id,
      name: clean
    });
  }

  function handleRenameWorkflowInWorkbook(workflowId, name) {
    var clean = String(name || '').trim();
    if (!workflowId || !clean) return;

    // Optimistic local updates so the UI reflects the new name immediately.
    setWorkflows(function(prev) {
      return (prev || []).map(function(w) {
        if (w && String(w.id) === String(workflowId)) {
          return Object.assign({}, w, { name: clean });
        }
        return w;
      });
    });
    setCurrentWorkbook(function(prev) {
      if (!prev || !Array.isArray(prev.workflows)) return prev;
      var nextFlows = prev.workflows.map(function(w) {
        if (w && String(w.id) === String(workflowId)) {
          return Object.assign({}, w, { name: clean });
        }
        return w;
      });
      return Object.assign({}, prev, { workflows: nextFlows });
    });

    // Live-update any flow-ref nodes on the currently open canvas that point
    // at this workflow, so renaming a .flw immediately renames the linked
    // node card without requiring the user to reopen the parent flow.
    setWorkflowState(function(prev) {
      if (!prev || !Array.isArray(prev.nodes)) return prev;
      var didChange = false;
      var nextNodes = prev.nodes.map(function(node) {
        if (!node || node.icon !== 'flow-ref') return node;
        if (String(node.flowRefId || '') !== String(workflowId)) return node;
        didChange = true;
        return Object.assign({}, node, { title: clean, flowRefName: clean });
      });
      if (!didChange) return prev;
      return Object.assign({}, prev, { nodes: nextNodes });
    });

    // If the renamed workflow is currently open, update its in-memory state
    // too so the header reflects the new name. The dirty effect will stash
    // it to the cache; the Save button flushes it.
    if (String(selectedWorkflowId || '') === String(workflowId)) {
      setWorkflowState(function(prev) {
        return Object.assign({}, prev, { workflowName: clean });
      });
      return;
    }

    // Otherwise, mutate the cached copy directly so the rename rides along
    // when the user clicks Save. If we don't have it cached yet, we have to
    // fetch from the server now (rare path).
    var cached = localFlowCache.readFlowCache(workflowId);
    if (cached && cached.state) {
      var nextStateCached = Object.assign({}, cached.state, { workflowName: clean });
      localFlowCache.writeFlowCache(workflowId, nextStateCached, { dirty: true });
      dirtyFlowIdsRef.current[workflowId] = true;
      setIsSaved(false);
      return;
    }
    loadWorkflowStateById(workflowId)
      .then(function(state) {
        var normalized = Object.assign({}, state || {}, { workflowName: clean });
        localFlowCache.writeFlowCache(workflowId, normalized, { dirty: true });
        dirtyFlowIdsRef.current[workflowId] = true;
        setIsSaved(false);
      })
      .catch(function() {});
  }

  function handleDeleteFolder(folderId) {
    if (!currentWorkbook) return;
    // Optimistic: drop the folder subtree locally and move its flows to the
    // root, mirroring what the server does. Resync if the delete fails.
    var removedIds = collectFolderSubtreeIds(currentWorkbook.folders, folderId);
    setCurrentWorkbook(function(prev) {
      if (!prev) return prev;
      var nextFolders = (prev.folders || []).filter(function(f) {
        return f && removedIds.indexOf(String(f.id)) === -1;
      });
      var nextFlows = (prev.workflows || []).map(function(w) {
        if (w && removedIds.indexOf(String(w.folderId || '')) !== -1) {
          return Object.assign({}, w, { folderId: '' });
        }
        return w;
      });
      return Object.assign({}, prev, { folders: nextFolders, workflows: nextFlows });
    });
    workbookStorage.deleteFolder(currentWorkbook.id, folderId)
      .catch(function() { reloadCurrentWorkbook(); });
  }

  function handleMoveFolder(folderId, parentId) {
    if (!currentWorkbook) return;
    var nextParentId = parentId || null;
    // Optimistic local update.
    setCurrentWorkbook(function(prev) {
      if (!prev || !Array.isArray(prev.folders)) return prev;
      var nextFolders = prev.folders.map(function(f) {
        if (f && String(f.id) === String(folderId)) {
          return Object.assign({}, f, { parentId: nextParentId });
        }
        return f;
      });
      return Object.assign({}, prev, { folders: nextFolders });
    });
    queueWorkbookOp({
      type: 'moveFolder',
      workbookId: currentWorkbook.id,
      folderId: folderId,
      parentId: nextParentId
    });
  }

  function handleCreateWorkflowInWorkbook(folderId) {
    if (!currentWorkbook) return;
    if (creatingWorkbookFlowRef.current) return; // ignore double-clicks — one flow per click
    creatingWorkbookFlowRef.current = true;
    setIsCreatingWorkflow(true);
    var wbId = currentWorkbook.id;
    var targetFolderId = folderId || '';
    createWorkflow({ name: 'Untitled Workflow' })
      .then(function(created) {
        if (!created) return null;
        // Show the new flow in the explorer and open it right away; assigning
        // it to the workbook server-side happens in the background.
        setWorkflows(function(prev) {
          var next = Object.assign({}, created, { workbookId: wbId, folderId: targetFolderId });
          return [next].concat(prev || []);
        });
        setCurrentWorkbook(function(prev) {
          if (!prev) return prev;
          var item = {
            id: String(created.id),
            name: String(created.name || 'Untitled Workflow'),
            folderId: targetFolderId,
            updatedAt: created.updatedAt || null,
            createdAt: created.createdAt || null
          };
          return Object.assign({}, prev, { workflows: (prev.workflows || []).concat([item]) });
        });
        navigateTo(buildWorkbookFlowPath(wbId, created.id));
        return workbookStorage.moveWorkflow(created.id, { workbookId: wbId, folderId: targetFolderId })
          .catch(function(err) {
            console.error('[workbook] Assign new flow failed:', err && err.message ? err.message : err);
            return reloadCurrentWorkbook();
          });
      })
      .catch(function(err) {
        var msg = err && err.message ? err.message : 'Could not create flow.';
        console.error('[workbook] Create flow failed:', msg);
      })
      .finally(function() {
        creatingWorkbookFlowRef.current = false;
        setIsCreatingWorkflow(false);
      });
  }

  function handleMoveWorkflowInWorkbook(workflowId, targetFolderId) {
    if (!currentWorkbook) return;
    var nextFolderId = targetFolderId || '';
    // Optimistic local updates.
    setWorkflows(function(prev) {
      return (prev || []).map(function(w) {
        if (w && String(w.id) === String(workflowId)) {
          return Object.assign({}, w, { folderId: nextFolderId });
        }
        return w;
      });
    });
    setCurrentWorkbook(function(prev) {
      if (!prev || !Array.isArray(prev.workflows)) return prev;
      var nextFlows = prev.workflows.map(function(w) {
        if (w && String(w.id) === String(workflowId)) {
          return Object.assign({}, w, { folderId: nextFolderId });
        }
        return w;
      });
      return Object.assign({}, prev, { workflows: nextFlows });
    });
    queueWorkbookOp({
      type: 'moveWorkflow',
      workflowId: workflowId,
      workbookId: currentWorkbook.id,
      folderId: nextFolderId
    });
  }

  function handleDeleteWorkflowInWorkbook(workflowId) {
    function withoutFlow(list) {
      return (list || []).filter(function(w) { return w && String(w.id) !== String(workflowId); });
    }
    var wasSelected = String(selectedWorkflowId || '') === String(workflowId);
    var remaining = currentWorkbook ? withoutFlow(currentWorkbook.workflows) : [];
    // Optimistic: drop the flow from the explorer and lists immediately.
    setWorkflows(withoutFlow);
    setCurrentWorkbook(function(prev) {
      if (!prev || !Array.isArray(prev.workflows)) return prev;
      return Object.assign({}, prev, { workflows: withoutFlow(prev.workflows) });
    });
    // Deleting the open flow: switch to another flow so the canvas never
    // keeps showing a dead workflow.
    if (wasSelected && currentWorkbook) {
      if (remaining.length) navigateTo(buildWorkbookFlowPath(currentWorkbook.id, remaining[0].id));
      else navigateTo('/dashboard');
    }
    deleteWorkflow(workflowId).then(function() {
      localFlowCache.clearFlowCache(workflowId);
      delete dirtyFlowIdsRef.current[workflowId];
    }).catch(function() {
      // Delete failed server-side — resync so the flow reappears.
      reloadCurrentWorkbook();
      listWorkflows().then(function(items) {
        setWorkflows(Array.isArray(items) ? items : []);
      }).catch(function() {});
    });
  }

  function handleDeleteCurrentWorkbook() {
    if (!currentWorkbook || !currentWorkbook.id) return;
    var wbId = currentWorkbook.id;
    workbookStorage.deleteWorkbook(wbId).then(function() {
      // Clear cache for any flows that lived inside this workbook so we don't
      // try to flush stale edits to a deleted workbook later.
      var flowIds = (currentWorkbook.workflows || []).map(function(w) { return w && w.id; }).filter(Boolean);
      flowIds.forEach(function(fid) {
        localFlowCache.clearFlowCache(fid);
        delete dirtyFlowIdsRef.current[fid];
      });
      setWorkbooks(function(prev) {
        return (prev || []).filter(function(wb) { return wb && String(wb.id) !== String(wbId); });
      });
      setCurrentWorkbook(null);
      setIsWorkbookPanelOpen(false);
      setSelectedWorkflowId('');
      navigateTo('/dashboard');
    }).catch(function() {});
  }

  function handleToggleWorkbookPanel() {
    setIsWorkbookPanelOpen(function(prev) { return !prev; });
  }

  function handleWorkflowNameChange(value) {
    setWorkflowState(function(prev) {
      return Object.assign({}, prev, { workflowName: value });
    });
  }

  function handleWorkflowNameBlur() {}

  function handleWorkbookNameChange(value) {
    if (!currentWorkbook) return;
    var next = String(value == null ? '' : value);
    // Optimistic local update so the input stays controlled while typing.
    setCurrentWorkbook(function(prev) {
      return prev ? Object.assign({}, prev, { name: next }) : prev;
    });
    setWorkbooks(function(prev) {
      return (prev || []).map(function(wb) {
        if (wb && String(wb.id) === String(currentWorkbook.id)) {
          return Object.assign({}, wb, { name: next });
        }
        return wb;
      });
    });
  }

  function handleWorkbookNameBlur() {
    if (!currentWorkbook) return;
    var clean = String(currentWorkbook.name || '').trim();
    if (!clean) return;
    queueWorkbookOp({
      type: 'renameWorkbook',
      workbookId: currentWorkbook.id,
      name: clean
    });
  }

  function handleSaveWorkflow() {
    if (isEmbed) return;
    // Support agents never persist: only the owner saves. Covers the button,
    // Ctrl+S, and any programmatic caller.
    if (remoteSessionRef.current) return;
    if (isSaving) return;
    // Don't save before the open flow has hydrated — the in-memory state is
    // still empty/partial, so saving now would persist a blank/stale workflow.
    // Covers the keyboard shortcut and any programmatic caller, not just the
    // (already-disabled) Save button.
    if (selectedWorkflowId && (!isWorkflowHydrated || isCanvasRendering)) return;
    // Don't save mid-run — the canvas state mutates as nodes execute (statuses,
    // outputs), so a save now would persist a transient run-state snapshot.
    // Blocks the keyboard shortcut + programmatic callers, not just the button.
    if (executionState && executionState.status === 'running') return;
    // If the user has unsaved edits, make sure the currently-open flow is in
    // the dirty set with the latest in-memory state — covers the rare case
    // where a click lands between an edit and React's commit phase.
    if (selectedWorkflowId && isWorkflowHydrated && !isSavedRef.current) {
      localFlowCache.writeFlowCache(selectedWorkflowId, workflowStateRef.current, { dirty: true });
      dirtyFlowIdsRef.current[selectedWorkflowId] = true;
    }

    var dirtyIds = Object.keys(dirtyFlowIdsRef.current || {});
    var pendingOps = (pendingWorkbookOpsRef.current || []).slice();
    if (dirtyIds.length === 0 && pendingOps.length === 0) {
      setIsSaved(true);
      return;
    }

    setIsSaving(true);

    var flowSavePromises = dirtyIds.map(function(id) {
      // Prefer the live state if this is the open flow; otherwise fall back
      // to whatever the cache holds.
      var entry = localFlowCache.readFlowCache(id);
      var stateToSave;
      if (String(id) === String(selectedWorkflowId) && workflowStateRef.current) {
        stateToSave = workflowStateRef.current;
      } else {
        stateToSave = entry && entry.state ? entry.state : null;
      }
      if (!stateToSave) {
        delete dirtyFlowIdsRef.current[id];
        localFlowCache.markFlowClean(id);
        return Promise.resolve();
      }
      // Never persist a blank canvas (no nodes) — a save firing mid-run before
      // the live state has rehydrated would otherwise wipe the saved workflow.
      // Skip it silently but leave the flow dirty so the next non-blank save lands.
      if (isBlankWorkflowState(stateToSave)) {
        console.warn('[save] skipped blank workflow', id);
        return Promise.resolve();
      }
      return saveWorkflowStateById(id, stateToSave, entry ? entry.baseUpdatedAt : null)
        .then(function(newUpdatedAt) {
          delete dirtyFlowIdsRef.current[id];
          localFlowCache.markFlowClean(id, newUpdatedAt);
        })
        .catch(function(err) {
          // A 404 means this flow no longer exists server-side (deleted, or a
          // stale draft pointing at a wiped DB). Retrying it forever just spams
          // the console and pins the dirty state, so drop the orphan instead.
          if (err && err.status === 404) {
            delete dirtyFlowIdsRef.current[id];
            localFlowCache.clearFlowCache(id);
            return;
          }
          // 409 = someone else saved a newer version since this draft's
          // baseline. Keep the draft dirty and surface the conflict banner for
          // the open flow (background flows show it when they're next opened).
          if (err && err.status === 409) {
            if (String(id) === String(selectedWorkflowId)) {
              loadWorkflowWithMetaById(id)
                .then(function(meta) {
                  setDraftConflict({ workflowId: id, serverUpdatedAt: meta ? meta.updatedAt : null });
                })
                .catch(function() {
                  setDraftConflict({ workflowId: id, serverUpdatedAt: null });
                });
            }
            return;
          }
          console.error('[save] flow', id, err && err.message ? err.message : err);
        });
    });

    var workbookOpPromise = flushPendingWorkbookOps(pendingOps);

    Promise.all(flowSavePromises.concat([workbookOpPromise]))
      .then(function() {
        var stillDirty = Object.keys(dirtyFlowIdsRef.current || {}).length;
        var stillPending = (pendingWorkbookOpsRef.current || []).length;
        if (stillDirty === 0 && stillPending === 0) setIsSaved(true);
        // Informational for connected support agents (their canvas already
        // converged through the op stream — nothing for them to do).
        if (collabActiveRef.current && collabClientRef.current) {
          collabClientRef.current.sendSaved(new Date().toISOString());
        }
        // Refresh the workbook tree so renames/moves are visible.
        if (currentWorkbook && currentWorkbook.id) {
          reloadCurrentWorkbook();
        }
      })
      .finally(function() { setIsSaving(false); });
  }

  // Apply a restored version to the open canvas. The server has already written
  // it back as the current state (and snapshotted it as a new version), so here
  // we only rehydrate the in-memory canvas + cache and mark it clean.
  function handleRestoreWorkflowVersion(restoredState) {
    if (!restoredState) return;
    var normalized = normalizeWorkflowState(restoredState);
    if (selectedWorkflowId) {
      localFlowCache.writeFlowCache(selectedWorkflowId, normalized, { dirty: false });
      delete dirtyFlowIdsRef.current[selectedWorkflowId];
      // The restore just wrote a new server version — adopt its updatedAt as
      // the draft baseline so the next save isn't flagged as a conflict.
      loadWorkflowWithMetaById(selectedWorkflowId)
        .then(function(meta) {
          if (meta) localFlowCache.setFlowBaseline(selectedWorkflowId, meta.updatedAt);
        })
        .catch(function() {});
    }
    lastHydratedStateRef.current = normalized;
    setWorkflowState(normalized);
    seedExecutionStateFromNodeLastOutputs(normalized);
    setIsSaved(true);
  }

  // Stale-draft conflict resolution ("Load latest"): throw away the local
  // draft and hydrate the canvas from the latest server version.
  function handleConflictLoadLatest() {
    var conflictId = draftConflict && draftConflict.workflowId;
    if (!conflictId || draftConflictBusy) return;
    setDraftConflictBusy(true);
    loadWorkflowWithMetaById(conflictId)
      .then(function(meta) {
        if (!meta || !meta.state) throw new Error('Could not load the latest version.');
        var normalized = normalizeWorkflowState(meta.state);
        localFlowCache.writeFlowCache(conflictId, normalized, { dirty: false, baseUpdatedAt: meta.updatedAt });
        delete dirtyFlowIdsRef.current[conflictId];
        if (String(conflictId) === String(selectedWorkflowId)) {
          lastHydratedStateRef.current = normalized;
          setWorkflowState(normalized);
          seedExecutionStateFromNodeLastOutputs(normalized);
        }
        if (Object.keys(dirtyFlowIdsRef.current || {}).length === 0 && (pendingWorkbookOpsRef.current || []).length === 0) {
          setIsSaved(true);
        }
        setDraftConflict(null);
      })
      .catch(function(err) {
        console.error('[conflict] load latest', err && err.message ? err.message : err);
      })
      .finally(function() {
        setDraftConflictBusy(false);
      });
  }

  // Stale-draft conflict resolution ("Keep my draft"): adopt the newer server
  // version's updatedAt as the draft baseline, so the user's next save is
  // accepted and intentionally replaces the version they chose to override.
  function handleConflictKeepDraft() {
    var conflictId = draftConflict && draftConflict.workflowId;
    if (!conflictId) return;
    if (draftConflict.serverUpdatedAt) {
      localFlowCache.setFlowBaseline(conflictId, draftConflict.serverUpdatedAt);
    } else {
      // We couldn't learn the server's stamp (409 + failed refetch): clear the
      // baseline so the next save is unconditional, matching the user's choice.
      localFlowCache.setFlowBaseline(conflictId, null);
    }
    setDraftConflict(null);
  }

  // Open the publish (Go Live / Update) dialog. Refresh the current live version
  // first so the dialog shows an up-to-date number and pre-fills the next patch.
  function handleOpenGoLive() {
    if (!selectedWorkflowId) return;
    setPublishLiveError('');
    refreshLiveVersionInfo();
    setPublishLiveOpen(true);
  }

  // Load the published live snapshot for read-only viewing. Embeds prefer Live,
  // but fall back to the workflow's staging state when nothing is published yet.
  function loadLiveSnapshot() {
    if (!selectedWorkflowId) {
      setLiveSnapshot(null);
      return Promise.resolve(null);
    }
    setLiveSnapshotLoading(true);
    return loadLiveVersionState(selectedWorkflowId)
      .then(function(state) {
        setLiveSnapshot(state || null);
        return state || null;
      })
      .catch(function(error) {
        setLiveSnapshot(null);
        if (isEmbed && error && error.status === 404) {
          setWorkflowMode('staging');
        }
        return null;
      })
      .finally(function() {
        setLiveSnapshotLoading(false);
      });
  }

  // Switch the Staging / Live view. Entering Live loads the published snapshot
  // (structure read-only, config editable); returning to Staging drops it (the
  // editable canvas state is untouched in memory the whole time).
  function handleChangeWorkflowMode(mode) {
    var next = mode === 'live' ? 'live' : 'staging';
    setWorkflowMode(next);
    setLiveDirty(false);
    if (next === 'live') {
      refreshLiveVersionInfo();
      loadLiveSnapshot();
    } else {
      setLiveSnapshot(null);
    }
  }

  // The canvas's setWorkflowState in Live mode routes here: apply the update to
  // the live snapshot and mark it dirty so the Save button appears. Supports the
  // functional-updater form the canvas uses.
  function handleSetLiveSnapshot(updater) {
    setLiveSnapshot(function(prev) {
      var base = prev || EMPTY_LIVE_STATE;
      var next = typeof updater === 'function' ? updater(base) : updater;
      return next;
    });
    setLiveDirty(true);
  }

  // Persist Live-mode config edits (variable values + hyperparameters) back to
  // the live snapshot — no version bump.
  function handleSaveLive() {
    if (!selectedWorkflowId || liveSaving) return;
    var state = liveSnapshotRef.current;
    if (!state) return;
    setLiveSaving(true);
    saveLiveVersionState(selectedWorkflowId, state)
      .then(function(result) {
        // Server returns { value, version } — value is the merged live snapshot,
        // version is the auto-bumped patch (e.g. 1.0.7 → 1.0.8).
        if (result && result.value) setLiveSnapshot(result.value);
        if (result && result.version) {
          setLiveVersionInfo(function(prev) {
            return Object.assign({}, prev || {}, { version: result.version, publishedAt: new Date().toISOString() });
          });
        }
        setLiveDirty(false);
      })
      .catch(function(err) {
        console.error('[live-save]', err && err.message ? err.message : err);
      })
      .finally(function() {
        setLiveSaving(false);
      });
  }

  // Toggle the live version offline (runs use staging) / back online.
  function handleToggleLiveOffline() {
    if (!selectedWorkflowId || liveOfflineBusy) return;
    var nextOffline = !(liveVersionInfo && liveVersionInfo.offline);
    setLiveOfflineBusy(true);
    setLiveOffline(selectedWorkflowId, nextOffline)
      .then(function(info) { if (info) setLiveVersionInfo(info); })
      .catch(function(err) {
        console.error('[live-offline]', err && err.message ? err.message : err);
      })
      .finally(function() { setLiveOfflineBusy(false); });
  }

  // Publish the on-screen (staging) workflow as the live version. We save the
  // current canvas state first so publish reflects exactly what's in staging,
  // then call the publish endpoint (which strips variable values server-side).
  // In Live mode the canvas is read-only, so we publish from the in-memory
  // staging state (workflowStateRef), which Live mode never mutates.
  function handlePublishLive(version) {
    if (!selectedWorkflowId || publishLiveBusy) return;
    var stateToSave = workflowStateRef.current;
    if (!stateToSave || isBlankWorkflowState(stateToSave)) {
      setPublishLiveError('Add at least one node in staging before publishing to live.');
      return;
    }
    setPublishLiveBusy(true);
    setPublishLiveError('');
    saveWorkflowStateById(selectedWorkflowId, stateToSave)
      .then(function(newUpdatedAt) {
        // This save advanced the server's updatedAt — re-baseline the local
        // draft so the next regular Save isn't flagged as a false conflict.
        localFlowCache.setFlowBaseline(selectedWorkflowId, newUpdatedAt);
        return publishLiveVersion(selectedWorkflowId, version);
      })
      .then(function(result) {
        setLiveVersionInfo(result || null);
        setPublishLiveBusy(false);
        setPublishLiveOpen(false);
        // If we're viewing Live, refresh the read-only snapshot to the new one.
        if (workflowMode === 'live') loadLiveSnapshot();
      })
      .catch(function(err) {
        setPublishLiveBusy(false);
        setPublishLiveError(err && err.message ? err.message : 'Could not publish to live.');
      });
  }

  function flushPendingWorkbookOps(opsSnapshot) {
    var ops = opsSnapshot || (pendingWorkbookOpsRef.current || []).slice();
    if (!ops.length) return Promise.resolve();
    // Optimistically clear the queue; we'll re-queue any op that fails so the
    // user can retry by hitting Save again.
    pendingWorkbookOpsRef.current = [];
    localFlowCache.clearPendingWorkbookOps();

    var failures = [];
    var promises = ops.map(function(op) {
      return runWorkbookOp(op).catch(function(err) {
        console.error('[save] workbook op', op && op.type, err && err.message ? err.message : err);
        failures.push(op);
      });
    });
    return Promise.all(promises).then(function() {
      if (failures.length) {
        pendingWorkbookOpsRef.current = failures.concat(pendingWorkbookOpsRef.current || []);
        localFlowCache.writePendingWorkbookOps(pendingWorkbookOpsRef.current);
      }
    });
  }

  function runWorkbookOp(op) {
    if (!op || !op.type) return Promise.resolve();
    if (op.type === 'renameWorkbook') {
      return workbookStorage.renameWorkbook(op.workbookId, op.name);
    }
    if (op.type === 'renameFolder') {
      return workbookStorage.renameFolder(op.workbookId, op.folderId, op.name);
    }
    if (op.type === 'moveFolder') {
      return workbookStorage.moveFolder(op.workbookId, op.folderId, op.parentId);
    }
    if (op.type === 'moveWorkflow') {
      return workbookStorage.moveWorkflow(op.workflowId, {
        workbookId: op.workbookId,
        folderId: op.folderId
      });
    }
    return Promise.resolve();
  }

  function queueWorkbookOp(op) {
    if (!op || !op.type) return;
    // Coalesce repeated ops on the same target so the queue stays small.
    var queue = (pendingWorkbookOpsRef.current || []).filter(function(existing) {
      if (existing.type !== op.type) return true;
      if (op.type === 'renameWorkbook') {
        return existing.workbookId !== op.workbookId;
      }
      if (op.type === 'renameFolder' || op.type === 'moveFolder') {
        return !(existing.workbookId === op.workbookId && existing.folderId === op.folderId);
      }
      if (op.type === 'moveWorkflow') {
        return existing.workflowId !== op.workflowId;
      }
      return true;
    });
    queue.push(op);
    pendingWorkbookOpsRef.current = queue;
    localFlowCache.writePendingWorkbookOps(queue);
    setIsSaved(false);
  }

  function jumpToHistory(index) {
    var entry = historyRef.current[index];
    if (!entry) return;
    isHistoryNavRef.current = true;
    historyPosRef.current = index;
    setHistoryPos(index);
    setWorkflowState(entry.state);
    setIsSaved(false);
    setTimeout(function() { isHistoryNavRef.current = false; }, 0);
  }

  function handleUndo() {
    var nextPos = historyPosRef.current + 1;
    if (nextPos >= historyRef.current.length) return;
    jumpToHistory(nextPos);
  }

  function handleRedo() {
    var nextPos = historyPosRef.current - 1;
    if (nextPos < 0) return;
    jumpToHistory(nextPos);
  }

  function handleOpenEditor(obj) {
    setEditingObject(obj || null);
    setEditorOpen(true);
  }

  function handleCloseEditor() {
    setEditorOpen(false);
    setEditingObject(null);
    setEditorMode('template');
    editingNodeIdRef.current = null;
    manualFixAutoCloseRef.current = false;
    autoFixActiveRef.current = false;
    autoFixNodeIdRef.current = null;
    autoFixSnapshotRef.current = null;
    setAutoFixPrompt(null);
    setAutoFixReason('');
    setAutoFixSource(null);
    // Doctor "Approve" queue: always call advance when a doctor flow is
    // active. If the queue still has steps it pops the next one; if it's
    // empty it fires the completion notice. Without this branch, the LAST
    // step closing would never trigger the notice. setTimeout(0) lets the
    // editor fully unmount before the next remount (or notice).
    if (doctorFixActiveRef.current) {
      setTimeout(advanceDoctorFixQueue, 0);
    }
  }

  function handleSilentErrorFlag(nodeId, category, message) {
    autoFixSnapshotRef.current = {
      nodeOutputById: Object.assign({}, executionState && executionState.nodeOutputById ? executionState.nodeOutputById : {}),
      variableValuesByName: Object.assign({}, executionState && executionState.variableValuesByName ? executionState.variableValuesByName : {})
    };
    // Only interrupt the user with the "provide a value" popup when the flag
    // points at a variable/param that is ACTUALLY empty or null — that is the
    // only case a human must resolve by typing a value. If the named value
    // already has data (e.g. an upstream WIRING issue left inputs[0] empty even
    // though the variable is populated), fall through to autopilot so it fixes
    // the node in place instead of showing a popup the user cannot act on.
    var dataCategories = { hyperparam: 1, variable: 1 };
    if (dataCategories[category]) {
      var nodes = (workflowStateRef.current && Array.isArray(workflowStateRef.current.nodes))
        ? workflowStateRef.current.nodes
        : [];
      var node = nodes.find(function(n) { return n && String(n.id) === String(nodeId); });
      if (hasEmptyRequiredValue(node, nodes, category, message)) {
        showSilentErrorUserPopup(nodeId, category, message);
        return;
      }
    }
    handleAutoFixNode(nodeId, message);
  }

  function rehydrateExecutionStateFromStorage(workflowId, stateToRun) {
    if (!workflowId) return;
    try {
      var record = workflowProgress.readRecord(workflowId);
      if (!record) return;
      var storedIds = Object.keys(record.nodeOutputById || {});
      if (!storedIds.length && !Object.keys(record.variableValuesByName || {}).length) return;
      setExecutionState(function(prev) {
        var nextOutputById = Object.assign({}, prev.nodeOutputById || {});
        var nextStatusById = Object.assign({}, prev.nodeStatusById || {});
        var nextTimingById = Object.assign({}, prev.nodeTimingById || {});
        var nextVars = Object.assign({}, prev.variableValuesByName || {});
        storedIds.forEach(function(id) {
          var entry = record.nodeOutputById[id];
          if (!entry) return;
          if (!Object.prototype.hasOwnProperty.call(nextOutputById, id)) nextOutputById[id] = entry.output;
          if (!nextStatusById[id]) nextStatusById[id] = 'completed';
          if (!nextTimingById[id] && entry.completedAt) {
            nextTimingById[id] = { startedAt: entry.completedAt, endedAt: entry.completedAt, durationMs: 0 };
          }
        });
        Object.keys(record.variableValuesByName || {}).forEach(function(name) {
          if (!Object.prototype.hasOwnProperty.call(nextVars, name)) {
            var v = record.variableValuesByName[name];
            nextVars[name] = v ? v.value : null;
          }
        });
        return Object.assign({}, prev, {
          nodeOutputById: nextOutputById,
          nodeStatusById: nextStatusById,
          nodeTimingById: nextTimingById,
          variableValuesByName: nextVars
        });
      });
    } catch (e) {}
  }

  // Cross-session seed: walk a freshly loaded workflow's nodes and copy each
  // persisted lastOutput.shape into executionState.nodeOutputById so the
  // variable picker + node generator can describe upstream output structure
  // without requiring a re-run. Skips ids that already have a fresher output
  // from localStorage rehydration.
  function seedExecutionStateFromNodeLastOutputs(state) {
    if (!state || !Array.isArray(state.nodes)) return;
    // Prefer the COMPLETE outputs the last run stored in localStorage
    // (workflowProgressStorage) over the clipped lastOutput.shape sample:
    // both fills below skip ids that already have an output, so rehydrating
    // first lets the user inspect the full data after a reload, and the
    // shrunk shape only covers nodes with no stored output (other device,
    // cleared storage, output over the 2MB record cap).
    var wfId = selectedWorkflowIdRef.current || state.id || '';
    if (wfId) rehydrateExecutionStateFromStorage(wfId, state);
    var seedById = {};
    var anySeed = false;
    state.nodes.forEach(function(node) {
      if (!node || !node.id || !node.lastOutput || node.lastOutput.shape === undefined) return;
      seedById[String(node.id)] = node.lastOutput.shape;
      anySeed = true;
    });
    if (!anySeed) return;
    setExecutionState(function(prev) {
      var nextOutputById = Object.assign({}, prev.nodeOutputById || {});
      var nextStatusById = Object.assign({}, prev.nodeStatusById || {});
      Object.keys(seedById).forEach(function(id) {
        if (!Object.prototype.hasOwnProperty.call(nextOutputById, id)) {
          nextOutputById[id] = seedById[id];
          if (!nextStatusById[id]) nextStatusById[id] = 'completed';
        }
      });
      return Object.assign({}, prev, {
        nodeOutputById: nextOutputById,
        nodeStatusById: nextStatusById
      });
    });
  }

  function handleResumePastSilentError(nodeId) {
    var snapshot = autoFixSnapshotRef.current;
    autoFixSnapshotRef.current = null;
    var stateToRun = workflowStateRef.current;
    var priorNodeOutputByName = {};
    var outputsById = (snapshot && snapshot.nodeOutputById) ? snapshot.nodeOutputById : {};
    if (stateToRun && Array.isArray(stateToRun.nodes)) {
      stateToRun.nodes.forEach(function(node) {
        if (!node || !node.id) return;
        var nid = String(node.id);
        if (Object.prototype.hasOwnProperty.call(outputsById, nid)) {
          var title = String(node.title || '').trim();
          if (title) priorNodeOutputByName[title] = outputsById[nid];
        }
      });
    }
    var priorVariableValuesByName = (snapshot && snapshot.variableValuesByName)
      ? Object.assign({}, snapshot.variableValuesByName)
      : {};
    var wfIdResume = selectedWorkflowIdRef.current || selectedWorkflowId || (stateToRun && stateToRun.id) || '';
    if (wfIdResume) {
      rehydrateExecutionStateFromStorage(wfIdResume, stateToRun);
      try {
        var storageSnap2 = workflowProgress.buildResumeSnapshot(wfIdResume, stateToRun, nodeId);
        if (typeof console !== 'undefined' && console.log) {
          console.log('[Resume] handleResumePastSilentError', {
            nodeId: nodeId,
            wfId: wfIdResume,
            inMemoryOutputKeys: Object.keys(priorNodeOutputByName),
            storageOutputKeys: Object.keys(storageSnap2.priorNodeOutputByName)
          });
        }
        Object.keys(storageSnap2.priorNodeOutputByName).forEach(function(k) {
          if (!Object.prototype.hasOwnProperty.call(priorNodeOutputByName, k)) {
            priorNodeOutputByName[k] = storageSnap2.priorNodeOutputByName[k];
          }
        });
        Object.keys(storageSnap2.priorVariableValuesByName).forEach(function(k) {
          if (!Object.prototype.hasOwnProperty.call(priorVariableValuesByName, k)) {
            priorVariableValuesByName[k] = storageSnap2.priorVariableValuesByName[k];
          }
        });
      } catch (e) {}
    }
    startExecutionWithState(stateToRun, {
      fromNodeId: nodeId,
      priorNodeOutputByName: Object.keys(priorNodeOutputByName).length ? priorNodeOutputByName : null,
      priorVariableValuesByName: Object.keys(priorVariableValuesByName).length ? priorVariableValuesByName : null,
      skipSilentCheckNodeIds: [String(nodeId)],
      preservePriorState: true
    }).catch(function() {});
  }

  function renderHighlightedText(text) {
    var str = String(text || '');
    if (!str) return '';
    var parts = str.split(/(['"`@][A-Za-z0-9_\s:]+['"`]?)/g);
    return parts.map(function(p, i) {
      if (/^['"`][^'"`]+['"`]$/.test(p) || /^@[A-Za-z0-9_]+/.test(p)) {
        return h('strong', {
          key: i,
          style: {
            color: '#0c5a89',
            background: '#e0f2fe',
            padding: '1px 6px',
            borderRadius: 4,
            fontWeight: 600,
            fontFamily: 'SF Mono, Consolas, monospace',
            fontSize: '12.5px'
          }
        }, p);
      }
      return p;
    });
  }

  function buildSilentErrorPopupBody(category, message, guidance) {
    var sectionLabel = {
      display: 'block',
      fontWeight: 700,
      fontSize: 11,
      textTransform: 'uppercase',
      letterSpacing: '0.6px',
      marginBottom: 5
    };
    return h('div', { style: { textAlign: 'left', fontSize: '13.5px', lineHeight: 1.55, color: '#444' } },
      h('div', { style: { marginBottom: 12 } },
        h('span', { style: Object.assign({}, sectionLabel, { color: '#dc2626' }) }, 'Issue'),
        h('span', null, renderHighlightedText(message || '(no details)'))
      ),
      h('div', null,
        h('span', { style: Object.assign({}, sectionLabel, { color: '#0c5a89' }) }, 'What to do'),
        h('span', null, renderHighlightedText(guidance))
      )
    );
  }

  function buildConnectionInfoBody(rawMessage) {
    var text = String(rawMessage || 'A possible wiring issue was detected on the outgoing connections.');
    var parts = text.split(/\s*How to fix:\s*/i);
    var issue = (parts[0] || '').trim();
    var fix = parts.length > 1 ? parts.slice(1).join('How to fix:').trim() : '';

    var blockStyle = {
      textAlign: 'left',
      borderLeft: '3px solid',
      padding: '8px 12px',
      borderRadius: '6px',
      marginBottom: '8px',
      background: '#f7f7f9'
    };
    var labelStyle = {
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      marginBottom: '4px'
    };
    var bodyStyle = { fontSize: '13.5px', color: '#333', lineHeight: 1.5 };

    var blocks = [];
    if (issue) {
      blocks.push(h('div', {
        key: 'issue',
        style: Object.assign({}, blockStyle, { borderLeftColor: '#dc2626', background: '#fdecec' })
      },
        h('div', { style: Object.assign({}, labelStyle, { color: '#dc2626' }) }, 'Issue'),
        h('div', { style: bodyStyle }, issue)
      ));
    }
    if (fix) {
      blocks.push(h('div', {
        key: 'fix',
        style: Object.assign({}, blockStyle, { borderLeftColor: '#2563eb', background: '#eef4ff', marginBottom: 0 })
      },
        h('div', { style: Object.assign({}, labelStyle, { color: '#2563eb' }) }, 'How to fix'),
        h('div', { style: bodyStyle }, fix)
      ));
    }
    if (!blocks.length) return text;
    return h('div', { style: { width: '100%' } }, blocks);
  }

  function showConnectionInfoPopup(nodeId, message) {
    var nodes = (workflowStateRef.current && Array.isArray(workflowStateRef.current.nodes))
      ? workflowStateRef.current.nodes
      : [];
    var node = nodes.find(function(n) { return n && String(n.id) === String(nodeId); });
    var nodeLabel = (node && node.title) ? node.title : String(nodeId);
    setRuntimePopup({
      title: 'Connection check on "' + nodeLabel + '"',
      message: buildConnectionInfoBody(message),
      confirmLabel: 'Got it',
      hideCancel: true
    });
  }

  function showSilentErrorUserPopup(nodeId, category, message) {
    var nodes = (workflowStateRef.current && Array.isArray(workflowStateRef.current.nodes))
      ? workflowStateRef.current.nodes
      : [];
    var node = nodes.find(function(n) { return n && String(n.id) === String(nodeId); });
    var nodeLabel = (node && node.title) ? node.title : String(nodeId);
    var titleByCategory = {
      hyperparam: 'Missing input on "' + nodeLabel + '"',
      variable: 'Variable issue affecting "' + nodeLabel + '"',
      input: 'Upstream input issue for "' + nodeLabel + '"'
    };
    var guidanceByCategory = {
      hyperparam: 'Open the node and fill in the missing or empty hyperparameter. Code changes will not fix a missing input.',
      variable: 'Open the referenced variable (e.g. @VariableName) and set its value, or add a Set Variable node earlier in the flow.',
      input: 'Check the upstream node \u2014 its output appears to be missing the field this node expected, or is empty.'
    };
    setRuntimePopup({
      title: titleByCategory[category] || 'Silent error on "' + nodeLabel + '"',
      message: buildSilentErrorPopupBody(category, message, guidanceByCategory[category] || 'Review the node settings and the upstream data.'),
      confirmLabel: 'Resume',
      cancelLabel: 'Close',
      hideCancel: false,
      onConfirm: function() { handleResumePastSilentError(nodeId); },
      onCancel: function() { autoFixSnapshotRef.current = null; }
    });
  }

  // Surfaced when the server-side auto-fix finished WITHOUT changing code. The
  // backend's node.fix.skipped reason tells us WHY: a missing setting, a missing
  // value/credential, or a root cause that lives outside this node. We pair the
  // fixer's short root-cause message with a concrete next step so the user knows
  // what to do — and never silently leaves them wondering. Deduped to one notice
  // per node per run.
  function showCodefixSkippedNotice(nodeId, reason, message) {
    var key = String(nodeId || '');
    if (!key || codefixSkipNotifiedRef.current[key]) return;
    codefixSkipNotifiedRef.current[key] = true;

    var nodes = (workflowStateRef.current && Array.isArray(workflowStateRef.current.nodes))
      ? workflowStateRef.current.nodes
      : [];
    var node = nodes.find(function(n) { return n && String(n.id) === key; });
    var nodeLabel = (node && node.title) ? node.title : key;

    var titleByReason = {
      hyperparam_required: 'Auto-fix needs a setting on "' + nodeLabel + '"',
      user_data_required: 'Auto-fix needs a value on "' + nodeLabel + '"',
      no_fix: 'Auto-fix made no change to "' + nodeLabel + '"'
    };
    var guidanceByReason = {
      hyperparam_required: 'Open the node and fill in the missing or empty hyperparameter — changing code can’t supply a value that isn’t there.',
      user_data_required: 'Provide the missing value or credential (open the node, or the variable it reads), then run again.',
      no_fix: 'The cause looks to be outside this node’s code. Check the upstream node’s output and any variables this node reads, then adjust them — or open the node to edit it yourself.'
    };
    var causeText = message ? String(message).trim() : 'The auto-fixer reviewed the node but did not change its code.';

    setRuntimePopup({
      title: titleByReason[reason] || ('Auto-fix made no change to "' + nodeLabel + '"'),
      message: 'Why no code change was made:\n' + causeText + '\n\n'
        + 'What you can do:\n' + (guidanceByReason[reason] || 'Review the node settings and the upstream data, then run again.'),
      confirmLabel: 'Got it',
      hideCancel: true
    });
  }

  // Build a compact "last 5 completed nodes" summary for the autofix prompt.
  // Each line is "Title → outgoing target titles" so the fix agent can see the
  // shape of the path leading into the node it's about to edit. Matches the
  // context the silent-error checker already sees, so the checker and the
  // fixer reason from the same workflow overview. Excludes the current node
  // (the one being fixed — its identity is already in the editor context).
  function buildRecentPathContext(currentNodeId) {
    var steps = (executionTraceRef.current && Array.isArray(executionTraceRef.current.steps))
      ? executionTraceRef.current.steps
      : [];
    if (steps.length === 0) return '';
    var nodes = (workflowStateRef.current && Array.isArray(workflowStateRef.current.nodes))
      ? workflowStateRef.current.nodes
      : [];
    var connections = (workflowStateRef.current && Array.isArray(workflowStateRef.current.connections))
      ? workflowStateRef.current.connections
      : [];
    // Resolve a display label per node. For set-variable nodes we append the
    // target variable name — the raw title is often just "Set Variable" which
    // tells the fixer nothing about which variable was written.
    var labelById = {};
    for (var i = 0; i < nodes.length; i += 1) {
      var n = nodes[i];
      if (!n || !n.id) continue;
      var baseTitle = String(n.title || '');
      if (n.icon === 'set-variable') {
        var varName = String((n.paramValues && n.paramValues.variable) || '').trim();
        if (varName) baseTitle = (baseTitle || 'Set Variable') + " (sets '" + varName + "')";
      }
      labelById[String(n.id)] = baseTitle;
    }
    var outgoingById = {};
    for (var j = 0; j < connections.length; j += 1) {
      var c = connections[j];
      if (!c || !c.from) continue;
      var fromKey = String(c.from);
      if (!outgoingById[fromKey]) outgoingById[fromKey] = [];
      outgoingById[fromKey].push(String(c.to || ''));
    }
    var currentKey = String(currentNodeId || '');
    var filtered = [];
    for (var k = 0; k < steps.length; k += 1) {
      var s = steps[k];
      if (!s || !s.nodeId) continue;
      if (currentKey && String(s.nodeId) === currentKey) continue;
      filtered.push(s);
    }
    var recent = filtered.slice(-5);
    if (recent.length === 0) return '';
    var lines = [];
    for (var m = 0; m < recent.length; m += 1) {
      var step = recent[m];
      var stepKey = String(step.nodeId);
      var label = labelById[stepKey] || step.nodeName || '(untitled)';
      var targets = (outgoingById[stepKey] || [])
        .map(function(toId) { return labelById[toId] || toId; })
        .filter(Boolean);
      var suffix = targets.length ? ' \u2192 ' + targets.join(', ') : ' \u2192 (none)';
      lines.push((m + 1) + '. ' + label + suffix);
    }
    return lines.join('\n');
  }

  // Manual fix entry-point used by surfaces like Runtime Log Chat findings:
  // open the node editor with AskCode pre-loaded, but skip the autopilot retry
  // counter (the user is asking once, on purpose).
  function handleManualFixNode(nodeId, message, opts) {
    var nodes = (workflowStateRef.current && Array.isArray(workflowStateRef.current.nodes))
      ? workflowStateRef.current.nodes
      : [];
    var node = nodes.find(function(n) { return n && String(n.id) === String(nodeId); });
    if (!node) return { opened: false, reason: 'no node with id "' + nodeId + '"' };
    var lastOutput = (executionState && executionState.nodeOutputById)
      ? executionState.nodeOutputById[nodeId]
      : undefined;
    var reasonText = message ? String(message).trim() : '';
    var promptText = reasonText || 'Help me fix this node.';
    setAutoFixPrompt(promptText);
    setAutoFixReason(reasonText);
    setAutoFixSource('doctor');
    var opened = handleEditCanvasNode(node, lastOutput);
    if (!opened) return { opened: false, reason: 'the node is locked' };
    manualFixAutoCloseRef.current = !!(opts && opts.autoClose);
    return { opened: true };
  }

  // Doctor-chat "Approve" entry point. Stash the ordered list and open
  // AskCode for the FIRST step. onAutoFixComplete auto-closes each editor
  // when the change lands; handleCloseEditor then advances the queue.
  function handleApplyFixPlanSteps(steps) {
    var list = Array.isArray(steps) ? steps.filter(function(s) { return s && s.nodeId; }) : [];
    if (list.length === 0) return;
    doctorFixQueueRef.current = list.slice();
    doctorFixActiveRef.current = true;
    // Reset the per-queue tally so the doctor sidebar gets a fresh summary.
    doctorFixResultsRef.current = {
      applied: [],
      skipped: [],
      totalSteps: list.length
    };
    setDoctorFixStatus({ activeNodeId: null, completed: {} });
    advanceDoctorFixQueue();
  }

  // Pop the next queued step and open AskCode for it. When the queue is
  // empty AND a doctor flow was active, push a completion notice into the
  // doctor sidebar so it can summarize what got applied vs skipped.
  function advanceDoctorFixQueue() {
    var queue = doctorFixQueueRef.current;
    if (!Array.isArray(queue) || queue.length === 0) {
      var wasActive = doctorFixActiveRef.current;
      doctorFixQueueRef.current = [];
      doctorFixActiveRef.current = false;
      if (wasActive && doctorFixResultsRef.current) {
        var results = doctorFixResultsRef.current;
        doctorFixResultsRef.current = null;
        setDoctorPendingNotice({
          kind: 'queue_complete',
          totalSteps: results.totalSteps,
          applied: results.applied,
          skipped: results.skipped
        });
      }
      return;
    }
    var next = queue.shift();
    doctorFixQueueRef.current = queue;
    setDoctorFixStatus(function(prev) {
      return { activeNodeId: next.nodeId, completed: (prev && prev.completed) || {} };
    });
    handleManualFixNode(next.nodeId, next.askcodePrompt || '');
  }

  function handleAutoFixNode(nodeId, message) {
    var nodes = (workflowStateRef.current && Array.isArray(workflowStateRef.current.nodes))
      ? workflowStateRef.current.nodes
      : [];
    var node = nodes.find(function(n) { return n && String(n.id) === String(nodeId); });
    if (!node) return;
    // Apify nodes have no AI-generated code to fix — they run a user-picked
    // Store actor with the user's own API key + input. A failure (missing/invalid
    // key, no actor picked, actor permissions not approved, bad input) is only
    // fixable by the user in the node editor, so skip AskCode and point them at
    // the settings on the right.
    if (node.icon === 'apify') {
      var AB = RUN_NOTICE_STYLE;
      setRuntimePopup({
        boxStyle: { width: 520 },
        title: '"' + (node.title || 'Apify') + '" needs your attention',
        message: buildRunFailureNotice({
          lead: ['Autopilot doesn’t code-fix Apify steps — this node runs a Store actor you configure with your own API key and input.'],
          issueText: message ? String(message) : '',
          stepsLabel: 'How to fix it',
          steps: [
            ['If the message links to the ', h('span', { style: AB.mark }, 'Apify console'), ', open it and approve the actor’s permissions, then come back.'],
            ['Otherwise open the node and check your ', h('span', { style: AB.mark }, 'API key'), ', the ', h('span', { style: AB.mark }, 'picked actor'), ', and its ', h('span', { style: AB.mark }, 'input'), ' on the right.'],
            ['Then press ', h('span', { style: AB.mark }, 'Play'), ' to run it again.']
          ]
        })
      });
      return;
    }
    // Browser automation nodes have no AI-generated code to fix. AskCode
    // cannot repair a stale deterministic selector or an agentic task/page
    // mismatch, so show the user the relevant browser-node recovery steps.
    if (node.icon === 'browser-action' || node.icon === 'browser-agentic-action') {
      var isAgenticBrowserNode = node.icon === 'browser-agentic-action';
      var SB = RUN_NOTICE_STYLE;
      setRuntimePopup({
        boxStyle: { width: 520 },
        title: '"' + (node.title || (isAgenticBrowserNode ? 'Browser Agentic Action' : 'Browser Action')) + '" needs attention',
        message: buildRunFailureNotice({
          lead: [isAgenticBrowserNode
            ? 'Autopilot doesn’t code-fix agentic browser steps — check its Task and starting page.'
            : 'Autopilot doesn’t code-fix browser steps — the page has likely changed since the element was picked.'],
          issueText: message ? String(message) : '',
          stepsLabel: 'How to fix it',
          steps: isAgenticBrowserNode ? [
            ['Open the node and confirm its ', h('span', { style: SB.mark }, 'Task'), ' describes one exact, visibly verifiable result.'],
            ['Confirm the starting page or ', h('span', { style: SB.mark }, 'Continue from'), ' browser node is correct and the Browser Action extension is connected.'],
            ['Then press ', h('span', { style: SB.mark }, 'Play'), ' to run it again.']
          ] : [
            ['If the element only appears after a click (a popup, menu or composer), add a Browser Action that ', h('span', { style: SB.mark }, 'clicks it open first'), ', and set this node to ', h('span', { style: SB.mark }, 'Continue from'), ' that one.'],
            ['Otherwise open the node and press ', h('span', { style: SB.mark }, 'Pick element'), ' to select the target again.'],
            ['Then press ', h('span', { style: SB.mark }, 'Play'), ' to run it again.']
          ]
        })
      });
      return;
    }
    // Autopilot can't write code that supplies a value the user never entered.
    // If the failure names a setting/variable that is actually empty, show the
    // "what to do" popup instead of opening the AskCode fix loop. Manual fixes
    // (handleManualFixNode / doctor queue) bypass this on purpose.
    var missingInput = detectMissingUserInput(node, nodes, message);
    if (missingInput) {
      showCodefixSkippedNotice(
        nodeId,
        missingInput.category === 'variable' ? 'user_data_required' : 'hyperparam_required',
        message
      );
      return;
    }
    var key = String(nodeId);
    var prevAttempts = autoFixAttemptsRef.current[key] || 0;
    if (prevAttempts >= AUTOFIX_MAX_ATTEMPTS) {
      autoFixAttemptsRef.current[key] = 0;
      var nodeLabel = node.title || key;
      var issueText = message ? String(message) : 'Output did not match expected input/variables.';
      var S = RUN_NOTICE_STYLE;
      setRuntimePopup({
        boxStyle: { width: 520 },
        title: 'We couldn\u2019t fix "' + nodeLabel + '" for you',
        message: buildRunFailureNotice({
          lead: ['Autopilot gave it ', h('span', { style: S.strong }, AUTOFIX_MAX_ATTEMPTS + ' tries'), ', but this one needs a human touch.'],
          issueText: issueText,
          stepsLabel: 'How to fix it yourself',
          steps: [
            ['Fill in the ', h('span', { style: S.mark }, 'empty boxes on the right'), ' \u2014 the message above tells you which one is still missing.'],
            ['Then press ', h('span', { style: S.mark }, 'Play'), ' to run it again.'],
            ['Meant to leave it blank? You can turn off ', h('span', { style: S.chip }, 'Check Silent Error'), ' for this node so it stops flagging it.']
          ]
        })
      });
      return;
    }
    autoFixAttemptsRef.current[key] = prevAttempts + 1;
    var lastOutput = (executionState && executionState.nodeOutputById)
      ? executionState.nodeOutputById[nodeId]
      : undefined;
    // Use the checker's actual reason as the user message in the AskCode chat
    // so the AI sees exactly what was flagged (e.g. "Input was not a JSON
    // string as required by the goal.") instead of a generic "fix any issue."
    var reasonText = message ? String(message).trim() : '';
    var promptText = reasonText || 'fix any issue.';
    autoFixActiveRef.current = true;
    autoFixNodeIdRef.current = key;
    setAutoFixPrompt(promptText);
    // Same reason still drives the banner above the chat for visual context.
    setAutoFixReason(reasonText);
    setAutoFixSource('autopilot');
    handleEditCanvasNode(node, lastOutput);
  }

  function handleEditCanvasNode(node, lastOutput) {
    if (isLockedDuplicateNode(node)) return false;
    var staticVars = {};
    var variableTypesByName = {};
    (workflowStateRef.current && workflowStateRef.current.nodes || []).forEach(function(n) {
      if (n && n.icon === 'variables' && n.title) {
        var declaredType = n.paramValues && n.paramValues.dataType ? String(n.paramValues.dataType) : 'string';
        var rawValue = n.paramValues && n.paramValues.value !== undefined ? n.paramValues.value : '';
        // Match the runtime: parse JSON-typed defaults so AskCode sees a real
        // array/object instead of the textarea string. Without this, vars["X"]
        // arrives as `"[]"` for a List and the model has to JSON.parse it.
        staticVars[n.title] = parseVariableValue(declaredType, rawValue);
        variableTypesByName[n.title] = declaredType;
      }
    });
    var allVars = Object.assign({}, staticVars, executionState && executionState.variableValuesByName ? executionState.variableValuesByName : {});
    var nodeId = node && node.id;
    var lastInput = executionState && executionState.nodeInputById && nodeId ? executionState.nodeInputById[nodeId] : undefined;
    var nodeOutputByTitle = {};
    var outputById = (executionState && executionState.nodeOutputById) || {};
    (workflowStateRef.current && workflowStateRef.current.nodes || []).forEach(function(n) {
      if (n && n.id && n.title && Object.prototype.hasOwnProperty.call(outputById, n.id)) {
        nodeOutputByTitle[n.title] = outputById[n.id];
      }
    });
    // Snapshot the live graph so AskCode can describe downstream destinations
    // even when the workflow is unsaved (no workflowId in the DB yet). We only
    // need the fields buildDownstreamContext reads — id/title/icon/category/
    // paramValues for nodes, and the from/to/propPath/etc. for connections.
    var liveState = workflowStateRef.current || {};
    var workflowGraph = {
      nodes: (Array.isArray(liveState.nodes) ? liveState.nodes : []).map(function(n) {
        return {
          id: n && n.id,
          title: n && n.title,
          icon: n && n.icon,
          category: n && n.category,
          paramValues: n && n.paramValues
        };
      }),
      connections: (Array.isArray(liveState.connections) ? liveState.connections : []).map(function(c) {
        return {
          id: c && c.id,
          from: c && c.from,
          to: c && c.to,
          fromAnchor: c && c.fromAnchor,
          toAnchor: c && c.toAnchor,
          propPath: c && c.propPath,
          branch: c && c.branch,
          noData: c && c.noData
        };
      })
    };
    // Preserve persisted lastOutput.at (timestamp of last cached run) before
    // we overwrite enrichedNode.lastOutput with the live runtime value below.
    // Lets the editor show "last tested 3w ago" so users know when the shape
    // they're looking at was captured.
    var persistedLastOutputAt = (node && node.lastOutput && node.lastOutput.at) ? node.lastOutput.at : null;
    var enrichedNode = Object.assign({}, node || {}, {
      availableVariables: allVars,
      variableTypesByName: variableTypesByName,
      nodeOutputByTitle: nodeOutputByTitle,
      lastInput: lastInput,
      lastOutputAt: persistedLastOutputAt,
      startParams: getStartNodeParams(workflowStateRef.current && workflowStateRef.current.nodes),
      workflowGraph: workflowGraph
    });
    if (lastOutput !== undefined) {
      enrichedNode.lastOutput = lastOutput;
    }
    setEditingObject(enrichedNode);
    setEditorMode('node');
    setEditorOpen(true);
    editingNodeIdRef.current = enrichedNode.id || null;
    return true;
  }

  function buildNodeParamValues(currentNode, nextHyperparameters) {
    var currentParamValues = currentNode && currentNode.paramValues && typeof currentNode.paramValues === 'object'
      ? currentNode.paramValues
      : {};
    var previousHyperparameters = currentNode && Array.isArray(currentNode.hyperparameters)
      ? currentNode.hyperparameters
      : [];
    var previousById = previousHyperparameters.reduce(function(map, param) {
      if (param && param.id) map[String(param.id)] = param;
      return map;
    }, {});

    return (Array.isArray(nextHyperparameters) ? nextHyperparameters : []).reduce(function(acc, param) {
      var paramKey = param && param.key ? String(param.key).trim() : '';
      if (!paramKey) return acc;

      var priorParam = param && param.id ? previousById[String(param.id)] : null;
      var priorKey = priorParam && priorParam.key ? String(priorParam.key).trim() : paramKey;
      var hasDefault = param && Object.prototype.hasOwnProperty.call(param, 'default');

      // Lockstep (modal→sidebar): when the user changes a param's default in
      // the "Edit this step" modal, adopt it as the live value — overriding any
      // stale per-instance value — so both editors keep showing the same thing.
      var defaultChanged = priorParam && hasDefault && priorParam.default !== param.default;

      if (defaultChanged) {
        acc[paramKey] = param.default;
      } else if (Object.prototype.hasOwnProperty.call(currentParamValues, paramKey)) {
        acc[paramKey] = currentParamValues[paramKey];
      } else if (priorKey !== paramKey && Object.prototype.hasOwnProperty.call(currentParamValues, priorKey)) {
        acc[paramKey] = currentParamValues[priorKey];
      } else {
        acc[paramKey] = hasDefault ? param.default : '';
      }

      return acc;
    }, {});
  }

  function mergeCanvasNodeUpdate(node, update) {
    if (!node) return node;

    var nextNode = Object.assign({}, node);

    if (Object.prototype.hasOwnProperty.call(update, 'code')) {
      nextNode.code = update.code;
    }
    if (Object.prototype.hasOwnProperty.call(update, 'logoUrl')) {
      nextNode.logoUrl = update.logoUrl;
    }
    if (Object.prototype.hasOwnProperty.call(update, 'borderColor')) {
      nextNode.borderColor = update.borderColor;
    }
    if (Object.prototype.hasOwnProperty.call(update, 'generationPrompt')) {
      nextNode.generationPrompt = update.generationPrompt;
    }
    if (Object.prototype.hasOwnProperty.call(update, 'changeHistory')) {
      nextNode.changeHistory = Array.isArray(update.changeHistory) ? update.changeHistory : [];
    }
    if (Object.prototype.hasOwnProperty.call(update, 'hyperparameters')) {
      nextNode.hyperparameters = Array.isArray(update.hyperparameters) ? update.hyperparameters : [];
      nextNode.paramValues = buildNodeParamValues(node, nextNode.hyperparameters);
    }
    if (Object.prototype.hasOwnProperty.call(update, 'paramValues')) {
      nextNode.paramValues = update.paramValues;
    }

    return nextNode;
  }

  // Apply <edge_changes> proposed by AskCode to outgoing connections from a
  // source node. Each update is { from, to, branch?, propPath, connectionId? }
  // and overrides only the propPath of the matching connection — leaves all
  // other connection fields (id, anchors, noData, branch, etc.) intact.
  function applyConnectionUpdates(prevConnections, sourceNodeId, updates) {
    var connections = Array.isArray(prevConnections) ? prevConnections : [];
    if (!Array.isArray(updates) || !updates.length || !sourceNodeId) return connections;
    return connections.map(function(c) {
      if (!c) return c;
      if (String(c.from) !== String(sourceNodeId)) return c;
      // Find a matching update for this connection: prefer connectionId match,
      // fall back to (to + optional branch) so the AI doesn't have to know the
      // edge id when only a target node id is unambiguous.
      var match = null;
      for (var i = 0; i < updates.length; i += 1) {
        var u = updates[i];
        if (!u) continue;
        if (u.connectionId && c.id && String(u.connectionId) === String(c.id)) { match = u; break; }
        if (String(u.to) !== String(c.to)) continue;
        if (u.branch) {
          var connBranch = c.branch || c.fromAnchor || '';
          if (String(connBranch) !== String(u.branch)) continue;
        }
        match = u;
        break;
      }
      if (!match) return c;
      var nextPath = match.propPath === null || match.propPath === undefined || match.propPath === ''
        ? null
        : String(match.propPath);
      return Object.assign({}, c, { propPath: nextPath });
    });
  }

  function handleSaveCanvasNodeCode(update) {
    var nodeId = update && update.id;
    if (nodeId) {
      setWorkflowState(function(prev) {
        var nextNodes = (prev.nodes || []).map(function(n) {
          if (n.id !== nodeId) return n;
          if (isLockedDuplicateNode(n)) return n;
          return mergeCanvasNodeUpdate(n, update);
        });
        var nextConnections = Array.isArray(update.connectionUpdates) && update.connectionUpdates.length
          ? applyConnectionUpdates(prev.connections, nodeId, update.connectionUpdates)
          : prev.connections;
        return Object.assign({}, prev, { nodes: nextNodes, connections: nextConnections });
      });
    }
    handleCloseEditor();
  }

  function handleUpdateCanvasNodeCodeSilent(update) {
    var nodeId = update && update.id;
    if (!nodeId) return;
    setWorkflowState(function(prev) {
      var nextNodes = (prev.nodes || []).map(function(n) {
        if (n.id !== nodeId) return n;
        if (isLockedDuplicateNode(n)) return n;
        return mergeCanvasNodeUpdate(n, update);
      });
      var nextConnections = Array.isArray(update.connectionUpdates) && update.connectionUpdates.length
        ? applyConnectionUpdates(prev.connections, nodeId, update.connectionUpdates)
        : prev.connections;
      var nextState = Object.assign({}, prev, { nodes: nextNodes, connections: nextConnections });
      // Local-first: stash to cache and mark dirty. The flow will be flushed
      // to the backend the next time the user clicks Save.
      if (selectedWorkflowId) {
        localFlowCache.writeFlowCache(selectedWorkflowId, nextState, { dirty: true });
        dirtyFlowIdsRef.current[selectedWorkflowId] = true;
      }
      return nextState;
    });
  }

  function handleSaveCustomObject(template) {
    return saveCustomObject(template)
      .then(function() {
        setCustomObjectTemplates(function(prev) {
          var replaced = false;
          var next = (prev || []).map(function(item) {
            if (item.id !== template.id) return item;
            replaced = true;
            return template;
          });
          return replaced ? next : next.concat(template);
        });
        handleCloseEditor();
      })
      .catch(function(error) {
        setFatalError(error && error.message ? error.message : 'Could not save custom object.');
      });
  }

  function handleDeleteCustomObject(objectId) {
    if (!objectId) return;
    deleteCustomObject(objectId)
      .then(function() {
        setCustomObjectTemplates(function(prev) {
          return (prev || []).filter(function(item) { return item.id !== objectId; });
        });
      })
      .catch(function(error) {
        setFatalError(error && error.message ? error.message : 'Could not delete custom object.');
      });
  }

  function extractUsedVariables(node) {
    var found = {};
    var varPattern = /vars\[["']([^"']+)["']\]/g;
    // Scan code
    var code = node.code || '';
    var m;
    while ((m = varPattern.exec(code)) !== null) { found[m[1]] = true; }
    // Scan hyperparameter default values
    if (Array.isArray(node.hyperparameters)) {
      node.hyperparameters.forEach(function(p) {
        var val = String(p.default || '');
        varPattern.lastIndex = 0;
        while ((m = varPattern.exec(val)) !== null) { found[m[1]] = true; }
      });
    }
    // Scan paramValues
    if (node.paramValues && typeof node.paramValues === 'object') {
      Object.values(node.paramValues).forEach(function(val) {
        varPattern.lastIndex = 0;
        while ((m = varPattern.exec(String(val || ''))) !== null) { found[m[1]] = true; }
      });
    }
    return Object.keys(found);
  }

  function handleSaveNodeAsTemplate(node) {
    if (!node) return;
    var newId = 'custom-' + Date.now().toString(36);
    var template = {
      id: newId,
      kind: 'workflow-node',
      nodeType: 'card',
      category: 'custom',
      isUserTemplate: true,
      title: node.title || node.label || 'Custom Node',
      subtitle: node.subtitle || 'Custom node',
      label: node.label || node.title || 'Custom Node',
      icon: node.icon || 'custom',
      code: node.code || '',
      hyperparameters: Array.isArray(node.hyperparameters) ? node.hyperparameters : [],
      generationPrompt: node.generationPrompt || '',
      logoUrl: node.logoUrl || '',
      conditionRule: null,
      options: null,
      stats: null,
      borderColor: '',
      usedVariables: extractUsedVariables(node)
    };
    handleSaveCustomObject(template);
  }

  function handleExportWorkflow() {
    downloadWorkflowJson(workflowState);
  }

  function handleConvertToCode() {
    var nodes = (workflowState && Array.isArray(workflowState.nodes)) ? workflowState.nodes : [];
    var blockingIcons = { agent: 1, classifier: 1, router: 1, 'computer-use': 1 };
    var blockers = nodes.filter(function(n) { return n && blockingIcons[n.icon]; });
    if (blockers.length) {
      var names = blockers.map(function(n) { return '• ' + (n.title || n.icon); }).join('\n');
      setRuntimePopup({
        title: 'Cannot convert to code',
        message: 'This workflow contains AI nodes that require live LLM execution and can’t be turned into a runnable script:\n\n'
          + names
          + '\n\nRemove or replace these nodes and try again.',
        confirmLabel: 'Got it',
        hideCancel: true
      });
      return;
    }
    downloadWorkflowCode(workflowState);
  }

  function handleImportWorkflow() {
    if (typeof document === 'undefined') return;
    var input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = function(event) {
      var file = event && event.target && event.target.files ? event.target.files[0] : null;
      if (!file) return;
      readTextFile(file)
        .then(parseImportedWorkflowState)
        .then(function(nextState) {
          setWorkflowState(nextState);
          setFatalError('');
        })
        .catch(function(error) {
          setFatalError(error && error.message ? error.message : 'Could not import workflow.');
        });
    };
    input.click();
  }

  function handleClearWorkflow() {
    var currentState = workflowStateRef.current || createEmptyWorkflowState();
    closeExecutionStream();
    setExecutionState(createExecutionState());
    setWorkflowState({
      workflowName: currentState.workflowName !== undefined && currentState.workflowName !== null
        ? String(currentState.workflowName)
        : 'Untitled Workflow',
      aiInstruction: currentState.aiInstruction !== undefined && currentState.aiInstruction !== null
        ? String(currentState.aiInstruction)
        : '',
      nodes: ensureDefaultStartNode([]),
      connections: []
    });
    setFatalError('');
  }

  // Records a variable change into the execution trace: the running snapshot
  // plus the per-step delta that gets persisted with the next pushed step.
  function recordTraceVariable(name, value) {
    var trace = executionTraceRef.current;
    if (!trace) return;
    trace.variables[name] = value;
    if (!trace.changedSinceLastStep) trace.changedSinceLastStep = {};
    trace.changedSinceLastStep[name] = value;
  }

  function applyExecutionEvent(event, executionIdOverride) {
    if (!event || !event.type) return;

    // ── Execution trace collection ──
    if (event.type === 'execution.started') {
      // `variables` is the full running snapshot; `changedSinceLastStep` holds
      // only what changed since the previous step was pushed. Steps persist
      // the DELTA — storing the full snapshot per step multiplied every big
      // variable by the number of steps and blew the localStorage quota.
      executionTraceRef.current = { variables: {}, changedSinceLastStep: {}, steps: [] };
      // Clear any lingering "Checking skipped" chips from the prior run so
      // they don't bleed into the next one.
      setExecutionState(function(prev) {
        if (!prev.silentCheckSkippedByNodeId || Object.keys(prev.silentCheckSkippedByNodeId).length === 0) return prev;
        return Object.assign({}, prev, { silentCheckSkippedByNodeId: {} });
      });
    }
    // Keep running variable snapshot up to date
    if (event.type === 'variables.updated' && event.updates && typeof event.updates === 'object') {
      var varKeys = Object.keys(event.updates);
      for (var vi2 = 0; vi2 < varKeys.length; vi2++) {
        recordTraceVariable(varKeys[vi2], event.updates[varKeys[vi2]]);
      }
    }
    // Record the failing node SYNCHRONOUSLY (not inside the setExecutionState
    // updater below). React 18 batches state updaters and runs them at flush
    // time, so a ref mutation inside the updater is not guaranteed to have run
    // before the next SSE event (execution.failed) reads lastFailedNodeRef in
    // the same tick. When that race lost, execution.failed saw a null ref and
    // skipped opening AskCode — the user just got a red ERROR badge. Setting it
    // here, in the synchronous body of applyExecutionEvent, closes that race.
    if (event.type === 'node.failed' && event.nodeId) {
      lastFailedNodeRef.current = { nodeId: String(event.nodeId), error: event.error || 'Node execution failed.' };
    }
    if ((event.type === 'node.completed' || event.type === 'node.failed') && event.nodeId) {
      var traceNode = (workflowStateRef.current && Array.isArray(workflowStateRef.current.nodes))
        ? workflowStateRef.current.nodes.find(function(n) { return n && String(n.id) === String(event.nodeId); })
        : null;
      // Update variable snapshot from variable / set-variable nodes
      if (traceNode && event.type === 'node.completed') {
        if (traceNode.icon === 'variables') {
          var vName = String(traceNode.title || '').trim();
          if (vName) recordTraceVariable(vName, event.output !== undefined ? event.output : null);
        } else if (traceNode.icon === 'set-variable') {
          var svName = String((traceNode.paramValues && traceNode.paramValues.variable) || '').trim();
          if (svName) recordTraceVariable(svName, event.output !== undefined ? event.output : null);
        }
      }
      executionTraceRef.current.steps.push({
        nodeId: event.nodeId,
        nodeName: traceNode ? (traceNode.title || '') : '',
        nodeOutput: event.type === 'node.completed' ? (event.output !== undefined ? event.output : null) : { __error: true, message: event.error || 'Node execution failed.' },
        // Only the variables that changed since the previous step — readers
        // fold deltas in order to rebuild the snapshot at any step.
        changedVariables: Object.assign({}, executionTraceRef.current.changedSinceLastStep || {})
      });
      executionTraceRef.current.changedSinceLastStep = {};
    }
    if (event.type === 'execution.completed' || event.type === 'execution.failed' || event.type === 'execution.stopped') {
      try {
        var singleNodeId = executionFromNodeIdRef.current;
        if (singleNodeId) {
          // Single-node run: update only the matching step(s) in the existing trace
          var existing = null;
          try { existing = JSON.parse(localStorage.getItem('alleflow_execution_trace') || 'null'); } catch (e) {}
          if (existing && Array.isArray(existing.steps)) {
            var newSteps = executionTraceRef.current.steps || [];
            // Build a map of updated nodes from this run (latest per nodeId)
            var updatedByNodeId = {};
            for (var si = 0; si < newSteps.length; si++) {
              if (newSteps[si] && newSteps[si].nodeId) updatedByNodeId[newSteps[si].nodeId] = newSteps[si];
            }
            // Replace latest matching step in existing trace
            for (var ei = existing.steps.length - 1; ei >= 0; ei--) {
              var stepNodeId = existing.steps[ei] && existing.steps[ei].nodeId;
              if (stepNodeId && updatedByNodeId[stepNodeId]) {
                existing.steps[ei] = updatedByNodeId[stepNodeId];
                delete updatedByNodeId[stepNodeId];
              }
            }
            existing.timestamp = new Date().toISOString();
            storageQuota.safeSetItem('alleflow_execution_trace', JSON.stringify(existing));
          }
        } else {
          // Full workflow run: replace entire trace
          var traceRecord = {
            executionId: executionIdOverride || null,
            workflowId: selectedWorkflowIdRef.current || null,
            workflowName: (workflowStateRef.current && workflowStateRef.current.workflowName) ? String(workflowStateRef.current.workflowName) : '',
            status: event.type === 'execution.completed' ? 'completed' : event.type === 'execution.stopped' ? 'stopped' : 'failed',
            timestamp: new Date().toISOString(),
            steps: executionTraceRef.current.steps || []
          };
          storageQuota.safeSetItem('alleflow_execution_trace', JSON.stringify(traceRecord));
        }
      } catch (e) { /* localStorage may be full or unavailable */ }
    }

    if ((event.type === 'node.silent_check.started' || event.type === 'node.connection_check.started') && event.nodeId) {
      setExecutionState(function(prev) {
        var nextMap = Object.assign({}, prev.silentCheckingByNodeId || {});
        nextMap[String(event.nodeId)] = { text: '' };
        return Object.assign({}, prev, { silentCheckingByNodeId: nextMap });
      });
      return;
    }
    if (event.type === 'node.silent_check.progress' && event.nodeId) {
      setExecutionState(function(prev) {
        var nextMap = Object.assign({}, prev.silentCheckingByNodeId || {});
        // Only update if the check is still active — guards against a late
        // progress event arriving after 'ended' already cleared the entry.
        if (nextMap[String(event.nodeId)]) {
          nextMap[String(event.nodeId)] = { text: String(event.text || '') };
        }
        return Object.assign({}, prev, { silentCheckingByNodeId: nextMap });
      });
      return;
    }
    if ((event.type === 'node.silent_check.ended' || event.type === 'node.connection_check.ended') && event.nodeId) {
      setExecutionState(function(prev) {
        var nextMap = Object.assign({}, prev.silentCheckingByNodeId || {});
        delete nextMap[String(event.nodeId)];
        return Object.assign({}, prev, { silentCheckingByNodeId: nextMap });
      });
      return;
    }
    if (event.type === 'node.silent_check.skipped' && event.nodeId) {
      if (typeof console !== 'undefined' && console.info) {
        console.info('[SilentCheck] skipped', event.nodeId, 'reason=' + (event.reason || '?'), 'icon=' + (event.icon || ''));
      }
      var skippedReason = String(event.reason || '');
      // Don't chip when the whole feature is off — that would show a chip on
      // every node and be pure noise. Chip only for cases the user would
      // actually want to know about (node bypassed despite the toggle).
      if (skippedReason === 'toggle_off') return;
      // Same logic for node types that are intrinsically skipped (Start,
      // Console Log, Computer Use) — they never audit by design, so the chip
      // just delays an obviously "run immediately" node.
      if (skippedReason.indexOf('icon_skip:') === 0) return;
      var skippedNodeId = String(event.nodeId);
      var skippedDetail = String(event.detail || '');
      setExecutionState(function(prev) {
        var nextMap = Object.assign({}, prev.silentCheckSkippedByNodeId || {});
        nextMap[skippedNodeId] = { reason: skippedReason, detail: skippedDetail };
        return Object.assign({}, prev, { silentCheckSkippedByNodeId: nextMap });
      });
      // Chip stays until the next execution starts (cleared by the
      // execution.started handler), so the user has time to see it
      // regardless of how fast the node completes.
      return;
    }
    if (event.type === 'node.connection_info' && event.nodeId) {
      if (typeof console !== 'undefined' && console.info) {
        console.info('[ConnectionInfo]', event.nodeId, event.message);
      }
      showConnectionInfoPopup(event.nodeId, event.message || '');
      return;
    }
    if (event.type === 'node.silent_error' && event.nodeId) {
      setExecutionState(function(prev) {
        var nextMap = Object.assign({}, prev.silentErrorByNodeId || {});
        nextMap[String(event.nodeId)] = {
          severity: event.severity || 'warning',
          category: event.category || 'other',
          message: event.message || ''
        };
        return Object.assign({}, prev, { silentErrorByNodeId: nextMap });
      });
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[SilentError]', event.nodeId, event.category, event.severity, event.message);
      }
      handleSilentErrorFlag(event.nodeId, event.category || 'other', event.message || '');
      return;
    }
    if (event.type === 'node.completed' && event.nodeId) {
      try {
        var wfIdForSave = selectedWorkflowIdRef.current
          || selectedWorkflowId
          || (workflowStateRef.current && workflowStateRef.current.id)
          || '';
        if (wfIdForSave) {
          var outToStore = event.output !== undefined ? event.output : (event.outputPreview !== undefined && event.outputPreview !== null ? String(event.outputPreview) : '');
          workflowProgress.recordNodeOutput(wfIdForSave, event.nodeId, outToStore);
        }
      } catch (e) {}
      // Persist a depth-bounded type-tagged shape on the node itself so the
      // variable picker can show field structure on next page load even if
      // localStorage is cleared. Only writes when the server provided
      // outputShape — never fabricates one client-side.
      if (event.outputShape !== undefined && event.outputShape !== null) {
        // Shrink before persisting: the picker only needs structure, and the
        // raw server shape (full arrays, 8k strings) bloated every Save /
        // Publish payload. null = too big even shrunk → skip persisting.
        var persistableShape = shrinkShapeForPersistence(event.outputShape);
        if (persistableShape !== null) {
          var lastOutputEntry = {
            shape: persistableShape,
            at: new Date().toISOString(),
            runId: event.executionId || event.runId || null
          };
          setWorkflowState(function(prev) {
            var nextState = applyNodeLastOutput(prev, event.nodeId, lastOutputEntry);
            if (nextState !== prev) workflowStateRef.current = nextState;
            return nextState;
          });
        }
      }
      var completedExecutionNode = (workflowStateRef.current && Array.isArray(workflowStateRef.current.nodes))
        ? workflowStateRef.current.nodes.find(function(node) { return node && String(node.id) === String(event.nodeId); })
        : null;
      if (completedExecutionNode && completedExecutionNode.icon === 'set-variable') {
        var retainedVarName = String((completedExecutionNode.paramValues && completedExecutionNode.paramValues.variable) || '').trim();
        if (retainedVarName) {
          setWorkflowState(function(prev) {
            var nextState = applyStaticVariableValue(prev, retainedVarName, event.output);
            if (nextState !== prev) workflowStateRef.current = nextState;
            return nextState;
          });
        }
      }
    }
    if (event.type === 'variables.updated' && event.updates && typeof event.updates === 'object') {
      var updatedNames = Object.keys(event.updates);
      if (updatedNames.length) {
        setWorkflowState(function(prev) {
          var nextState = prev;
          for (var i = 0; i < updatedNames.length; i += 1) {
            nextState = applyStaticVariableValue(nextState, updatedNames[i], event.updates[updatedNames[i]]);
          }
          if (nextState !== prev) workflowStateRef.current = nextState;
          return nextState;
        });
        try {
          var wfIdForVarSave = selectedWorkflowIdRef.current
            || selectedWorkflowId
            || (workflowStateRef.current && workflowStateRef.current.id)
            || '';
          if (wfIdForVarSave) {
            for (var vpi = 0; vpi < updatedNames.length; vpi += 1) {
              workflowProgress.recordVariableValue(wfIdForVarSave, updatedNames[vpi], event.updates[updatedNames[vpi]]);
            }
          }
        } catch (e) {}
      }
    }

    setExecutionState(function(prev) {
      var nextNodeStatus = Object.assign({}, prev.nodeStatusById);
      var nextNodeFix = Object.assign({}, prev.nodeFixById);
      var nextNodeTiming = Object.assign({}, prev.nodeTimingById);
      var nextNodeTokens = Object.assign({}, prev.nodeTokensById);
      var nextTraversed = Object.assign({}, prev.traversedConnectionIds);
      var nextInputById = Object.assign({}, prev.nodeInputById);
      var nextOutputById = Object.assign({}, prev.nodeOutputById);
      var nextVariables = Object.assign({}, prev.variableValuesByName);
      var nextState = Object.assign({}, prev);
      var eventTime = event.timestamp ? Date.parse(event.timestamp) : Date.now();
      if (!Number.isFinite(eventTime)) eventTime = Date.now();

      if (event.type === 'execution.started') {
        var preserve = preservePriorExecStateRef.current;
        preservePriorExecStateRef.current = false;
        console.log('[AutoRunNewNode] execution.started event received. preserve=', preserve, 'prior nodeStatusById keys:', Object.keys(prev.nodeStatusById || {}));
        nextState.status = 'running';
        nextState.executionId = executionIdOverride || prev.executionId || null;
        nextState.activeNodeId = null;
        nextState.error = '';
        if (!preserve) {
          nextState.nodeStatusById = {};
          nextState.nodeFixById = {};
          nextState.nodeTimingById = {};
          nextState.nodeTokensById = {};
          nextState.traversedConnectionIds = {};
          nextState.nodeInputById = {};
          nextState.nodeOutputById = {};
          nextState.nodeCallCountById = {};
          nextState.nodeVariableSnapshotById = {};
          var seedVars = priorRerunVariablesRef.current;
          priorRerunVariablesRef.current = null;
          nextState.variableValuesByName = seedVars ? Object.assign({}, seedVars) : {};
          nextState.accessedVariableNames = {};
          nextState.accessedVariableNodeId = {};
        } else {
          priorRerunVariablesRef.current = null;
        }
        return nextState;
      }

      if (event.type === 'node.input') {
        nextInputById[event.nodeId] = event.input;
        nextState.nodeInputById = nextInputById;
        return nextState;
      }

      if (event.type === 'node.started') {
        nextNodeStatus[event.nodeId] = 'running';
        nextNodeTiming[event.nodeId] = { startedAt: eventTime, endedAt: null, durationMs: null };
        if (!(nextNodeFix[event.nodeId] && nextNodeFix[event.nodeId].active)) {
          delete nextNodeFix[event.nodeId];
        }
        var nextCallCount = Object.assign({}, prev.nodeCallCountById);
        nextCallCount[event.nodeId] = (nextCallCount[event.nodeId] || 0) + 1;
        var nextVarSnapshot = Object.assign({}, prev.nodeVariableSnapshotById);
        nextVarSnapshot[event.nodeId] = Object.assign({}, prev.variableValuesByName);
        nextState.activeNodeId = event.nodeId;
        nextState.nodeStatusById = nextNodeStatus;
        nextState.nodeFixById = nextNodeFix;
        nextState.nodeTimingById = nextNodeTiming;
        nextState.nodeCallCountById = nextCallCount;
        nextState.nodeVariableSnapshotById = nextVarSnapshot;
        return nextState;
      }

      if (event.type === 'node.completed') {
        // A completed event can still carry `error` — this happens for a tool
        // node that threw inside the Tool Agent loop. We flag the node red (status
        // 'failed') so the failure is visible, but we DON'T touch run-level state
        // (nextState.error) or emit node.failed, because the agent loop continues
        // and may recover — the run as a whole is not failed.
        var __completedWithError = !!(event.error || (event.output && typeof event.output === 'object' && event.output.__error === true));
        nextNodeStatus[event.nodeId] = __completedWithError ? 'failed' : 'completed';
        var prevTiming = nextNodeTiming[event.nodeId] || {};
        var startedAt = Number(prevTiming.startedAt);
        if (!Number.isFinite(startedAt)) startedAt = eventTime;
        nextNodeTiming[event.nodeId] = { startedAt: startedAt, endedAt: eventTime, durationMs: Math.max(0, eventTime - startedAt) };
        // Agent nodes report the tokens they spent on their runtime chat() calls.
        // Other node types send no `tokens`, so no badge is stored for them.
        if (event.tokens && ((event.tokens.in || 0) > 0 || (event.tokens.out || 0) > 0)) {
          nextNodeTokens[event.nodeId] = { in: event.tokens.in || 0, out: event.tokens.out || 0 };
        }
        var __storedOut = __completedWithError
          ? { __error: true, message: event.error || ((event.output && event.output.message) || 'Tool call failed.') }
          : (event.output !== undefined ? event.output : (event.outputPreview !== undefined && event.outputPreview !== null ? String(event.outputPreview) : ''));
        console.log('[OUTPUT-DBG] node.completed nodeId=' + event.nodeId +
          ' eventOutputType=' + (event.output === null ? 'null' : Array.isArray(event.output) ? 'array' : typeof event.output) +
          ' eventPreviewType=' + typeof event.outputPreview +
          ' storedType=' + (__storedOut === null ? 'null' : Array.isArray(__storedOut) ? 'array' : typeof __storedOut) +
          ' storedSample=' + (typeof __storedOut === 'string' ? __storedOut.slice(0, 120) : JSON.stringify(__storedOut).slice(0, 120)));
        nextOutputById[event.nodeId] = __storedOut;
        var completedNode = (workflowStateRef.current && Array.isArray(workflowStateRef.current.nodes))
          ? workflowStateRef.current.nodes.find(function(n) { return n && String(n.id) === String(event.nodeId); })
          : null;
        if (completedNode && completedNode.icon === 'variables') {
          var variableName = String(completedNode.title || '').trim();
          if (variableName) nextVariables[variableName] = event.output;
        } else if (completedNode && completedNode.icon === 'set-variable') {
          var targetVarName = String((completedNode.paramValues && completedNode.paramValues.variable) || '').trim();
          if (targetVarName) nextVariables[targetVarName] = event.output;
        }
        if (!__completedWithError && nextNodeFix[event.nodeId] && nextNodeFix[event.nodeId].active) {
          nextNodeFix[event.nodeId] = { active: false, succeeded: true, phase: 'node.fix.succeeded', attempt: nextNodeFix[event.nodeId].attempt || 1 };
          var fixedNodeId = event.nodeId;
          setTimeout(function() {
            setExecutionState(function(p) {
              var nf = Object.assign({}, p.nodeFixById);
              if (nf[fixedNodeId] && nf[fixedNodeId].succeeded) delete nf[fixedNodeId];
              return Object.assign({}, p, { nodeFixById: nf });
            });
          }, 3200);
        } else {
          delete nextNodeFix[event.nodeId];
        }
        var completedConns = workflowStateRef.current && Array.isArray(workflowStateRef.current.connections) ? workflowStateRef.current.connections : [];
        completedConns.forEach(function(conn) {
          if (conn && String(conn.to) === String(event.nodeId)) delete nextTraversed[conn.id];
        });
        nextState.nodeStatusById = nextNodeStatus;
        nextState.nodeFixById = nextNodeFix;
        nextState.nodeTimingById = nextNodeTiming;
        nextState.nodeTokensById = nextNodeTokens;
        nextState.nodeInputById = nextInputById;
        nextState.nodeOutputById = nextOutputById;
        nextState.variableValuesByName = nextVariables;
        nextState.traversedConnectionIds = nextTraversed;
        if (nextState.activeNodeId === event.nodeId) nextState.activeNodeId = null;
        return nextState;
      }

      if (event.type === 'variables.accessed' && Array.isArray(event.names) && event.names.length) {
        var nextAccessed = Object.assign({}, prev.accessedVariableNames);
        var nextAccessedNodeId = Object.assign({}, prev.accessedVariableNodeId);
        event.names.forEach(function(name) {
          nextAccessed[name] = true;
          nextAccessedNodeId[name] = event.nodeId;
        });
        nextState.accessedVariableNames = nextAccessed;
        nextState.accessedVariableNodeId = nextAccessedNodeId;
        return nextState;
      }

      if (event.type === 'variables.updated' && event.updates && typeof event.updates === 'object') {
        var updateNames = Object.keys(event.updates);
        if (!updateNames.length) return nextState;
        for (var vi = 0; vi < updateNames.length; vi += 1) {
          nextVariables[updateNames[vi]] = event.updates[updateNames[vi]];
        }
        nextState.variableValuesByName = nextVariables;
        return nextState;
      }

      if (event.type === 'node.failed') {
        if (event.nodeId) lastFailedNodeRef.current = { nodeId: String(event.nodeId), error: event.error || 'Node execution failed.' };
        nextNodeStatus[event.nodeId] = 'failed';
        var failedTiming = nextNodeTiming[event.nodeId] || {};
        var failedStart = Number(failedTiming.startedAt);
        if (!Number.isFinite(failedStart)) failedStart = eventTime;
        nextNodeTiming[event.nodeId] = { startedAt: failedStart, endedAt: eventTime, durationMs: Math.max(0, eventTime - failedStart) };
        var failureMessage = event.error || 'Node execution failed.';
        var existingFixState = event.nodeId ? nextNodeFix[event.nodeId] : null;
        if (existingFixState) {
          nextNodeFix[event.nodeId] = Object.assign({}, existingFixState, { active: false });
          if (existingFixState.message) {
            failureMessage += '\n\nAuto-fix: ' + String(existingFixState.message);
          } else if (existingFixState.reason) {
            failureMessage += '\n\nAuto-fix reason: ' + String(existingFixState.reason);
          }
        }
        nextOutputById[event.nodeId] = {
          __error: true,
          message: failureMessage,
          fixState: existingFixState
            ? {
                attempt: existingFixState.attempt || 1,
                phase: existingFixState.phase || '',
                reason: existingFixState.reason || '',
                message: existingFixState.message || ''
              }
            : null
        };
        var failedConns = workflowStateRef.current && Array.isArray(workflowStateRef.current.connections) ? workflowStateRef.current.connections : [];
        failedConns.forEach(function(conn) {
          if (conn && String(conn.to) === String(event.nodeId)) delete nextTraversed[conn.id];
        });
        nextState.error = failureMessage;
        nextState.nodeStatusById = nextNodeStatus;
        nextState.nodeFixById = nextNodeFix;
        nextState.nodeTimingById = nextNodeTiming;
        nextState.nodeOutputById = nextOutputById;
        nextState.traversedConnectionIds = nextTraversed;
        nextState.activeNodeId = null;
        return nextState;
      }

      if (event.type === 'node.fix.progress') {
        if (event.nodeId && nextNodeFix[event.nodeId]) {
          nextNodeFix[event.nodeId] = Object.assign({}, nextNodeFix[event.nodeId], {
            progressPhase: event.phase || '',
            message: event.message || ''
          });
          nextState.nodeFixById = nextNodeFix;
        }
        return nextState;
      }

      if (event.type === 'node.fix.attempted' || event.type === 'node.fix.applied' || event.type === 'node.fix.retry' || event.type === 'node.fix.skipped') {
        if (event.nodeId) {
          nextNodeFix[event.nodeId] = {
            active: event.type !== 'node.fix.skipped',
            attempt: Number(event.attempt || (nextNodeFix[event.nodeId] && nextNodeFix[event.nodeId].attempt) || 1),
            phase: event.type,
            reason: event.reason ? String(event.reason) : '',
            message: event.message ? String(event.message) : ''
          };
          if (event.type === 'node.fix.attempted' && event.error) {
            nextOutputById[event.nodeId] = { __error: true, message: String(event.error) };
            nextState.nodeOutputById = nextOutputById;
          }
        }
        nextState.status = 'running';
        nextState.nodeFixById = nextNodeFix;
        return nextState;
      }

      if (event.type === 'connection.traversed') {
        nextTraversed[event.connectionId] = true;
        nextState.traversedConnectionIds = nextTraversed;
        return nextState;
      }

      if (event.type === 'execution.completed') {
        nextState.status = 'completed';
        nextState.activeNodeId = null;
        nextState.error = '';
        var survivingFix = {};
        Object.keys(nextNodeFix).forEach(function(nid) {
          if (nextNodeFix[nid] && nextNodeFix[nid].succeeded) survivingFix[nid] = nextNodeFix[nid];
        });
        nextState.nodeFixById = survivingFix;
        return nextState;
      }

      if (event.type === 'execution.failed') {
        nextState.status = 'failed';
        nextState.activeNodeId = null;
        nextState.error = event.error || 'Workflow execution failed.';
        nextState.nodeFixById = {};
        return nextState;
      }

      if (event.type === 'execution.stopped') {
        nextState.status = 'stopped';
        nextState.activeNodeId = null;
        nextState.error = '';
        nextState.nodeFixById = {};
        return nextState;
      }

      return prev;
    });

    // Remember why a server-side auto-fix gave up so we can explain it to the
    // user when the run ultimately fails. Recording it (rather than notifying
    // immediately) avoids a false "no change" notice when a later retry of the
    // same node actually succeeds.
    if (event.type === 'node.fix.skipped' && event.nodeId
        && (event.reason === 'no_fix' || event.reason === 'hyperparam_required' || event.reason === 'user_data_required')) {
      codefixSkipInfoRef.current[String(event.nodeId)] = {
        reason: String(event.reason),
        message: event.message ? String(event.message) : ''
      };
    }

    if (event.type === 'execution.completed') {
      setExecutionLogs(function(prev) {
        var endTime = Date.now();
        return prev.map(function(entry) {
          if (entry.executionId !== executionIdOverride) return entry;
          return Object.assign({}, entry, { status: 'completed', endedAt: endTime, durationMs: endTime - entry.startedAt });
        });
      });
    }

    if (event.type === 'execution.failed') {
      setExecutionLogs(function(prev) {
        var endTime = Date.now();
        return prev.map(function(entry) {
          if (entry.executionId !== executionIdOverride) return entry;
          return Object.assign({}, entry, { status: 'failed', endedAt: endTime, durationMs: endTime - entry.startedAt, error: event.error || 'Workflow execution failed.' });
        });
      });
      var failMsg = event.error || 'Workflow execution failed.';
      // Prefer routing the user to AskCode on the failing node so they can fix
      // it in-place. If autopilot has already exhausted its retries for this
      // node, handleAutoFixNode itself surfaces the failure popup. We only
      // fall back to the generic popup when no offending node is identifiable.
      var failedRef = lastFailedNodeRef.current;
      lastFailedNodeRef.current = null;
      var codefixSkip = (failedRef && failedRef.nodeId)
        ? codefixSkipInfoRef.current[String(failedRef.nodeId)]
        : null;
      if (codefixSkip) {
        // The auto-fix concluded it can't fix this in code (missing setting /
        // credential, or a cause upstream). Explain the cause and the next step
        // rather than dropping the user into the code editor.
        showCodefixSkippedNotice(failedRef.nodeId, codefixSkip.reason, codefixSkip.message);
      } else if (failedRef && failedRef.nodeId) {
        autoFixSnapshotRef.current = {
          nodeOutputById: Object.assign({}, executionState && executionState.nodeOutputById ? executionState.nodeOutputById : {}),
          variableValuesByName: Object.assign({}, executionState && executionState.variableValuesByName ? executionState.variableValuesByName : {})
        };
        handleAutoFixNode(failedRef.nodeId, failedRef.error || failMsg);
      } else {
        // No single node owned the failure (e.g. a run-level error). The server's
        // generic "stopped after node failure" line tells the user nothing, so we
        // only show the reason when it actually adds detail (rate limit, loop, …)
        // and always give plain, non-technical next steps instead.
        var GENERIC_FAIL_MESSAGES = ['workflow execution failed.', 'workflow execution failed', 'workflow execution stopped after node failure.', 'workflow execution stopped after node failure'];
        var reason = (GENERIC_FAIL_MESSAGES.indexOf(String(failMsg).trim().toLowerCase()) === -1) ? failMsg : '';
        var SF = RUN_NOTICE_STYLE;
        setRuntimePopup({
          boxStyle: { width: 520 },
          title: 'Your workflow couldn’t finish',
          message: buildRunFailureNotice({
            lead: 'One of your steps ran into a problem, so the run stopped before it could finish.',
            issueText: reason,
            stepsLabel: 'What to do next',
            steps: [
              ['Find the step ', h('span', { style: SF.mark }, 'marked in red'), ' and open it.'],
              ['Check its settings and fill in anything that’s ', h('span', { style: SF.mark }, 'empty or missing'), '.'],
              ['Then press ', h('span', { style: SF.mark }, 'Play'), ' to run your workflow again.']
            ]
          })
        });
      }
    }

    if (event.type === 'execution.stopped') {
      setExecutionLogs(function(prev) {
        var endTime = Date.now();
        return prev.map(function(entry) {
          if (entry.executionId !== executionIdOverride) return entry;
          return Object.assign({}, entry, { status: 'stopped', endedAt: endTime, durationMs: endTime - entry.startedAt });
        });
      });
    }

    if (event.type === 'execution.paused') {
      // Manual pause keeps the execution alive — the wave loop is just parked
      // between waves, waiting for resume. Silent-error pause is terminal:
      // the run is already aborted server-side, so we mark it stopped.
      var isManualPause = event.reason === 'manual';
      setExecutionState(function(prev) {
        if (isManualPause) {
          return Object.assign({}, prev, { status: 'paused' });
        }
        return Object.assign({}, prev, {
          status: 'stopped',
          activeNodeId: null,
          error: event.message ? ('Paused: ' + event.message) : 'Paused by silent-error check.'
        });
      });
      if (!isManualPause && typeof console !== 'undefined' && console.warn) {
        console.warn('[SilentErrorPause]', event.nodeId, event.severity, event.message);
      }
    }

    if (event.type === 'execution.resumed') {
      setExecutionState(function(prev) {
        if (prev.status !== 'paused') return prev;
        return Object.assign({}, prev, { status: 'running' });
      });
    }

    if (event.type === 'node.fix.applied' && event.nodeId && typeof event.fixedCode === 'string') {
      setWorkflowState(function(prev) {
        return Object.assign({}, prev, {
          nodes: (prev.nodes || []).map(function(node) {
            if (!node || String(node.id) !== String(event.nodeId)) return node;
            return Object.assign({}, node, { code: event.fixedCode });
          })
        });
      });
    }

    // Runtime edge auto-fix: a Set Variable received a wrong-shaped object and
    // the engine selected its single matching field. Persist that selection as
    // the edge's propPath — same result as dragging from that field's output
    // row — so future runs receive the field directly.
    if (event.type === 'connection.autofix' && event.connectionId && typeof event.propPath === 'string' && event.propPath) {
      setWorkflowState(function(prev) {
        return Object.assign({}, prev, {
          connections: (prev.connections || []).map(function(conn) {
            if (!conn || String(conn.id) !== String(event.connectionId)) return conn;
            return Object.assign({}, conn, { propPath: event.propPath });
          })
        });
      });
    }
  }

  function handleExecutionEvent(event, executionIdOverride, options) {
    var opts = options || {};
    if (!event || !event.type) return;

    // Mirror execution to the live Remote Support session — whoever's client
    // is driving the run, every other member sees the same statuses, badges,
    // outputs and run log. Events that themselves arrived from the session
    // are not forwarded back (applyingRemoteExecRef).
    if (!applyingRemoteExecRef.current && collabActiveRef.current && collabClientRef.current) {
      collabClientRef.current.sendPeerEvent('exec_event', {
        event: event,
        executionId: executionIdOverride || null
      });
    }

    // Live Run Log: a fresh run starts a new list; every other event appends a
    // line (when runLogLineFromEvent surfaces one).
    if (event.type === 'execution.started') {
      runLogSeqRef.current = 0;
      runLogInputByNodeRef.current = {};
      setRunLogLines([]);
      // Collapse the panel for the new run; it's hidden while running and
      // reappears (collapsed) once this run finishes.
      setRunLogOpen(false);
    }
    // node.input arrives just before the node runs — stash it so we can show it
    // alongside the output on the matching node.completed line.
    if (event.type === 'node.input' && event.nodeId) {
      runLogInputByNodeRef.current[String(event.nodeId)] = event.input;
    }
    var logLine = runLogLineFromEvent(event, workflowStateRef.current && workflowStateRef.current.nodes);
    if (logLine) {
      // For completed/failed lines, attach the stashed input + client-computed
      // duration (the server's completion event carries neither).
      if ((event.type === 'node.completed' || event.type === 'node.failed') && event.nodeId) {
        var nKey = String(event.nodeId);
        if (logLine.input === undefined && runLogInputByNodeRef.current[nKey] !== undefined) {
          logLine.input = runLogInputByNodeRef.current[nKey];
        }
        if (logLine.durationMs == null) {
          var startedMs = nodeRunStartClientTimeRef.current[nKey];
          if (startedMs) {
            var dms = Date.now() - startedMs;
            logLine.durationMs = dms;
            logLine.text = logLine.text + ' (' + formatRunLogDuration(dms) + ')';
          }
        }
      }
      var lineId = ++runLogSeqRef.current;
      setRunLogLines(function(prev) {
        return prev.concat([{
          id: lineId,
          tone: logLine.tone,
          text: logLine.text,
          nodeId: logLine.nodeId || null,
          durationMs: logLine.durationMs,
          input: logLine.input,
          output: logLine.output
        }]);
      });
    }

    if ((event.type === 'execution.completed' || event.type === 'execution.failed' || event.type === 'execution.stopped') && opts.closeOnTerminal !== false) {
      closeExecutionStream();
    }

    if (event.type === 'execution.started') {
      nodeRunStartClientTimeRef.current = {};
    }

    // Track client-side start time so we can enforce a minimum glow duration
    if (event.type === 'node.started' && event.nodeId) {
      nodeRunStartClientTimeRef.current[event.nodeId] = Date.now();
    }

    // Delay node.completed if the running glow hasn't been visible long enough.
    // This happens when events are replayed all at once to a late subscriber.
    if (event.type === 'node.completed' && event.nodeId) {
      var startedClientMs = nodeRunStartClientTimeRef.current[event.nodeId] || 0;
      var elapsed = startedClientMs > 0 ? Date.now() - startedClientMs : 9999;
      var MIN_GLOW_MS = 420;
      var remaining = MIN_GLOW_MS - elapsed;
      if (remaining > 20) {
        setTimeout(function() { applyExecutionEvent(event, executionIdOverride); }, remaining);
        return;
      }
    }

    applyExecutionEvent(event, executionIdOverride);
  }

  function startPublicEventStreamListening(streamCode) {
    var nextCode = String(streamCode || '').trim() || createPublicStreamCode();
    var workflowId = String(selectedWorkflowId || '').trim();
    if (!workflowId) {
      setPublicListenState(createPublicListenState({ error: 'Workflow not found.' }));
      return Promise.reject(new Error('Workflow not found.'));
    }

    logPublicListenDebug('listener.start', {
      workflowId: workflowId,
      streamCode: nextCode,
      sessionId: publicListenerSessionIdRef.current
    });
    return acquireWorkflowPublicListener(workflowId, nextCode, publicListenerSessionIdRef.current)
      .then(function() {
        logPublicListenDebug('listener.acquire.success', {
          workflowId: workflowId,
          streamCode: nextCode
        });
        closeExecutionStream();
        stopPublicListenerHeartbeat();
        closePublicExecutionStream();
        setExecutionState(createExecutionState());
        publicSeenEventKeysRef.current = {};
        setPublicListenState(createPublicListenState({
          enabled: true,
          code: nextCode,
          connectionStatus: 'connecting'
        }));

        publicListenerHeartbeatRef.current = setInterval(function() {
          logPublicListenDebug('listener.heartbeat.tick', {
            workflowId: workflowId,
            sessionId: publicListenerSessionIdRef.current
          });
          heartbeatWorkflowPublicListener(workflowId, publicListenerSessionIdRef.current).catch(function(error) {
            logPublicListenDebug('listener.heartbeat.error', {
              workflowId: workflowId,
              message: error && error.message ? error.message : 'Listener lease expired.'
            });
            stopPublicListenerHeartbeat();
            closePublicExecutionStream();
            setPublicListenState(createPublicListenState({
              error: error && error.message ? error.message : 'Listener lease expired.',
              connectionStatus: 'disconnected'
            }));
          });
        }, PUBLIC_LISTENER_HEARTBEAT_MS);

        publicExecutionStreamCloseRef.current = subscribePublicWorkflowExecution(nextCode, {
          onOpen: function() {
            logPublicListenDebug('listener.stream.open', {
              workflowId: workflowId,
              streamCode: nextCode
            });
            setPublicListenState(function(prev) {
              if (!prev.enabled) return prev;
              return Object.assign({}, prev, {
                connectionStatus: 'connected',
                error: ''
              });
            });
          },
          onEvent: function(event) {
            var executionId = event && event.executionId ? String(event.executionId) : nextCode;
            logPublicListenDebug('listener.stream.event', {
              workflowId: workflowId,
              streamCode: nextCode,
              type: event && event.type ? event.type : 'unknown',
              executionId: executionId,
              eventId: event && event.id !== undefined ? event.id : null
            });
            var eventKey = event && event.id !== undefined ? (executionId + ':' + String(event.id)) : '';
            if (eventKey && publicSeenEventKeysRef.current[eventKey]) {
              logPublicListenDebug('listener.stream.event.skipped_duplicate', {
                workflowId: workflowId,
                streamCode: nextCode,
                eventKey: eventKey
              });
              return;
            }
            if (eventKey) {
              publicSeenEventKeysRef.current[eventKey] = true;
            }
            if (event && event.type === 'execution.started') {
              var logId = 'public-' + executionId;
              var logWorkflowName = workflowStateRef.current && workflowStateRef.current.workflowName
                ? String(workflowStateRef.current.workflowName)
                : 'Untitled Workflow';
              setExecutionLogs(function(prev) {
                return [{
                  id: logId,
                  executionId: executionId,
                  workflowName: logWorkflowName,
                  startedAt: Date.now(),
                  status: 'running',
                  endedAt: null,
                  durationMs: null,
                  error: ''
                }].concat(prev);
              });
            }
            setPublicListenState(function(prev) {
              if (!prev.enabled) return prev;
              return Object.assign({}, prev, {
                connectionStatus: 'connected',
                error: '',
                lastEventAt: new Date().toISOString()
              });
            });
            handleExecutionEvent(event, executionId, { closeOnTerminal: false });
          },
          onError: function() {
            logPublicListenDebug('listener.stream.error', {
              workflowId: workflowId,
              streamCode: nextCode
            });
            setPublicListenState(function(prev) {
              if (!prev.enabled) return prev;
              return Object.assign({}, prev, {
                connectionStatus: 'disconnected',
                error: 'Listener disconnected. Waiting to reconnect.'
              });
            });
          }
        });
      })
      .catch(function(error) {
        logPublicListenDebug('listener.start.error', {
          workflowId: workflowId,
          streamCode: nextCode,
          message: error && error.message ? error.message : 'Could not enable listener.'
        });
        stopPublicListenerHeartbeat();
        closePublicExecutionStream();
        setPublicListenState(createPublicListenState({
          error: error && error.message ? error.message : 'Could not enable listener.',
          connectionStatus: 'disconnected'
        }));
        throw error;
      });
  }

  // manualCode comes from the Listen modal textbox; callers that auto-enable
  // listening (UI tab) pass nothing and fall back to the existing/generated code.
  function handlePublicEventStreamToggle(nextEnabled, manualCode) {
    if (!nextEnabled) {
      stopPublicListenerHeartbeat();
      releasePublicListenerLease(selectedWorkflowId);
      closePublicExecutionStream();
      setPublicListenState(createPublicListenState());
      publicListenerAutoResumeRef.current = '';
      return;
    }

    var streamCode = String(manualCode || '').trim()
      || (publicListenState && publicListenState.code ? String(publicListenState.code) : '')
      || createPublicStreamCode();

    startPublicEventStreamListening(streamCode).catch(function() {});
  }

  function handleApplyPublicEventStreamCode(code) {
    var streamCode = String(code || '').trim();
    if (!streamCode) return;
    startPublicEventStreamListening(streamCode).catch(function() {});
  }

  // Auto-start listening when the user switches to the UI tab so that the
  // embedded iframe can trigger workflows and receive results immediately.
  // When switching back to 'workflow', auto-stop only if we auto-started it
  // (don't kill a manually-started listen session).
  useEffect(function() {
    if (canvasViewMode === 'ui') {
      if (!publicListenEnabledRef.current) {
        uiViewAutoListenRef.current = true;
        var code = (publicListenState && publicListenState.code)
          ? String(publicListenState.code)
          : createPublicStreamCode();
        startPublicEventStreamListening(code).catch(function() {});
      }
    } else {
      if (uiViewAutoListenRef.current && publicListenEnabledRef.current) {
        uiViewAutoListenRef.current = false;
        stopPublicListenerHeartbeat();
        releasePublicListenerLease(selectedWorkflowId);
        closePublicExecutionStream();
        setPublicListenState(createPublicListenState());
        publicListenerAutoResumeRef.current = '';
      }
      uiViewAutoListenRef.current = false;
    }
  }, [canvasViewMode]);

  function startExecutionWithState(nextWorkflowState, rerunOpts) {
    var stateToRun = nextWorkflowState || workflowStateRef.current;
    var opts = rerunOpts || {};
    var logId = Date.now() + '-' + Math.random().toString(36).substr(2, 6);
    var logStartTime = Date.now();
    var logWorkflowName = stateToRun && stateToRun.workflowName ? String(stateToRun.workflowName) : 'Untitled Workflow';
    setExecutionLogs(function(prev) {
      return [{ id: logId, executionId: null, workflowName: logWorkflowName, startedAt: logStartTime, status: 'running', endedAt: null, durationMs: null, error: '' }].concat(prev);
    });
    closeExecutionStream();
    // Claim this run. Any in-flight start with an older token is now stale and
    // will refuse to open its SSE when its start POST resolves.
    var myStartToken = ++executionStartTokenRef.current;
    priorRerunVariablesRef.current = (opts.priorVariableValuesByName && typeof opts.priorVariableValuesByName === 'object') ? Object.assign({}, opts.priorVariableValuesByName) : null;
    executionFromNodeIdRef.current = opts.fromNodeId || null;
    preservePriorExecStateRef.current = !!opts.preservePriorState;
    if (opts.preservePriorState) {
      console.log('[AutoRunNewNode] preserving prior exec state. fromNodeId:', opts.fromNodeId, 'priorNodeOutputByName keys:', Object.keys(opts.priorNodeOutputByName || {}));
      setExecutionState(function(prev) {
        console.log('[AutoRunNewNode] prior nodeStatusById keys:', Object.keys(prev.nodeStatusById || {}));
        return Object.assign({}, prev, {
          status: 'running',
          executionId: null,
          activeNodeId: null,
          error: ''
        });
      });
    } else {
      setExecutionState(createExecutionState({ status: 'running' }));
    }

    // Optimistic UI: light up the Start node immediately so the user sees
    // feedback before SSE connects (live latency makes the real node.started
    // event arrive 200–800ms late). The real event later is a no-op because
    // the node will already be marked running.
    var optimisticStartNode = findStartNodeForOptimisticGlow(stateToRun, opts.fromNodeId);
    if (optimisticStartNode) {
      setExecutionState(function(prev) {
        if (prev.status !== 'running') return prev;
        var nextStatus = Object.assign({}, prev.nodeStatusById);
        nextStatus[optimisticStartNode] = 'running';
        var nextTiming = Object.assign({}, prev.nodeTimingById);
        nextTiming[optimisticStartNode] = { startedAt: Date.now(), endedAt: null, durationMs: null };
        return Object.assign({}, prev, {
          activeNodeId: optimisticStartNode,
          nodeStatusById: nextStatus,
          nodeTimingById: nextTiming
        });
      });
      nodeRunStartClientTimeRef.current[optimisticStartNode] = Date.now();
    }

    var startArgs = {
      workflowId: selectedWorkflowId || '',
      fromNodeId: opts.fromNodeId || null,
      priorNodeOutputByName: opts.priorNodeOutputByName || null,
      priorVariableValuesByName: opts.priorVariableValuesByName || null,
      // Force-on when the caller explicitly requested it (e.g. the re-run
      // after a /codefix autopilot fix — we always want to verify the fix
      // and keep checking every downstream node too). Live mode runs the
      // published snapshot and ALWAYS keeps the silent-error checker OFF — the
      // staging toggle must never leak into a Live run.
      checkSilentError: workflowModeRef.current === 'live'
        ? false
        : (opts.forceCheckSilentError === true ? true : !!checkSilentErrorRef.current),
      // Hard kill-switch for the checker on Live runs — overrides even the
      // server-side autofix-recovery path that would otherwise re-enable it.
      disableSilentCheck: workflowModeRef.current === 'live',
      skipSilentCheckNodeIds: Array.isArray(opts.skipSilentCheckNodeIds) ? opts.skipSilentCheckNodeIds : null
    };

    // Auto-retry the start ONCE on a transient network failure before giving up
    // (a real failure surfaces the Rerun popup in .catch). Skip the retry if the
    // run was superseded meanwhile (Stop pressed / newer run claimed the token).
    function attemptStart(retriesLeft) {
      return startWorkflowExecution(stateToRun, startArgs).catch(function(err) {
        var superseded = myStartToken !== executionStartTokenRef.current;
        if (!superseded && retriesLeft > 0 && isTransientStartError(err)) {
          return new Promise(function(r) { setTimeout(r, 800); }).then(function() {
            return attemptStart(retriesLeft - 1);
          });
        }
        throw err;
      });
    }

    return attemptStart(1).then(function(result) {
      var executionId = result && result.executionId ? result.executionId : null;
      if (!executionId) throw new Error('Execution id is missing.');
      // Superseded while the start POST was in flight (Stop pressed, or a newer
      // run claimed the token). Don't open an orphan SSE — tell the server to
      // stop this execution and bail. The 'stopped' state set by handleStopWorkflow
      // (or the newer run's own state) stands.
      if (myStartToken !== executionStartTokenRef.current) {
        stopWorkflowExecution(executionId).catch(function() {});
        return { executionId: executionId, superseded: true };
      }
      setExecutionState(function(prev) {
        return Object.assign({}, prev, { executionId: executionId });
      });
      setExecutionLogs(function(prev) {
        return prev.map(function(entry) {
          if (entry.id !== logId) return entry;
          return Object.assign({}, entry, { executionId: executionId });
        });
      });
      executionStreamCloseRef.current = subscribeWorkflowExecution(executionId, {
        onEvent: function(event) { handleExecutionEvent(event, executionId); },
        onError: function() {
          setExecutionState(function(prev) {
            if (prev.status !== 'running') return prev;
            return Object.assign({}, prev, {
              status: 'failed',
              activeNodeId: null,
              error: 'Execution stream disconnected.'
            });
          });
          setExecutionLogs(function(prev) {
            var endTime = Date.now();
            return prev.map(function(entry) {
              if (entry.executionId !== executionId) return entry;
              return Object.assign({}, entry, { status: 'failed', endedAt: endTime, durationMs: endTime - entry.startedAt, error: 'Stream disconnected.' });
            });
          });
        }
      });
      return { executionId: executionId };
    }).catch(function(error) {
      setExecutionState(createExecutionState({
        status: 'failed',
        error: error && error.message ? error.message : 'Could not start workflow execution.'
      }));
      setExecutionLogs(function(prev) {
        var endTime = Date.now();
        return prev.map(function(entry) {
          if (entry.id !== logId) return entry;
          return Object.assign({}, entry, { status: 'failed', endedAt: endTime, durationMs: endTime - entry.startedAt, error: error && error.message ? error.message : 'Could not start.' });
        });
      });
      // Payload-too-large can't be fixed by rerunning — show its own notice.
      if (isPayloadTooLargeError(error)) {
        setRuntimePopup({
          title: 'Variable too big',
          message: 'One of the runtime variables is too big to process. Reduce the variable size and try again.'
        });
        return;
      }
      // Transient failure survived the auto-retry (or a server error) — offer a
      // one-click Rerun instead of making the user hunt for the Play button.
      setRuntimePopup({
        title: 'Couldn\'t start the workflow',
        message: 'The run didn\'t start — usually a brief connection issue. We already retried once automatically.',
        confirmLabel: 'Rerun',
        cancelLabel: 'Close',
        hideCancel: false,
        onConfirm: function() { startExecutionWithState(nextWorkflowState, rerunOpts); }
      });
    });
  }

  // Doctor-tool entry point: kick off a fresh full-workflow run and resolve
  // when execution reaches a terminal status. The Doctor's runWorkflow client
  // tool awaits this so the agent can read live outputs after pressing Play.
  //
  // Implementation note: SSE updates land in executionStateRef. We poll every
  // 250ms (cheap, no listener plumbing) and resolve when status flips to
  // 'completed' / 'failed' / 'stopped'. Hard timeout at 10 min so a stuck run
  // can't pin the Doctor forever — caller surfaces a timeout error.
  function runWorkflowAndAwait() {
    return new Promise(function(resolve) {
      var stateToRun = workflowStateRef.current;
      var startedAt = Date.now();
      var TERMINAL = { completed: true, failed: true, stopped: true };
      var TIMEOUT_MS = 10 * 60 * 1000;

      // Fire the same path the Play button uses. startExecutionWithState
      // already handles SSE subscription, log entry creation, and per-node
      // status updates — we just need to know when it's done.
      try {
        startExecutionWithState(stateToRun);
      } catch (err) {
        return resolve({ status: 'failed', durationMs: 0, executionLog: null, error: (err && err.message) || String(err) });
      }

      var pollId = setInterval(function() {
        var s = executionStateRef.current;
        if (s && TERMINAL[s.status]) {
          clearInterval(pollId);
          resolve({
            status: s.status,
            durationMs: Date.now() - startedAt,
            // Caller (Doctor tool) summarizes per-node entries. Pass the
            // whole log object so trim/error-extract logic stays in one
            // place (doctorChatTools.summarizeRunResult).
            executionLog: s.log || null
          });
        } else if (Date.now() - startedAt > TIMEOUT_MS) {
          clearInterval(pollId);
          resolve({
            status: 'timeout',
            durationMs: Date.now() - startedAt,
            executionLog: (s && s.log) || null,
            error: 'Workflow run exceeded the 10-minute Doctor watcher timeout'
          });
        }
      }, 250);
    });
  }

  // Toggle manual pause/resume on the active execution. The actual status
  // flip happens when the server emits `execution.paused` / `execution.resumed`
  // back over SSE — we don't pre-flip locally so the UI stays truthful even
  // if the network call fails or the server has already moved on.
  function handlePauseWorkflow() {
    var execId = executionState && executionState.executionId;
    if (!execId) return;
    if (executionState.status === 'paused') {
      resumeWorkflowExecution(execId).catch(function() {});
    } else if (executionState.status === 'running') {
      pauseWorkflowExecution(execId).catch(function() {});
    }
  }

  function handleStopWorkflow() {
    // Invalidate any in-flight start so its async .then refuses to open an SSE
    // (and stops the server execution it created) instead of leaking a stream
    // after the user already hit Stop.
    executionStartTokenRef.current++;
    var execId = executionState && executionState.executionId;
    closeExecutionStream();
    if (execId) {
      stopWorkflowExecution(execId).catch(function() {});
    }
    setExecutionState(function(prev) {
      var nextNodeStatus = Object.assign({}, prev.nodeStatusById);
      var keys = Object.keys(nextNodeStatus);
      for (var i = 0; i < keys.length; i++) {
        if (nextNodeStatus[keys[i]] === 'running') delete nextNodeStatus[keys[i]];
      }
      return Object.assign({}, prev, {
        status: 'stopped',
        executionId: null,
        activeNodeId: null,
        nodeStatusById: nextNodeStatus,
        nodeFixById: {},
        silentCheckingByNodeId: {},
        silentCheckSkippedByNodeId: {}
      });
    });
    setExecutionLogs(function(prev) {
      var endTime = Date.now();
      return prev.map(function(entry) {
        if (entry.status !== 'running') return entry;
        return Object.assign({}, entry, { status: 'stopped', endedAt: endTime, durationMs: endTime - entry.startedAt });
      });
    });
  }

  function getTraceDataForNode(nodeId) {
    // Build prior node outputs and variables from the saved trace.
    // If a node appears multiple times (e.g. loops), use the latest occurrence.
    var trace = null;
    try { trace = JSON.parse(localStorage.getItem('alleflow_execution_trace') || 'null'); } catch (e) {}
    if (!trace || !Array.isArray(trace.steps) || !trace.steps.length) return null;
    // Find the latest step matching nodeId to get variables snapshot at that point
    var targetStepIndex = -1;
    for (var i = trace.steps.length - 1; i >= 0; i--) {
      if (trace.steps[i] && trace.steps[i].nodeId === nodeId) { targetStepIndex = i; break; }
    }
    // Build prior outputs from latest occurrence of each node before the target
    var priorOutputByName = {};
    var stateNodes = workflowStateRef.current && Array.isArray(workflowStateRef.current.nodes) ? workflowStateRef.current.nodes : [];
    var nodeNameById = {};
    stateNodes.forEach(function(n) { if (n && n.id) nodeNameById[String(n.id)] = String(n.title || '').trim(); });
    // Walk all steps up to (but not including) target, latest wins
    var limit = targetStepIndex >= 0 ? targetStepIndex : trace.steps.length;
    for (var j = 0; j < limit; j++) {
      var s = trace.steps[j];
      if (!s || !s.nodeId) continue;
      var name = s.nodeName || nodeNameById[s.nodeId] || '';
      if (name && s.nodeOutput !== undefined && !(s.nodeOutput && s.nodeOutput.__error)) {
        priorOutputByName[name] = s.nodeOutput;
      }
    }
    // Variables: fold per-step deltas (changedVariables) in order to rebuild
    // the snapshot right before the target node. Older persisted traces stored
    // a full cumulative snapshot per step (`variables`) — folding those in the
    // same loop yields the identical result.
    var variables = {};
    if (targetStepIndex >= 0) {
      var foldEnd = targetStepIndex === 0 ? 1 : targetStepIndex;
      for (var vk = 0; vk < foldEnd; vk++) {
        var stepVars = trace.steps[vk] && (trace.steps[vk].changedVariables || trace.steps[vk].variables);
        if (stepVars) Object.assign(variables, stepVars);
      }
    }
    return { priorNodeOutputByName: priorOutputByName, priorVariableValuesByName: variables };
  }

  function handleRunNodeIsolated(nodeId) {
    var stateToRun = workflowStateRef.current;
    var traceData = getTraceDataForNode(nodeId);
    var priorNodeOutputByName = traceData ? traceData.priorNodeOutputByName : {};
    var priorVars = traceData ? traceData.priorVariableValuesByName : {};
    console.log('[RunNodeIsolated] nodeId:', nodeId);
    console.log('[RunNodeIsolated] priorNodeOutputByName:', priorNodeOutputByName);
    console.log('[RunNodeIsolated] priorVariableValuesByName:', priorVars);
    startExecutionWithState(stateToRun, {
      fromNodeId: nodeId,
      priorNodeOutputByName: Object.keys(priorNodeOutputByName).length ? priorNodeOutputByName : null,
      priorVariableValuesByName: Object.keys(priorVars).length ? priorVars : null
    }).catch(function() {});
  }

  function handleRerunFromNode(nodeId, snapshot) {
    var stateToRun = workflowStateRef.current;
    var priorNodeOutputByName = {};
    var currentNodeOutputById = (snapshot && snapshot.nodeOutputById)
      ? snapshot.nodeOutputById
      : (executionState && executionState.nodeOutputById ? executionState.nodeOutputById : {});
    if (stateToRun && Array.isArray(stateToRun.nodes)) {
      stateToRun.nodes.forEach(function(node) {
        if (!node || !node.id) return;
        var nid = String(node.id);
        if (Object.prototype.hasOwnProperty.call(currentNodeOutputById, nid)) {
          var title = String(node.title || '').trim();
          if (title) priorNodeOutputByName[title] = currentNodeOutputById[nid];
        }
      });
    }
    var currentVars = (snapshot && snapshot.variableValuesByName)
      ? snapshot.variableValuesByName
      : (executionState && executionState.variableValuesByName ? executionState.variableValuesByName : {});
    var priorVariableValuesByName = Object.assign({}, currentVars);
    var wfIdForResume = selectedWorkflowIdRef.current || selectedWorkflowId || (stateToRun && stateToRun.id) || '';
    if (wfIdForResume) {
      rehydrateExecutionStateFromStorage(wfIdForResume, stateToRun);
      try {
        var storageSnap = workflowProgress.buildResumeSnapshot(wfIdForResume, stateToRun, nodeId);
        if (typeof console !== 'undefined' && console.log) {
          console.log('[Resume] handleRerunFromNode', {
            nodeId: nodeId,
            wfId: wfIdForResume,
            inMemoryOutputKeys: Object.keys(priorNodeOutputByName),
            storageOutputKeys: Object.keys(storageSnap.priorNodeOutputByName)
          });
        }
        Object.keys(storageSnap.priorNodeOutputByName).forEach(function(k) {
          if (!Object.prototype.hasOwnProperty.call(priorNodeOutputByName, k)) {
            priorNodeOutputByName[k] = storageSnap.priorNodeOutputByName[k];
          }
        });
        Object.keys(storageSnap.priorVariableValuesByName).forEach(function(k) {
          if (!Object.prototype.hasOwnProperty.call(priorVariableValuesByName, k)) {
            priorVariableValuesByName[k] = storageSnap.priorVariableValuesByName[k];
          }
        });
      } catch (e) {}
    }
    // After a /codefix autopilot fix, re-run starting at the fixed node.
    // - skipSilentCheckNodeIds is empty: we WANT the silent-error checker to
    //   run on the freshly re-executed node so we can verify the fix worked.
    // - forceCheckSilentError=true: a fix can change output shape, so every
    //   downstream node in this re-run must also be audited — even if the
    //   user left the global "Check Silent Error" toggle off. If any node
    //   still looks wrong, autopilot kicks in again (bounded by
    //   AUTOFIX_MAX_ATTEMPTS in handleAutoFixNode).
    startExecutionWithState(stateToRun, {
      fromNodeId: nodeId,
      priorNodeOutputByName: Object.keys(priorNodeOutputByName).length ? priorNodeOutputByName : null,
      priorVariableValuesByName: Object.keys(priorVariableValuesByName).length ? priorVariableValuesByName : null,
      skipSilentCheckNodeIds: [],
      forceCheckSilentError: true,
      preservePriorState: true
    }).catch(function() {});
  }

  function handleAutoRunNewNode(nodeId, overrideState) {
    var stateToRun = overrideState || workflowStateRef.current;
    if (!stateToRun || !nodeId) return;
    var nodeExists = Array.isArray(stateToRun.nodes) && stateToRun.nodes.some(function(n) { return n && String(n.id) === String(nodeId); });
    if (!nodeExists) { console.warn('[AutoRunNewNode] node not in state', nodeId); return; }
    var priorNodeOutputByName = {};
    var currentNodeOutputById = executionState && executionState.nodeOutputById ? executionState.nodeOutputById : {};
    (stateToRun.nodes || []).forEach(function(node) {
      if (!node || !node.id || String(node.id) === String(nodeId)) return;
      var nid = String(node.id);
      if (Object.prototype.hasOwnProperty.call(currentNodeOutputById, nid)) {
        var title = String(node.title || '').trim();
        if (title) priorNodeOutputByName[title] = currentNodeOutputById[nid];
      } else if (node.icon === 'start') {
        var startOutput = {};
        if (Array.isArray(node.hyperparameters)) {
          node.hyperparameters.forEach(function(p) {
            if (!p || !p.key) return;
            startOutput[p.key] = (node.paramValues && node.paramValues[p.key] !== undefined)
              ? node.paramValues[p.key]
              : (p.default !== undefined ? p.default : '');
          });
        }
        var startTitle = String(node.title || 'Start').trim();
        if (Object.keys(startOutput).length && startTitle) priorNodeOutputByName[startTitle] = startOutput;
      }
    });
    var priorVars = executionState && executionState.variableValuesByName
      ? Object.assign({}, executionState.variableValuesByName)
      : {};
    startExecutionWithState(stateToRun, {
      fromNodeId: nodeId,
      priorNodeOutputByName: Object.keys(priorNodeOutputByName).length ? priorNodeOutputByName : null,
      priorVariableValuesByName: Object.keys(priorVars).length ? priorVars : null,
      preservePriorState: true
    }).catch(function() {});
  }

  // Kick off a fresh run with the given state. Shared by the no-input path and
  // the human-input path (after the Start-input popup is filled in).
  function beginWorkflowRun(stateToRun) {
    autoFixAttemptsRef.current = {};
    codefixSkipInfoRef.current = {};
    codefixSkipNotifiedRef.current = {};
    try {
      var freshWfId = selectedWorkflowIdRef.current || selectedWorkflowId || (stateToRun && stateToRun.id) || '';
      if (freshWfId) {
        workflowProgress.beginRun(freshWfId);
        if (typeof console !== 'undefined' && console.log) console.log('[WFProgress] beginRun wf=' + freshWfId);
      } else {
        if (typeof console !== 'undefined' && console.warn) console.warn('[WFProgress] beginRun skipped - no workflow id available');
      }
    } catch (e) {}
    startExecutionWithState(stateToRun).catch(function() {});
  }

  // Write the user-entered Start inputs onto the workflow state (paramValues),
  // so the run uses them and the canvas/sidebar reflect them next time.
  function applyStartInputValues(state, startNodeId, values) {
    if (!state || !Array.isArray(state.nodes)) return state;
    return Object.assign({}, state, {
      nodes: state.nodes.map(function(node) {
        if (node.id !== startNodeId) return node;
        var nextValues = Object.assign({}, node.paramValues || {});
        Object.keys(values || {}).forEach(function(k) { nextValues[k] = values[k]; });
        return Object.assign({}, node, { paramValues: nextValues });
      })
    });
  }

  // Called by the Start-input popup's Run button: apply the entered inputs and
  // start the run with them.
  // The state Play should execute: the live snapshot when viewing Live, else the
  // editable staging state. Lets the canvas Play button run the version on screen.
  function getRunnableState() {
    if (workflowMode === 'live') return liveSnapshot || EMPTY_LIVE_STATE;
    return workflowState;
  }

  function runWithStartInputs(values) {
    var info = runInputModal;
    setRunInputModal(null);
    if (!info) return;
    var baseState = getRunnableState();
    var nextState = applyStartInputValues(baseState, info.nodeId, values);
    // Only persist start-input edits back to the canvas in Staging; Live is
    // read-only structure, so we just run the live snapshot with the inputs.
    if (workflowMode !== 'live') setWorkflowState(nextState);
    beginWorkflowRun(nextState);
  }

  function handleRunWorkflow() {
    // Support agent: Play/Stop trigger on the OWNER's client (their context,
    // their connections); the run then mirrors back to the whole session.
    if (remoteSessionRef.current) {
      if (collabClientRef.current) {
        collabClientRef.current.sendPeerEvent('ui_state', {
          popup: 'run_request',
          action: executionState.status === 'running' ? 'stop' : 'run'
        });
      }
      return;
    }
    if (publicListenState.enabled) {
      return;
    }
    if (executionState.status === 'running') {
      handleStopWorkflow();
      return;
    }
    // While paused, the run button shouldn't kick off a brand-new execution —
    // it should resume the parked one. The dedicated pause button does the
    // same thing; this just keeps the play icon honest in either spot.
    if (executionState.status === 'paused') {
      handlePauseWorkflow();
      return;
    }
    // Run the version currently on screen: live snapshot in Live mode, else
    // the editable staging state.
    var runnable = getRunnableState();
    // If the Start node declares inputs, collect them from the user first, then
    // run with the entered values (manual human-input step).
    var startNode = ((runnable && runnable.nodes) || []).find(function(n) { return n && n.icon === 'start'; });
    var startParams = (startNode && Array.isArray(startNode.hyperparameters))
      ? startNode.hyperparameters.filter(function(p) { return p && p.key; })
      : [];
    if (startParams.length > 0) {
      setRunInputModal({ nodeId: startNode.id, params: startParams, initialValues: startNode.paramValues || {} });
      return;
    }
    beginWorkflowRun(runnable);
  }

  if (fatalError && route === 'workflow') {
    return h(ErrorPage, { title: 'Workflow Error', message: fatalError });
  }

  // Beta gate (frontend): a gated user must never see an app route, no matter how
  // they got there (direct nav, refresh, back button) — render the waitlist page
  // instead. Public routes (home/login/waitlist/legal) are exempt so they can
  // still reach + submit the form. The backend enforces this too (requireApproved);
  // this is the UI half.
  var PROTECTED_ROUTES = {
    dashboard: true, workflow: true, 'workbook-redirect': true,
    store: true, 'ui-page-editor': true, 'extension-auth': true
  };
  if (isUserGated(currentUser) && PROTECTED_ROUTES[route]) {
    return h('div', { className: 'login-shell' },
      h(WaitlistPage, { currentUser: currentUser, onNavigateTo: navigateTo, strings: landingStrings, onSubmitted: handleRefreshCurrentUser })
    );
  }

  var isLegal = route === 'privacy' || route === 'terms';
  return h('div', { className: (route === 'dashboard' || route === 'workflow' || route === 'workbook-redirect' || route === 'ui-page-editor' || route === 'ui-page-public' || route === 'store' || route === 'admin' || route === 'admin-benchmark') ? 'dashboard-shell' : (route === 'login' || route === 'reset-password' || route === 'waitlist' || route === 'extension-auth') ? 'login-shell' : route === 'user-guide' ? 'ug-shell' : ('landing-page' + (route !== 'home' ? ' landing-page--plain' : '') + (isLegal ? ' landing-page--legal' : '')) },
    // Mounted inside App (as well as index.js) so hot-reload keeps the Google
    // scope-picker host available without an entry-point restart. Double-mount
    // is harmless — the controller keeps only the last subscriber.
    h(GoogleScopePickerHost, null),
    route === 'dashboard' ? h(ExtensionAuthNotice, { currentUser: currentUser }) : null,
    route === 'home' ? h('div', { className: 'landing-bg' }) : null,
    route === 'dashboard' || route === 'workflow' || route === 'workbook-redirect' || route === 'login' || route === 'reset-password' || route === 'waitlist' || route === 'extension-auth' || route === 'ui-page-editor' || route === 'ui-page-public' || route === 'user-guide' || route === 'store' || route === 'admin' || route === 'admin-benchmark'
      ? null
      : h(TopNav, { currentUser: currentUser, onOpenLogin: handleOpenLogin, onSignout: handleSignout, onNavigateSection: handleNavigateLandingSection, activeSection: activeLandingSection, onNavigateTo: navigateTo, mobileMenuOpen: mobileMenuOpen, onToggleMobileMenu: function() { setMobileMenuOpen(!mobileMenuOpen); }, navItems: navItems, strings: landingStrings, language: language, onChangeLanguage: handleChangeLanguage }),
    route === 'extension-auth'
      ? h(ExtensionAuthPage, { currentUser: currentUser, onNavigateTo: navigateTo })
      : route === 'login'
      ? h(LoginPage, {
          onSignin: handleSignin,
          onSignup: handleSignup,
          onGoogleSignin: signinWithGoogle,
          onSuccess: handleAuthSuccess,
          onForgotPassword: requestPasswordReset,
          onBack: function() { navigateTo('/'); },
          strings: landingStrings
        })
      : route === 'reset-password'
        ? h(ResetPasswordPage, { onResetPassword: resetPassword, onNavigateTo: navigateTo, strings: landingStrings })
      : route === 'waitlist'
        ? h(WaitlistPage, { currentUser: currentUser, onNavigateTo: navigateTo, strings: landingStrings, onSubmitted: handleRefreshCurrentUser })
      : route === 'store'
        ? h(TemplateStorePage, { templateId: storeTemplateId, currentUser: currentUser, onNavigateTo: navigateTo })
      : route === 'admin'
        ? h(AdminDashboardPage, { currentUser: currentUser, onNavigateTo: navigateTo })
      : route === 'admin-benchmark'
        ? h(AdminEvalPage, null)
      : route === 'benchmark'
        ? h(PublicBenchmarkPage, null)
      : route === 'dashboard'
        ? h(WorkflowListPage, {
            user: currentUser,
            workflows: workflows,
            workbooks: workbooks,
            loading: !isWorkflowListHydrated,
            creating: isCreatingWorkflow || isDuplicatingWorkflow,
            creatingWorkbook: isCreatingWorkbook,
            initialTab: dashboardTab || '',
            onCreateWorkflow: handleCreateWorkflow,
            onCreateWorkbook: handleCreateWorkbook,
            onOpenWorkbook: handleOpenWorkbook,
            onDuplicateWorkflow: handleDuplicateWorkflow,
            onImportWorkflowFiles: handleImportWorkflowFiles,
            onOpenWorkflow: handleOpenWorkflow,
            onSignout: handleSignout,
            onOpenStore: function() { navigateTo('/store'); },
            onRefreshWorkflows: handleRefreshWorkflows,
            onResetUsage: handleResetUsage,
            onRefreshUser: handleRefreshCurrentUser,
            language: language,
            onChangeLanguage: handleChangeLanguage,
            onJoinSupportSession: handleJoinSupportSession,
            supportSessionNotice: collabNotice,
            onDismissSupportSessionNotice: function() { setCollabNotice(''); }
          })
        : route === 'workbook-redirect'
          ? h(ErrorPage, { title: 'Opening playbook', message: 'Loading playbook…' })
        : route === 'workflow'
          // Support agent inside someone else's session: TeamViewer-style
          // live mirror of the owner's screen + virtual pointer, instead of
          // a local editor.
          ? (remoteSession
              ? h(RemoteSessionViewer, {
                  ownerUsername: remoteSession.ownerUsername,
                  mirrorBus: mirrorBusRef.current,
                  onPointer: handleAgentPointer,
                  onResync: function() {
                    if (collabClientRef.current) collabClientRef.current.sendMirrorResync();
                  },
                  onLeave: function() { endRemoteSupportSession(''); }
                })
              : h('div', { className: 'app' },
                h(Header, {
                  onSignout: handleSignout,
                  user: currentUser,
                  workflowId: selectedWorkflowId || '',
                  isEmbed: isEmbed,
                  workflowName: workflowState.workflowName,
                  onWorkflowNameChange: handleWorkflowNameChange,
                  onWorkflowNameBlur: handleWorkflowNameBlur,
                  workbookName: (currentWorkbook && currentWorkbook.name) || '',
                  onWorkbookNameChange: handleWorkbookNameChange,
                  onWorkbookNameBlur: handleWorkbookNameBlur,
                  onOpenWorkflowList: handleOpenWorkflowList,
                  onDeleteWorkflow: handleDeleteWorkflow,
                  onDeleteWorkbook: handleDeleteCurrentWorkbook,
                  onSaveWorkflow: handleSaveWorkflow,
                  isWorkflowHydrated: isWorkflowHydrated && !isCanvasRendering,
                  listWorkflowVersions: listWorkflowVersions,
                  restoreWorkflowVersion: restoreWorkflowVersion,
                  onRestoreWorkflowVersion: handleRestoreWorkflowVersion,
                  onPublishToStore: (selectedWorkflowId && !remoteSession) ? function() {
                    // The store preview is now rendered live from a stripped
                    // graph derived server-side (templateService.buildPreviewGraph
                    // → WorkflowPreviewGraph), so no canvas screenshot is captured.
                    // Pass the current live version so the modal can show it (the
                    // store publishes the LIVE snapshot, Live-mode only).
                    setEditorPublishWorkflow({
                      id: selectedWorkflowId,
                      name: workflowState.workflowName || 'Untitled',
                      liveVersion: liveVersionInfo && liveVersionInfo.version ? String(liveVersionInfo.version) : ''
                    });
                  } : null,
                  liveVersion: liveVersionInfo && liveVersionInfo.version ? String(liveVersionInfo.version) : null,
                  workflowMode: workflowMode,
                  onChangeWorkflowMode: (selectedWorkflowId && !remoteSession) ? handleChangeWorkflowMode : null,
                  onUpdateLive: (selectedWorkflowId && !remoteSession) ? handleOpenGoLive : null,
                  getLiveVersionMeta: getLiveVersion,
                  listLiveVersionHistory: listLiveHistory,
                  isSaved: isSaved,
                  isSaving: isSaving,
                  onExportWorkflow: handleExportWorkflow,
                  onConvertToCode: handleConvertToCode,
                  onImportWorkflow: handleImportWorkflow,
                  onClearWorkflow: handleClearWorkflow,
                  onOpenAiInstruction: function() { setAiInstructionModalOpen(true); },
                  onRunWorkflow: handleRunWorkflow,
                  isExecutionRunning: executionState.status === 'running',
                  isRunDisabled: publicListenState.enabled,
                  publicRunUrl: selectedWorkflowId ? getApiBasePath() + '/public/workflow/run/' + encodeURIComponent(selectedWorkflowId) : '',
                  publicEventStreamEnabled: publicListenState.enabled,
                  publicEventStreamCode: publicListenState.code,
                  publicEventStreamError: publicListenState.error,
                  publicEventStreamStatus: publicListenState.connectionStatus,
                  publicEventStreamLastEventAt: publicListenState.lastEventAt,
                  onTogglePublicEventStream: handlePublicEventStreamToggle,
                  onApplyPublicEventStreamCode: handleApplyPublicEventStreamCode,
                  hasAiInstruction: !!(currentUser && currentUser.aiInstruction && currentUser.aiInstruction.trim()),
                  isDark: isDark,
                  onThemeToggle: handleThemeToggle,
                  hasWorkbook: !!(currentWorkbook && currentWorkbook.id),
                  isWorkbookPanelOpen: isWorkbookPanelOpen,
                  onToggleWorkbookPanel: handleToggleWorkbookPanel,
                  doctorChatOpen: doctorChatOpen,
                  onToggleDoctorChat: function() { setDoctorChatOpen(function(o) { return !o; }); },
                  remoteSupportOpen: remoteSupportOpen,
                  onToggleRemoteSupport: function() { setRemoteSupportOpen(function(o) { return !o; }); },
                  collabAgentCount: collabRoster.filter(function(m) { return m && m.role === 'agent'; }).length,
                  isRemoteSupportAgent: !!remoteSession,
                  startHyperparameters: (function() {
                    var startNode = (workflowState.nodes || []).find(function(n) { return n && n.icon === 'start'; });
                    return startNode && Array.isArray(startNode.hyperparameters) ? startNode.hyperparameters : [];
                  })()
                }),
                Object.keys(collabPeerUi).length > 0
                  ? h('div', { className: 'collab-activity-chips' },
                      Object.keys(collabPeerUi).map(function(key) {
                        var info = collabPeerUi[key];
                        var label = info.popup === 'doctor'
                          ? info.username + ' is using Workflow Doctor'
                          : info.username + ' is editing step' + (info.nodeTitle ? ' “' + info.nodeTitle + '”' : '');
                        return h('div', { className: 'collab-activity-chip', key: key },
                          h('span', { className: 'collab-activity-dot', style: { background: info.color || '#7c5cff' } }),
                          label
                        );
                      })
                    )
                  : null,
                (remoteSupportOpen && !isEmbed)
                  ? h(RemoteSupportPopup, {
                      isAgent: !!remoteSession,
                      ownerUsername: remoteSession ? remoteSession.ownerUsername : '',
                      roster: collabRoster,
                      selfSessionId: collabSelfSessionId,
                      onClose: function() { setRemoteSupportOpen(false); }
                    })
                  : null,
                h('div', { className: 'app-body' },
                  (isWorkbookPanelOpen && currentWorkbook)
                    ? h('div', { className: 'app-body__workbook-panel' },
                        h(WorkbookExplorerPanel, {
                          workbook: currentWorkbook,
                          selectedWorkflowId: selectedWorkflowId || '',
                          onOpenWorkflow: handleOpenWorkflowInsideWorkbook,
                          onCreateWorkflow: handleCreateWorkflowInWorkbook,
                          onCreateFolder: handleAddFolder,
                          onRenameFolder: handleRenameFolder,
                          onDeleteFolder: handleDeleteFolder,
                          onMoveWorkflow: handleMoveWorkflowInWorkbook,
                          onMoveFolder: handleMoveFolder,
                          onDeleteWorkflow: handleDeleteWorkflowInWorkbook,
                          onRenameWorkflow: handleRenameWorkflowInWorkbook,
                          onRenameWorkbook: handleRenameCurrentWorkbook,
                          onOpenMoveDialog: function(item) { setWorkbookMoveItem(item); },
                          renameRequest: workbookRenameRequest,
                          // Route the panel's delete confirmations through the
                          // shared styled dialog (never native popups).
                          onConfirm: function(spec) {
                            if (!spec) return;
                            setRuntimePopup({
                              title: spec.title,
                              message: spec.message,
                              confirmLabel: spec.confirmLabel || 'Confirm',
                              cancelLabel: spec.cancelLabel || 'Cancel',
                              danger: spec.danger === true,
                              hideCancel: false,
                              onConfirm: spec.onConfirm
                            });
                          },
                          uiPages: workbookUiPages,
                          onCreateUiPage: handleOpenUiPlanner,
                          onOpenUiPage: function(pageId) { navigateTo('/ui-page/' + encodeURIComponent(pageId)); }
                        })
                      )
                    : null,
                  h('div', { className: 'canvas-area' + (workflowMode === 'live' ? ' canvas-area-live' : '') },
                    workflowMode === 'live' && !isEmbed
                      ? h(LiveModeBanner, {
                          version: liveVersionInfo && liveVersionInfo.version ? String(liveVersionInfo.version) : null,
                          hasLive: !!liveSnapshot,
                          loading: liveSnapshotLoading,
                          dirty: liveDirty,
                          saving: liveSaving,
                          offline: !!(liveVersionInfo && liveVersionInfo.offline),
                          offlineBusy: liveOfflineBusy,
                          onSave: handleSaveLive,
                          onUpdate: handleOpenGoLive,
                          onToggleOffline: handleToggleLiveOffline
                        })
                      : null,
                    // Stale-draft warning: only meaningful in staging (the
                    // editable canvas) and only for the workflow it was
                    // detected on.
                    (workflowMode !== 'live' && draftConflict && String(draftConflict.workflowId) === String(selectedWorkflowId))
                      ? h(DraftConflictBanner, {
                          savedAt: draftConflict.serverUpdatedAt,
                          busy: draftConflictBusy,
                          onLoadLatest: handleConflictLoadLatest,
                          onKeepDraft: handleConflictKeepDraft
                        })
                      : null,
                    h(WorkflowCanvas, {
                      runLogLines: runLogLines,
                      runLogOpen: runLogOpen,
                      onToggleRunLog: function() { setRunLogOpen(function(o) { return !o; }); },
                      user: currentUser,
                      workflowId: selectedWorkflowId || '',
                      // Live mode edits the published snapshot's config (variable
                      // values + hyperparameters only — structure is locked via
                      // readOnly); staging renders the editable canvas state.
                      workflowState: workflowMode === 'live' ? (liveSnapshot || EMPTY_LIVE_STATE) : workflowState,
                      setWorkflowState: workflowMode === 'live' ? handleSetLiveSnapshot : setWorkflowState,
                      readOnly: workflowMode === 'live' || isEmbed,
                      isEmbed: isEmbed,
                      onSetVariableLivePreview: setVariableLivePreviewValue,
                      onSelectionChange: isEmbed ? null : setSelectedNodeId,
                      isLoading: workflowMode === 'live' ? liveSnapshotLoading : !isWorkflowHydrated,
                      onRenderingChange: handleCanvasRenderingChange,
                      autopilotStatus: autopilotStatus,
                      onLogoDoubleClick: function(nodeId) { setLogoPickerNodeId(String(nodeId || '')); },
                      objectTemplates: objectTemplates.concat(systemTemplates).concat(customObjectTemplates),
                      executionState: executionState,
                      collabRoster: collabRoster,
                      collabSelfSessionId: collabSelfSessionId,
                      collabCursorsRef: collabCursorsRef,
                      onCollabCursorMove: handleCollabCursorMove,
                      // nodeId -> {color, username}: peers' selected nodes
                      // (rendered as a colored ring).
                      collabSelections: (function() {
                        var byNode = {};
                        Object.keys(collabSelections).forEach(function(sid) {
                          var sel = collabSelections[sid];
                          if (sel && sel.nodeId) byNode[sel.nodeId] = sel;
                        });
                        return byNode;
                      })(),
                      doctorChatOpen: doctorChatOpen,
                      setDoctorChatOpen: setDoctorChatOpen,
                      doctorPendingNotice: doctorPendingNotice,
                      onConsumeDoctorPendingNotice: function() { setDoctorPendingNotice(null); },
                      doctorFixStatus: doctorFixStatus,
                      onFixNode: handleManualFixNode,
                      // Copilot editNodeCode tool: live view of the AskCode
                      // editor so it can tell "edit dead" from "still working".
                      getAskCodeEditorState: function() {
                        return { open: editorOpenRef.current, nodeId: editingNodeIdRef.current };
                      },
                      onApplyFixPlanSteps: handleApplyFixPlanSteps,
                      // Doctor's runWorkflow client tool — kicks off Play and
                      // resolves on terminal status. WorkflowCanvas forwards
                      // straight through to DoctorChatPopup.
                      onDoctorRunWorkflow: runWorkflowAndAwait,
                      onOpenUiPlanner: handleOpenUiPlanner,
                      // The "Edit UI Page" rename, the center Workflow|UI
                      // toggle, and the iframe overlay only apply to STANDALONE
                      // workflows. When the workflow is opened inside a
                      // workbook (currentWorkbook set), behave the way it
                      // always has: a plain "+ UI Page" button with no toggle.
                      existingUiPageId: (function() {
                        if (currentWorkbook && currentWorkbook.id) return '';
                        var wfid = selectedWorkflowId || '';
                        if (!wfid || !Array.isArray(allUserUiPages)) return '';
                        for (var i = 0; i < allUserUiPages.length; i++) {
                          var p = allUserUiPages[i];
                          // workflowRefs is exposed on the summary by the
                          // server (see pageToSummary in uiPageService.js).
                          var refs = p && Array.isArray(p.workflowRefs)
                            ? p.workflowRefs : [];
                          if (refs.indexOf(wfid) !== -1) return p.id || '';
                        }
                        return '';
                      })(),
                      // Click "Edit UI Page": fetch the full record (the list
                      // summary doesn't carry html) and open the planner in
                      // refine-existing mode for that page.
                      onEditUiPage: (currentWorkbook && currentWorkbook.id)
                        ? null
                        : function(pageId) {
                            if (!pageId) return Promise.resolve();
                            return uiPageStorage.loadUiPage(pageId).then(function(page) {
                              if (page) handleOpenUiPlannerForExisting(page);
                            });
                          },
                      onDeleteUiPage: (currentWorkbook && currentWorkbook.id)
                        ? null
                        : function(pageId) {
                            if (!pageId) return Promise.resolve();
                            return uiPageStorage.deleteUiPage(pageId).then(function() {
                              refreshAllUserUiPages();
                            });
                          },
                      uiPageReloadKey: uiPageEditorReloadKey,
                      onViewModeChange: setCanvasViewMode,
                      onCheckSilentErrorChange: function(val) {
                        var next = !!val;
                        setWorkflowState(function(prev) {
                          if (!prev || !!prev.checkSilentError === next) return prev;
                          return Object.assign({}, prev, { checkSilentError: next });
                        });
                      },
                      onRunWorkflow: handleRunWorkflow,
                      onPauseWorkflow: handlePauseWorkflow,
                      isExecutionRunning: executionState.status === 'running',
                      isExecutionPaused: executionState.status === 'paused',
                      isRunDisabled: publicListenState.enabled,
                      publicEventStreamEnabled: publicListenState.enabled,
                      publicEventStreamCode: publicListenState.code,
                      publicEventStreamStatus: publicListenState.connectionStatus,
                      publicEventStreamLastEventAt: publicListenState.lastEventAt,
                      publicEventStreamError: publicListenState.error,
                      onTogglePublicEventStream: handlePublicEventStreamToggle,
                      onRunWithSnapshot: startExecutionWithState,
                      onRerunFromNode: handleRerunFromNode,
                      onRunNodeIsolated: handleRunNodeIsolated,
                      onAutoRunNewNode: handleAutoRunNewNode,
                      historyEntries: historyEntries,
                      historyPos: historyPos,
                      onUndo: handleUndo,
                      onRedo: handleRedo,
                      onJumpHistory: jumpToHistory,
                      onEditCustomNode: handleEditCanvasNode,
                      onSaveNodeAsTemplate: handleSaveNodeAsTemplate,
                      onManualNodeAutoFix: function(nodeId, userPrompt) {
                        var targetNodeId = String(nodeId || '').trim();
                        if (!targetNodeId) return Promise.reject(new Error('Missing node id.'));
                        var targetNode = (workflowState && Array.isArray(workflowState.nodes))
                          ? workflowState.nodes.find(function(n) { return n && String(n.id) === targetNodeId; })
                          : null;
                        if (!targetNode) return Promise.reject(new Error('Target node not found.'));
                        setExecutionState(function(prev) {
                          var nextNodeFix = Object.assign({}, prev.nodeFixById);
                          nextNodeFix[targetNodeId] = { active: true, attempt: 1, phase: 'manual.fix' };
                          return Object.assign({}, prev, { nodeFixById: nextNodeFix });
                        });
                        var prompt = String(userPrompt || '').trim();
                        var errorMessage = prompt ? 'User said: ' + prompt : 'The output is not correct.';
                        var manualStaticVars = {};
                        (workflowStateRef.current && workflowStateRef.current.nodes || []).forEach(function(n) {
                          if (n && n.icon === 'variables' && n.title) {
                            manualStaticVars[n.title] = n.paramValues && n.paramValues.value !== undefined ? n.paramValues.value : '';
                          }
                        });
                        return putCodefix({
                          workflowId: selectedWorkflowId,
                          executionId: executionState && executionState.executionId ? String(executionState.executionId) : '',
                          nodeId: targetNodeId,
                          eventType: 'manual.fix',
                          error: errorMessage,
                          input: executionState && executionState.nodeInputById ? executionState.nodeInputById[targetNodeId] : null,
                          namedArgs: {},
                          variables: Object.assign({}, manualStaticVars, executionState && executionState.variableValuesByName ? executionState.variableValuesByName : {}),
                          hyperparams: targetNode && targetNode.paramValues && typeof targetNode.paramValues === 'object' ? targetNode.paramValues : {},
                          hyperparameters: targetNode && Array.isArray(targetNode.hyperparameters) ? targetNode.hyperparameters : [],
                          startParams: getStartNodeParams(workflowStateRef.current && workflowStateRef.current.nodes)
                        }).then(function(result) {
                          var fixedCode = result && typeof result.code === 'string' ? result.code : '';
                          if (fixedCode) {
                            setWorkflowState(function(prev) {
                              return Object.assign({}, prev, {
                                nodes: (prev.nodes || []).map(function(n) {
                                  if (!n || String(n.id) !== targetNodeId) return n;
                                  return Object.assign({}, n, { code: fixedCode });
                                })
                              });
                            });
                          }
                        }).finally(function() {
                          setExecutionState(function(prev) {
                            var nextNodeFix = Object.assign({}, prev.nodeFixById);
                            delete nextNodeFix[targetNodeId];
                            return Object.assign({}, prev, { nodeFixById: nextNodeFix });
                          });
                        });
                      }
                    })
                  ),
                  runtimePopup
                    ? h(ConfirmDialog, {
                        title: runtimePopup.title,
                        message: runtimePopup.message,
                        confirmLabel: runtimePopup.confirmLabel || 'Close',
                        cancelLabel: runtimePopup.cancelLabel || 'Cancel',
                        danger: runtimePopup.danger === true,
                        boxStyle: runtimePopup.boxStyle || null,
                        hideCancel: runtimePopup.hideCancel !== false,
                        onConfirm: function() {
                          var handler = runtimePopup.onConfirm;
                          setRuntimePopup(null);
                          if (typeof handler === 'function') handler();
                        },
                        onCancel: function() {
                          var handler = runtimePopup.onCancel;
                          setRuntimePopup(null);
                          if (typeof handler === 'function') handler();
                        }
                      })
                    : null,
                  runInputModal
                    ? h(RunInputModal, {
                        params: runInputModal.params,
                        initialValues: runInputModal.initialValues,
                        onRun: runWithStartInputs,
                        onCancel: function() { setRunInputModal(null); }
                      })
                    : null,
                  publishLiveOpen
                    ? h(PublishLiveDialog, {
                        currentVersion: liveVersionInfo && liveVersionInfo.version ? String(liveVersionInfo.version) : null,
                        publishedAt: liveVersionInfo && liveVersionInfo.publishedAt ? liveVersionInfo.publishedAt : null,
                        isPublishing: publishLiveBusy,
                        error: publishLiveError,
                        onPublish: handlePublishLive,
                        onCancel: function() { if (!publishLiveBusy) { setPublishLiveOpen(false); setPublishLiveError(''); } }
                      })
                    : null,
                  logoPickerNodeId
                    ? (function() {
                        var pickedNode = null;
                        var nodes = (workflowState && Array.isArray(workflowState.nodes)) ? workflowState.nodes : [];
                        for (var i = 0; i < nodes.length; i++) {
                          if (String(nodes[i].id) === logoPickerNodeId) { pickedNode = nodes[i]; break; }
                        }
                        return h(LogoPickerPopup, {
                          currentLogoUrl: pickedNode && pickedNode.logoUrl ? pickedNode.logoUrl : '',
                          workflowState: workflowState,
                          customObjectTemplates: customObjectTemplates,
                          onPick: function(url) {
                            handleUpdateCanvasNodeCodeSilent({ id: logoPickerNodeId, logoUrl: url });
                            setLogoPickerNodeId('');
                          },
                          onClose: function() { setLogoPickerNodeId(''); }
                        });
                      })()
                    : null,
                  // Staging shows the full Sidebar (palettes + node params). Live
                  // shows it in "credentials only" mode: just the per-node account
                  // picker for the selected node, which is editable + savable like
                  // variable values. The UI page view hides it in both modes.
                  (canvasViewMode !== 'ui' && !isEmbed) ? h(Sidebar, {
                    objectTemplates: objectTemplates,
                    onOpenEditor: handleOpenEditor,
                    // Built-in system templates (Apify, …) always lead the
                    // Templates section, followed by the user's own saved
                    // templates. System templates are undeletable (the sidebar
                    // hides their delete button via isSystemTemplate); only
                    // user templates route through the custom-object delete API.
                    userTemplates: systemTemplates
                      .concat(customObjectTemplates.filter(function(t) { return t.isUserTemplate; })),
                    onDeleteTemplate: handleDeleteCustomObject,
                    onSaveNodeAsTemplate: handleSaveNodeAsTemplate,
                    isRunning: executionState.status === 'running',
                    isEmbed: isEmbed,
                    workflowId: selectedWorkflowId || '',
                    liveCredentialsOnly: workflowMode === 'live',
                    selectedNode: (function() {
                      // In Live mode the selected node must come from the live
                      // snapshot so the picker reflects (and writes back) the
                      // saved live credentialIds — not the staging node.
                      var src = workflowMode === 'live' ? liveSnapshot : workflowState;
                      if (!selectedNodeId || !src || !Array.isArray(src.nodes)) return null;
                      for (var i = 0; i < src.nodes.length; i++) {
                        if (src.nodes[i].id === selectedNodeId) return src.nodes[i];
                      }
                      return null;
                    })(),
                    // Live run status for the selected node — drives the N++ / N--
                    // counter preview in the sidebar param input (runtime only).
                    selectedNodeStatus: selectedNodeId && executionState.nodeStatusById
                      ? (executionState.nodeStatusById[selectedNodeId] || null)
                      : null,
                    selectedNodeCallCount: selectedNodeId && executionState.nodeCallCountById
                      ? (executionState.nodeCallCountById[selectedNodeId] || 0)
                      : 0,
                    variableNodes: buildEditorContextNodes(
                      selectedNodeId,
                      (workflowState && Array.isArray(workflowState.nodes)) ? workflowState.nodes : [],
                      (workflowState && Array.isArray(workflowState.connections)) ? workflowState.connections : [],
                      executionState && executionState.nodeOutputById,
                      executionState && executionState.variableValuesByName
                    ),
                    // Browser nodes that run BEFORE the selected node, nearest
                    // first — the editor's "Continue from" dropdown only offers
                    // tabs that exist by the time this node runs, and its Auto
                    // mode inherits the first (nearest) one.
                    browserActionNodes: (function() {
                      var allNodes = (workflowState && Array.isArray(workflowState.nodes)) ? workflowState.nodes : [];
                      var browserById = {};
                      allNodes.forEach(function(n) {
                        if (n && (n.icon === 'browser-action' || n.icon === 'browser-agentic-action')) browserById[n.id] = n;
                      });
                      var upstreamIds = selectedNodeId
                        ? computeUpstreamIdsNearestFirst(
                            selectedNodeId,
                            (workflowState && Array.isArray(workflowState.connections)) ? workflowState.connections : []
                          )
                        : [];
                      return upstreamIds.filter(function(id) { return browserById[id]; }).map(function(id) {
                        var n = browserById[id];
                        return {
                          id: n.id,
                          title: n.title || (n.icon === 'browser-agentic-action' ? 'Browser Agentic Action' : 'Browser Action'),
                          url: (n.paramValues && n.paramValues.url) || '',
                          // Chain fields so the editor can walk continueFrom
                          // links to find a page URL to pick on.
                          tabMode: (n.paramValues && n.paramValues.tabMode) || 'new',
                          continueFrom: (n.paramValues && n.paramValues.continueFrom) || ''
                        };
                      });
                    })(),
                    onNodeParamChange: handleSidebarParamChange,
                    onNodeParamRename: handleSidebarParamRename,
                    onNodeParamAdd: handleSidebarParamAdd,
                    onNodeParamRemove: handleSidebarParamRemove,
                    onNodeConditionRuleChange: handleSidebarConditionRuleChange,
                    // In Live mode the pin writes to the live snapshot (saved by
                    // the Live "Save changes" button); in Staging it edits the
                    // staging workflow state.
                    onNodeCredentialChange: workflowMode === 'live'
                      ? handleLiveCredentialChange
                      : handleSidebarCredentialChange,
                    onCloseNodeParams: function() { setSelectedNodeId(null); },
                    // Live mode: hyperparameters/params are NOT editable (only
                    // variable values, via the left Variable panel). Tell the
                    // sidebar to show a read-only notice instead of the form.
                    readOnly: workflowMode === 'live'
                  }) : null
                ),
                editorOpen
                  ? h(CustomObjectEditor, {
                      // Remount when the edited node changes: initialData and
                      // initialPendingAsk are read only at mount, so without a
                      // key a second AskCode request while the editor is open
                      // would silently no-op on the stale node.
                      key: (editingObject && editingObject.id) || 'editor',
                      mode: editorMode,
                      workflowId: selectedWorkflowId || '',
                      initialData: editingObject || {},
                      onSave: editorMode === 'node' ? handleSaveCanvasNodeCode : handleSaveCustomObject,
                      onSilentSave: editorMode === 'node' ? handleUpdateCanvasNodeCodeSilent : undefined,
                      // The X button (manual close) aborts the rest of the
                      // doctor-queue. The auto-close path above bypasses
                      // this by calling handleCloseEditor directly so the
                      // queue keeps advancing.
                      onClose: function() {
                        if (doctorFixActiveRef.current) {
                          // User aborted mid-queue. Mark the current step
                          // and every remaining queued step as skipped so
                          // the doctor summary reflects what didn't run.
                          if (doctorFixResultsRef.current) {
                            var openId = editingObject && editingObject.id;
                            var openTitle = (editingObject && editingObject.title) || openId || 'Unknown';
                            doctorFixResultsRef.current.skipped.push({
                              nodeId: openId,
                              nodeTitle: openTitle,
                              reason: 'closed'
                            });
                            (doctorFixQueueRef.current || []).forEach(function(s) {
                              doctorFixResultsRef.current.skipped.push({
                                nodeId: s.nodeId,
                                nodeTitle: s.nodeTitle || s.nodeId,
                                reason: 'aborted'
                              });
                            });
                            // Mark aborted steps in live status
                            setDoctorFixStatus(function(prev) {
                              var prevCompleted = (prev && prev.completed) || {};
                              var nextCompleted = Object.assign({}, prevCompleted);
                              if (openId) nextCompleted[openId] = 'skipped';
                              (doctorFixQueueRef.current || []).forEach(function(s) {
                                if (s && s.nodeId) nextCompleted[s.nodeId] = 'skipped';
                              });
                              return { activeNodeId: null, completed: nextCompleted };
                            });
                          }
                          doctorFixQueueRef.current = [];
                          doctorFixActiveRef.current = false;
                          // Drain the queue normally so the completion
                          // notice fires (advance() will see empty queue).
                          if (doctorFixResultsRef.current) {
                            var aborted = doctorFixResultsRef.current;
                            doctorFixResultsRef.current = null;
                            setDoctorPendingNotice({
                              kind: 'queue_complete',
                              totalSteps: aborted.totalSteps,
                              applied: aborted.applied,
                              skipped: aborted.skipped
                            });
                          }
                        }
                        handleCloseEditor();
                      },
                      initialPendingAsk: autoFixPrompt,
                      autoFixReason: autoFixReason,
                      autoFixSource: autoFixSource,
                      onAutoFixComplete: function(result) {
                        // Doctor-chat "Approve" queue path: AskCode just
                        // finished its turn for the current step. Don't run
                        // the autopilot rerun-node sequence — record the
                        // outcome and auto-close so the queue advances.
                        if (doctorFixActiveRef.current) {
                          var dSafe = (result && typeof result === 'object') ? result : {};
                          var dApplied = !!(dSafe.code) || !!(dSafe.hasEdgeChanges);
                          // Record this step's outcome for the queue summary.
                          // editingObject is the node currently in AskCode.
                          if (doctorFixResultsRef.current) {
                            var stepNodeId = editingObject && editingObject.id;
                            var stepNodeTitle = (editingObject && editingObject.title) || stepNodeId || 'Unknown';
                            if (dApplied) {
                              doctorFixResultsRef.current.applied.push({
                                nodeId: stepNodeId,
                                nodeTitle: stepNodeTitle
                              });
                            } else {
                              doctorFixResultsRef.current.skipped.push({
                                nodeId: stepNodeId,
                                nodeTitle: stepNodeTitle,
                                reason: 'no_change'
                              });
                            }
                            var completedStatus = dApplied ? 'done' : 'skipped';
                            setDoctorFixStatus(function(prev) {
                              var next = { activeNodeId: null, completed: Object.assign({}, prev && prev.completed) };
                              next.completed[stepNodeId] = completedStatus;
                              return next;
                            });
                          }
                          // Always auto-close so the queue keeps moving — the
                          // brief delay lets the user see AskCode's "✓ Changes
                          // applied" / "no change" line before we advance.
                          setTimeout(handleCloseEditor, 600);
                          return;
                        }
                        // Copilot-opened AskCode (editNodeCode tool): the turn
                        // is done — auto-close after a short beat so the user
                        // sees the "changes applied" line before it goes away.
                        if (manualFixAutoCloseRef.current) {
                          manualFixAutoCloseRef.current = false;
                          setTimeout(handleCloseEditor, 3000);
                          return;
                        }
                        if (!autoFixActiveRef.current) return;
                        // Older callers may pass a plain string (newCode) or
                        // null. Normalize to { code, hasEdgeChanges }.
                        var safe = (result && typeof result === 'object')
                          ? result
                          : { code: result || null, hasEdgeChanges: false };
                        var newCode = safe.code || null;
                        var hasEdgeChanges = !!safe.hasEdgeChanges;
                        var resumeNodeId = autoFixNodeIdRef.current;
                        var snapshot = autoFixSnapshotRef.current;
                        autoFixActiveRef.current = false;
                        autoFixNodeIdRef.current = null;
                        autoFixSnapshotRef.current = null;
                        setAutoFixPrompt(null);
                        setAutoFixReason('');
                        setAutoFixSource(null);
                        // No resume target → just close.
                        if (!resumeNodeId) {
                          handleCloseEditor();
                          return;
                        }
                        // Hand off to the autopilot driver — it walks through
                        // wait-for-code → close-editor → rerun-node, surfacing
                        // each step on the canvas status pill. When there's no
                        // new code and no edge change ("no changes needed"),
                        // we still close + rerun from the flagged node so the
                        // workflow resumes; the wait-for-code step is skipped.
                        runAutoFixSequence(resumeNodeId, newCode, snapshot);
                      }
                    })
                  : null,
                aiInstructionModalOpen
                  ? h(AiInstructionModal, {
                      value: (currentUser && currentUser.aiInstruction) || '',
                      aiMemory: (currentUser && currentUser.aiMemory) || '',
                      onClose: function() { setAiInstructionModalOpen(false); },
                      onSave: function(text) {
                        var value = String(text || '').trim();
                        setCurrentUser(function(prev) { return Object.assign({}, prev, { aiInstruction: value }); });
                        saveUserAiInstruction(value).catch(function(err) {
                          console.error('Failed to save AI instruction:', err && err.message ? err.message : err);
                        });
                        setAiInstructionModalOpen(false);
                      }
                    })
                  : null,
                workbookMoveItem && currentWorkbook
                  ? h(WorkbookMoveToDialog, {
                      workbook: currentWorkbook,
                      item: workbookMoveItem,
                      onCancel: function() { setWorkbookMoveItem(null); },
                      onConfirm: function(targetFolderId) {
                        if (workbookMoveItem.kind === 'workflow') handleMoveWorkflowInWorkbook(workbookMoveItem.id, targetFolderId);
                        else if (workbookMoveItem.kind === 'folder') handleMoveFolder(workbookMoveItem.id, targetFolderId || null);
                        setWorkbookMoveItem(null);
                      }
                    })
                  : null,
                uiPlannerOpen
                  ? h(UIPlannerModal, {
                      onClose: function() {
                        setUiPlannerOpen(false);
                        setActiveUiPageRecord(null);
                      },
                      onSavePage: handleSaveUiPageFromPlanner,
                      workflows: (function() {
                        var wbFlows = (currentWorkbook && currentWorkbook.workflows && currentWorkbook.workflows.length)
                          ? currentWorkbook.workflows
                          : [];
                        // Ensure the currently-viewed workflow is in the list
                        if (selectedWorkflowId) {
                          var found = wbFlows.some(function(w) { return w && w.id === selectedWorkflowId; });
                          if (!found) {
                            wbFlows = [{ id: selectedWorkflowId, name: workflowState.workflowName || 'Untitled' }].concat(wbFlows);
                          }
                        }
                        return wbFlows.length ? wbFlows
                          : (selectedWorkflowId
                              ? [{ id: selectedWorkflowId, name: workflowState.workflowName || 'Untitled' }]
                              : []);
                      })(),
                      existingPage: activeUiPageRecord,
                      focusWorkflowId: selectedWorkflowId || ''
                    })
                  : null
              ))
        : route === 'ui-page-public'
          ? h(UiPagePublicView, { slug: uiPageRoute.slug })
        : route === 'ui-page-editor'
          ? h('div', null,
              h(UIPageEditor, {
                key: 'ui-page-editor-' + uiPageRoute.pageId + '-' + uiPageEditorReloadKey,
                pageId: uiPageRoute.pageId,
                onClose: function() {
                  if (currentWorkbook && currentWorkbook.id) {
                    navigateTo('/workbook/' + encodeURIComponent(currentWorkbook.id));
                  } else {
                    navigateTo('/dashboard');
                  }
                },
                onOpenPlanner: handleOpenUiPlannerForExisting,
                onDeleted: handleUiPageDeleted
              }),
              uiPlannerOpen
                ? h(UIPlannerModal, {
                    onClose: function() {
                      setUiPlannerOpen(false);
                      setActiveUiPageRecord(null);
                    },
                    onSavePage: handleSaveUiPageFromPlanner,
                    workflows: (function() {
                      var wbFlows = (currentWorkbook && currentWorkbook.workflows && currentWorkbook.workflows.length)
                        ? currentWorkbook.workflows
                        : [];
                      if (selectedWorkflowId) {
                        var found = wbFlows.some(function(w) { return w && w.id === selectedWorkflowId; });
                        if (!found) {
                          wbFlows = [{ id: selectedWorkflowId, name: workflowState.workflowName || 'Untitled' }].concat(wbFlows);
                        }
                      }
                      return wbFlows.length ? wbFlows
                        : (selectedWorkflowId
                            ? [{ id: selectedWorkflowId, name: workflowState.workflowName || 'Untitled' }]
                            : []);
                    })(),
                    existingPage: activeUiPageRecord,
                    focusWorkflowId: selectedWorkflowId || ''
                  })
                : null
            )
        : route === 'privacy'
          ? h(LegalPage, Object.assign({}, privacyPolicy, { onBack: function() { navigateTo('/'); } }))
          : route === 'terms'
            ? h(LegalPage, Object.assign({}, termsOfService, { onBack: function() { navigateTo('/'); } }))
            : route === 'oauth-consent'
              ? h(OAuthConsentPage, { user: currentUser })
            : route === 'apidocs'
              ? h(ApiDocsPage)
            : route === 'user-guide'
              ? h(UserGuidePage, { onBack: function() { navigateTo('/'); } })
              : h(LandingHome, {
                // Signed out + waitlist mode: the CTA reads "Join Waiting List",
                // so send them to the form rather than the login page.
                onGetStarted: currentUser
                  ? function() { navigateTo(isUserGated(currentUser) ? '/waitlist' : '/dashboard'); }
                  : betaConfig.WAITLIST_MODE
                    ? function() { navigateTo('/waitlist'); }
                    : handleOpenLogin,
                onSelectPlan: handleSelectPlan,
                onNavigateTo: navigateTo,
                onExplore: function() { handleNavigateLandingSection('how-it-works'); },
                onExplorePricing: function() { handleNavigateLandingSection('pricing'); },
                navItems: navItems,
                landingPageContent: landingPageContent,
                landingStrings: landingStrings
              }),
    editorPublishWorkflow
      ? h(PublishTemplateModal, {
          workflow: editorPublishWorkflow,
          onClose: function() { setEditorPublishWorkflow(null); },
          onPublished: function() { setEditorPublishWorkflow(null); }
        })
      : null,
    // Beta gate popup — shown to unapproved users after sign-in/up. "Join the
    // waiting list" → /waitlist; cancel signs them out (they can't enter the app).
    showBetaGate
      ? h(ConfirmDialog, {
          title: (landingStrings.waitlist || {}).gateTitle,
          message: (landingStrings.waitlist || {}).gateMessage,
          confirmLabel: (landingStrings.waitlist || {}).gateConfirm,
          cancelLabel: (landingStrings.waitlist || {}).gateCancel,
          danger: false,
          hideCancel: false,
          onConfirm: function() { setShowBetaGate(false); navigateTo('/waitlist'); },
          onCancel: function() { setShowBetaGate(false); handleSignout(); }
        })
      : null
  );
}

// A folder id plus all of its descendant folder ids (matches the server's
// recursive delete in workbookService.deleteFolderForUser).
function collectFolderSubtreeIds(folders, folderId) {
  var list = Array.isArray(folders) ? folders : [];
  var ids = [String(folderId)];
  var queue = [String(folderId)];
  while (queue.length) {
    var current = queue.shift();
    list.forEach(function(f) {
      if (!f || String(f.parentId || '') !== current) return;
      var id = String(f.id);
      if (ids.indexOf(id) === -1) {
        ids.push(id);
        queue.push(id);
      }
    });
  }
  return ids;
}

function getRouteFromLocation() {
  if (typeof window === 'undefined') return 'home';
  var path = window.location.pathname || '';
  if (path === '/login') return 'login';
  if (path === '/extension-auth') return 'extension-auth';
  if (path === '/reset-password') return 'reset-password';
  if (path === '/waitlist') return 'waitlist';
  if (path === '/dashboard') return 'dashboard';
  if (path === '/admin') return 'admin';
  if (path === '/admin/benchmark') return 'admin-benchmark';
  if (path === '/benchmark') return 'benchmark';
  if (/^\/store(\/|$)/.test(path)) return 'store';
  if (path === '/oauth/authorize') return 'oauth-consent';
  if (path === '/apidocs') return 'apidocs';
  if (path === '/user-guide') return 'user-guide';
  if (path === '/privacy') return 'privacy';
  if (path === '/terms') return 'terms';
  if (/^\/p\/[^/]+/.test(path)) return 'ui-page-public';
  if (/^\/ui-page\/[^/]+/.test(path)) return 'ui-page-editor';
  if (/^\/workbook\/[^/]+\/[^/]+/.test(path)) return 'workflow';
  if (/^\/workbook\/[^/]+/.test(path)) return 'workbook-redirect';
  if (/^\/workflow\/[^/]+/.test(path)) return 'workflow';
  return 'home';
}

function getExtensionAuthCodeFromLocation() {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('extension-auth-code') || '';
}

function getUiPageIdFromLocation() {
  if (typeof window === 'undefined') return '';
  var match = (window.location.pathname || '').match(/^\/ui-page\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

function getStoreIdFromLocation() {
  if (typeof window === 'undefined') return '';
  var match = (window.location.pathname || '').match(/^\/store\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

function getUiPageSlugFromLocation() {
  if (typeof window === 'undefined') return '';
  var match = (window.location.pathname || '').match(/^\/p\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

function getWorkflowIdFromLocation() {
  if (typeof window === 'undefined') return '';
  var path = window.location.pathname || '';
  var nested = path.match(/^\/workbook\/[^/]+\/([^/]+)/);
  if (nested) return decodeURIComponent(nested[1]);
  var flat = path.match(/^\/workflow\/([^/]+)/);
  return flat ? decodeURIComponent(flat[1]) : '';
}

function getWorkbookIdFromLocation() {
  if (typeof window === 'undefined') return '';
  var match = (window.location.pathname || '').match(/^\/workbook\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

function getLandingSectionFromLocation() {
  if (typeof window === 'undefined') return '';
  var hash = String(window.location.hash || '').replace(/^#/, '').trim();
  return hash;
}

module.exports = App;
